import { useMemo, useRef } from 'react'
import { useFrame, useLoader } from '@react-three/fiber'
import * as THREE from 'three'
import type { BoardLayout } from './useBoardLayout'
import type { BiomeTheme } from '@/styles/theme'
import type { BiomeId } from '@/styles/theme'
import { biomeArt } from '@/game/art'
import { buildWorldLife, type WorldPropPlacement } from '@/game/worldLife'
import { buildWorldcraft, type WorldcraftMotif, type WorldcraftTerrainPiece } from '@/game/worldcraft'
import { makeRoundedBox } from './roundedBoxGeometry'

interface WorldEnvironmentProps {
  layout: BoardLayout
  theme: BiomeTheme
  biome: BiomeId
  reducedMotion: boolean
  graphics: 'high' | 'medium' | 'low'
  emberColor: string
  grid: string[]
}

type RimSide = 'north' | 'south' | 'west' | 'east'
type RimPieceShape = 'tree-cluster' | 'stone-spire' | 'basalt-vent' | 'snow-spire' | 'rune-shard' | 'dungeon-pillar'
type PlatformEdgeShape = 'root-drop' | 'crystal-drop' | 'basalt-crack' | 'icicle-drop' | 'rune-shard' | 'dungeon-chain'
type BaseSilhouetteShape = 'root-moss' | 'crystal-rock' | 'basalt-block' | 'ice-slab' | 'rune-block' | 'dungeon-block'
type PlatformSkirtShape = 'rooted-soil' | 'purple-rock' | 'basalt-rift' | 'ice-cliff' | 'rune-stone' | 'masonry-wall'
export type StageFloorAccentKind = 'forest-leaf-root' | 'cave-crystal-glint' | 'lava-ember-crack' | 'ice-snow-shard' | 'magical-rune-spark' | 'dungeon-torch-fleck'
export type StageFloorAccentZone = 'rear' | 'side' | 'front'
export type BiomeSetDressingKind = 'forest-grove-cluster' | 'cave-crystal-cluster' | 'lava-vent-cluster' | 'ice-pine-cluster' | 'magical-rune-garden' | 'dungeon-torch-rubble'
export type BiomeSetDressingZone = 'rear' | 'side' | 'front'
export type TerrainCornerShape = 'organic-mound' | 'crystal-block' | 'basalt-block' | 'ice-slab' | 'rune-block' | 'dungeon-block'

export interface RimPieceInfo {
  key: string
  side: RimSide
  shapeKind: RimPieceShape
  cornerRadius: number
  x: number
  z: number
  rot: number
  scale: number
  height: number
  tone: number
  variant: number
}

export interface BackdropPieceInfo {
  key: string
  x: number
  y: number
  z: number
  rot: number
  scale: number
  height: number
  tone: number
  variant: number
}

export type BiomeLandmarkKind =
  | 'forest-hollow-log'
  | 'forest-pine-bridge'
  | 'forest-canopy-grove'
  | 'cave-crystal-door'
  | 'cave-glow-pool'
  | 'cave-crystal-spires'
  | 'lava-volcano-rift'
  | 'lava-smoke-vent'
  | 'lava-basalt-columns'
  | 'ice-aurora-pines'
  | 'ice-frozen-slabs'
  | 'ice-glacier-spires'
  | 'magical-portal-arch'
  | 'magical-rune-orbs'
  | 'magical-floating-monoliths'
  | 'dungeon-gate-wall'
  | 'dungeon-chain-torches'
  | 'dungeon-torch-wall'

export interface BiomeLandmarkInfo {
  key: string
  kind: BiomeLandmarkKind
  side: RimSide
  x: number
  y: number
  z: number
  rot: number
  scale: number
  height: number
  tone: number
  variant: number
}

export interface BiomeLandmarkMaterialProfile {
  organicLobes: number
  facetedSpikes: number
  emissiveCuts: number
  masonryCourses: number
  floatingSatellites: number
  warmLights: number
  anchorChunks: number
  materialBands: number
}

export function landmarkMaterialProfileForKind(kind: BiomeLandmarkKind): BiomeLandmarkMaterialProfile {
  if (kind.startsWith('forest-')) return { organicLobes: kind === 'forest-canopy-grove' ? 11 : 7, facetedSpikes: 0, emissiveCuts: 0, masonryCourses: 0, floatingSatellites: 0, warmLights: 0, anchorChunks: kind === 'forest-canopy-grove' ? 8 : 5, materialBands: 4 }
  if (kind.startsWith('cave-')) return { organicLobes: 0, facetedSpikes: kind === 'cave-crystal-spires' ? 10 : 6, emissiveCuts: kind === 'cave-glow-pool' ? 3 : 2, masonryCourses: 0, floatingSatellites: 0, warmLights: 0, anchorChunks: 5, materialBands: 3 }
  if (kind.startsWith('lava-')) return { organicLobes: 0, facetedSpikes: 2, emissiveCuts: kind === 'lava-basalt-columns' ? 8 : 6, masonryCourses: 0, floatingSatellites: 0, warmLights: 1, anchorChunks: 7, materialBands: kind === 'lava-basalt-columns' ? 7 : 5 }
  if (kind.startsWith('ice-')) return { organicLobes: kind === 'ice-aurora-pines' ? 6 : 1, facetedSpikes: kind === 'ice-glacier-spires' ? 10 : 6, emissiveCuts: 2, masonryCourses: 0, floatingSatellites: 0, warmLights: 0, anchorChunks: 5, materialBands: 4 }
  if (kind.startsWith('magical-')) return { organicLobes: 2, facetedSpikes: 5, emissiveCuts: 5, masonryCourses: 0, floatingSatellites: kind === 'magical-floating-monoliths' ? 8 : 5, warmLights: 0, anchorChunks: 4, materialBands: 5 }
  return { organicLobes: 0, facetedSpikes: 0, emissiveCuts: 0, masonryCourses: kind === 'dungeon-chain-torches' ? 4 : 8, floatingSatellites: 0, warmLights: kind === 'dungeon-gate-wall' ? 2 : 1, anchorChunks: 6, materialBands: 7 }
}

export interface PlatformEdgePieceInfo {
  key: string
  side: 'north' | 'south' | 'west' | 'east'
  shapeKind: PlatformEdgeShape
  x: number
  y: number
  z: number
  rot: number
  scale: number
  length: number
  drop: number
  tone: number
  variant: number
}

export interface PlatformContourPieceInfo {
  key: string
  side: 'north' | 'south' | 'west' | 'east'
  cellX: number
  cellY: number
  x: number
  y: number
  z: number
  rot: number
  width: number
  depth: number
  height: number
  tone: number
  variant: number
}

export interface BaseSilhouettePieceInfo {
  key: string
  side: 'north' | 'south' | 'west' | 'east'
  shapeKind: BaseSilhouetteShape
  cornerRadius: number
  x: number
  y: number
  z: number
  rot: number
  width: number
  depth: number
  height: number
  drop: number
  tone: number
  variant: number
}

export interface DioramaDeckPatchInfo {
  key: string
  zone: 'center' | 'north' | 'south' | 'west' | 'east' | 'corner'
  x: number
  y: number
  z: number
  rot: number
  width: number
  depth: number
  height: number
  tone: number
  variant: number
}

export interface DioramaSupportBlockInfo {
  key: string
  side: RimSide
  x: number
  y: number
  z: number
  rot: number
  width: number
  depth: number
  height: number
  cornerRadius: number
  capKind: 'moss-slab' | 'stone-slab' | 'basalt-slab' | 'snow-slab' | 'rune-slab' | 'brass-slab'
  capWidth: number
  capDepth: number
  capHeight: number
  capRadius: number
  tone: number
  variant: number
}

export interface PlatformSkirtPanelInfo {
  key: string
  side: RimSide
  shapeKind: PlatformSkirtShape
  x: number
  y: number
  z: number
  rot: number
  width: number
  depth: number
  height: number
  cornerRadius: number
  tone: number
  variant: number
}

export interface StageFloorAccentInfo {
  key: string
  kind: StageFloorAccentKind
  zone: StageFloorAccentZone
  x: number
  z: number
  rot: number
  scale: number
  width: number
  depth: number
  opacity: number
  tone: number
  variant: number
}

export interface BiomeSetDressingClusterInfo {
  key: string
  kind: BiomeSetDressingKind
  zone: BiomeSetDressingZone
  x: number
  z: number
  rot: number
  scale: number
  width: number
  depth: number
  height: number
  tone: number
  variant: number
}

export interface StageGroundInfo {
  radius: number
  shadowRadius: number
  washRadius: number
  segments: number
  baseOpacity: number
  shadowOpacity: number
  washOpacity: number
  shadowSquash: number
  washSquash: number
}

export interface BiomeArtBackdropInfo {
  src: string
  width: number
  height: number
  y: number
  z: number
  opacity: number
  softMaskOpacity: number
  washOpacity: number
  vignetteOpacity: number
  glowScale: number
}

export type TerrainCompositionZone = 'rear' | 'side' | 'front'

const PLATFORM_CELL_TILES = new Set('-oepgktwsl~mxbd'.split(''))

function hashText(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function seeded(seed: number) {
  let s = seed >>> 0 || 1
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 0xffffffff)
}

function clusteredSideT(centerT: number, slot: number, count: number, radius: number, span: number, rnd: () => number): number {
  const spread = count === 1 ? 0 : slot / (count - 1) - 0.5
  return Math.max(0.04, Math.min(0.96, centerT + (spread * radius + (rnd() - 0.5) * radius * 0.3) / span))
}

function sidePoint(side: RimSide, width: number, depth: number, t: number, offset: number, rnd: () => number) {
  const halfWidth = width / 2
  const halfDepth = depth / 2
  const edgeJitter = (rnd() - 0.5) * 0.08
  const x = side === 'west' ? -halfWidth - offset : side === 'east' ? halfWidth + offset : -halfWidth + t * width
  const z = side === 'north' ? -halfDepth - offset : side === 'south' ? halfDepth + offset : -halfDepth + t * depth
  const rot = side === 'north' ? edgeJitter : side === 'south' ? Math.PI + edgeJitter : side === 'west' ? Math.PI / 2 + edgeJitter : -Math.PI / 2 + edgeJitter
  return { x, z, rot }
}

function dioramaMaterialForBiome(biome: BiomeId) {
  if (biome === 'forest') return { top: '#31482b', side: '#2b2015', glow: '#5fd38b', roughness: 0.98, metalness: 0 }
  if (biome === 'cave') return { top: '#342940', side: '#1d1626', glow: '#9b8cff', roughness: 0.94, metalness: 0.01 }
  if (biome === 'lava') return { top: '#2f2725', side: '#170807', glow: '#ff3d16', roughness: 0.88, metalness: 0.02 }
  if (biome === 'ice') return { top: '#9bddeb', side: '#14364a', glow: '#5fd6ff', roughness: 0.5, metalness: 0.02 }
  if (biome === 'magical') return { top: '#402052', side: '#190b22', glow: '#e56aff', roughness: 0.76, metalness: 0.01 }
  return { top: '#46341f', side: '#21170d', glow: '#d0a24c', roughness: 0.9, metalness: 0.03 }
}

function supportCapKindForBiome(biome: BiomeId): DioramaSupportBlockInfo['capKind'] {
  if (biome === 'forest') return 'moss-slab'
  if (biome === 'cave') return 'stone-slab'
  if (biome === 'lava') return 'basalt-slab'
  if (biome === 'ice') return 'snow-slab'
  if (biome === 'magical') return 'rune-slab'
  return 'brass-slab'
}

function platformEdgeShapeForBiome(biome: BiomeId): PlatformEdgeShape {
  if (biome === 'forest') return 'root-drop'
  if (biome === 'cave') return 'crystal-drop'
  if (biome === 'lava') return 'basalt-crack'
  if (biome === 'ice') return 'icicle-drop'
  if (biome === 'magical') return 'rune-shard'
  return 'dungeon-chain'
}

function baseSilhouetteShapeForBiome(biome: BiomeId): BaseSilhouetteShape {
  if (biome === 'forest') return 'root-moss'
  if (biome === 'cave') return 'crystal-rock'
  if (biome === 'lava') return 'basalt-block'
  if (biome === 'ice') return 'ice-slab'
  if (biome === 'magical') return 'rune-block'
  return 'dungeon-block'
}

function platformSkirtShapeForBiome(biome: BiomeId): PlatformSkirtShape {
  if (biome === 'forest') return 'rooted-soil'
  if (biome === 'cave') return 'purple-rock'
  if (biome === 'lava') return 'basalt-rift'
  if (biome === 'ice') return 'ice-cliff'
  if (biome === 'magical') return 'rune-stone'
  return 'masonry-wall'
}

function stageFloorAccentKindForBiome(biome: BiomeId): StageFloorAccentKind {
  if (biome === 'forest') return 'forest-leaf-root'
  if (biome === 'cave') return 'cave-crystal-glint'
  if (biome === 'lava') return 'lava-ember-crack'
  if (biome === 'ice') return 'ice-snow-shard'
  if (biome === 'magical') return 'magical-rune-spark'
  return 'dungeon-torch-fleck'
}

function setDressingKindForBiome(biome: BiomeId): BiomeSetDressingKind {
  if (biome === 'forest') return 'forest-grove-cluster'
  if (biome === 'cave') return 'cave-crystal-cluster'
  if (biome === 'lava') return 'lava-vent-cluster'
  if (biome === 'ice') return 'ice-pine-cluster'
  if (biome === 'magical') return 'magical-rune-garden'
  return 'dungeon-torch-rubble'
}

function rimShapeForBiome(biome: BiomeId): RimPieceShape {
  if (biome === 'forest') return 'tree-cluster'
  if (biome === 'cave') return 'stone-spire'
  if (biome === 'lava') return 'basalt-vent'
  if (biome === 'ice') return 'snow-spire'
  if (biome === 'magical') return 'rune-shard'
  return 'dungeon-pillar'
}

export function terrainCornerShapeForBiome(biome: BiomeId): TerrainCornerShape {
  if (biome === 'forest') return 'organic-mound'
  if (biome === 'cave') return 'crystal-block'
  if (biome === 'lava') return 'basalt-block'
  if (biome === 'ice') return 'ice-slab'
  if (biome === 'magical') return 'rune-block'
  return 'dungeon-block'
}

function chunkCornerRadius(biome: BiomeId, width: number, height: number, depth: number): number {
  const cap = biome === 'forest' ? 0.052 : biome === 'ice' ? 0.026 : 0.034
  const ratio = biome === 'forest' ? 0.15 : 0.105
  return Math.min(cap, Math.min(width, height, depth) * ratio)
}

let softDiscTexture: THREE.Texture | null = null
let softAlphaMaskTexture: THREE.Texture | null = null

function getSoftDiscTexture(): THREE.Texture | null {
  if (softDiscTexture) return softDiscTexture
  if (typeof document === 'undefined') return null
  const s = 128
  const c = document.createElement('canvas')
  c.width = s
  c.height = s
  const ctx = c.getContext('2d')
  if (!ctx) return null
  const grad = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2)
  grad.addColorStop(0, 'rgba(255,255,255,1)')
  grad.addColorStop(0.55, 'rgba(255,255,255,0.55)')
  grad.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, s, s)
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  softDiscTexture = tex
  return tex
}

function getSoftAlphaMaskTexture(): THREE.Texture | null {
  if (softAlphaMaskTexture) return softAlphaMaskTexture
  if (typeof document === 'undefined') return null
  const s = 128
  const c = document.createElement('canvas')
  c.width = s
  c.height = s
  const ctx = c.getContext('2d')
  if (!ctx) return null
  const grad = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2)
  grad.addColorStop(0, 'rgb(255,255,255)')
  grad.addColorStop(0.42, 'rgb(220,220,220)')
  grad.addColorStop(0.74, 'rgb(74,74,74)')
  grad.addColorStop(1, 'rgb(0,0,0)')
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, s, s)
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.NoColorSpace
  softAlphaMaskTexture = tex
  return tex
}

function platformTileAt(grid: string[], x: number, y: number): string {
  if (y < 0 || y >= grid.length) return ' '
  if (x < 0 || x >= grid[y].length) return ' '
  return grid[y][x] ?? ' '
}

function isPlatformCell(grid: string[], x: number, y: number): boolean {
  return PLATFORM_CELL_TILES.has(platformTileAt(grid, x, y))
}

function PulseLight({ color, distance, intensity, reducedMotion, phase = 0 }: { color: string; distance: number; intensity: number; reducedMotion: boolean; phase?: number }) {
  const ref = useRef<THREE.PointLight>(null)
  useFrame((state) => {
    if (!ref.current || reducedMotion) return
    ref.current.intensity = intensity * (0.78 + Math.sin(state.clock.elapsedTime * 2.8 + phase) * 0.22)
  })
  return <pointLight ref={ref} color={color} distance={distance} intensity={intensity} />
}

function FlickerFlame({
  color,
  hot = '#ffd45c',
  reducedMotion,
  distance = 1.8,
  intensity = 0.75,
  scale = 1,
}: {
  color: string
  hot?: string
  reducedMotion: boolean
  distance?: number
  intensity?: number
  scale?: number
}) {
  const groupRef = useRef<THREE.Group>(null)
  const matRef = useRef<THREE.MeshBasicMaterial>(null)
  const lightRef = useRef<THREE.PointLight>(null)
  useFrame((state) => {
    if (reducedMotion) return
    const t = state.clock.elapsedTime
    const pulse = 0.86 + Math.sin(t * 8.1) * 0.09 + Math.sin(t * 13.7) * 0.04
    if (groupRef.current) groupRef.current.scale.set(scale * (0.92 + pulse * 0.08), scale * (0.86 + pulse * 0.18), scale * (0.92 + pulse * 0.08))
    if (matRef.current) matRef.current.opacity = 0.78 + pulse * 0.18
    if (lightRef.current) lightRef.current.intensity = intensity * pulse
  })
  return (
    <group ref={groupRef} scale={scale}>
      <mesh position={[0, 0.05, 0]}>
        <coneGeometry args={[0.11, 0.28, 12]} />
        <meshBasicMaterial ref={matRef} color={color} transparent opacity={0.92} toneMapped={false} />
      </mesh>
      <mesh position={[0, 0.05, 0]} scale={[0.54, 0.68, 0.54]}>
        <coneGeometry args={[0.1, 0.24, 12]} />
        <meshBasicMaterial color={hot} transparent opacity={0.84} toneMapped={false} />
      </mesh>
      <pointLight ref={lightRef} color={color} distance={distance} intensity={intensity} />
    </group>
  )
}

export function buildDioramaDeckPatches(layout: BoardLayout, biome: BiomeId, graphics: 'high' | 'medium' | 'low'): DioramaDeckPatchInfo[] {
  if (graphics === 'low') return []
  const pitch = layout.tileSize + layout.gap
  const width = layout.cols * pitch + 1.08
  const depth = layout.rows * pitch + 1.08
  const rnd = seeded(hashText(`deck:${biome}:${layout.cols}:${layout.rows}`))
  const high = graphics === 'high'
  const specs: { zone: DioramaDeckPatchInfo['zone']; x: number; z: number; width: number; depth: number; keepMedium: boolean }[] = [
    { zone: 'center', x: 0, z: 0, width: 0.34, depth: 0.25, keepMedium: true },
    { zone: 'north', x: -0.28, z: -0.42, width: 0.22, depth: 0.14, keepMedium: true },
    { zone: 'north', x: 0.28, z: -0.44, width: 0.23, depth: 0.13, keepMedium: false },
    { zone: 'west', x: -0.43, z: -0.08, width: 0.13, depth: 0.3, keepMedium: true },
    { zone: 'east', x: 0.43, z: 0.1, width: 0.14, depth: 0.29, keepMedium: false },
    { zone: 'south', x: -0.24, z: 0.44, width: 0.12, depth: 0.065, keepMedium: false },
    { zone: 'south', x: 0.31, z: 0.43, width: 0.1, depth: 0.06, keepMedium: false },
    { zone: 'corner', x: -0.4, z: -0.38, width: 0.14, depth: 0.11, keepMedium: false },
    { zone: 'corner', x: 0.4, z: -0.36, width: 0.14, depth: 0.11, keepMedium: true },
    { zone: 'corner', x: -0.39, z: 0.36, width: 0.12, depth: 0.09, keepMedium: false },
    { zone: 'corner', x: 0.4, z: 0.36, width: 0.12, depth: 0.09, keepMedium: false },
  ]
  return specs
    .filter((spec) => high || spec.keepMedium)
    .map((spec, i) => {
      const tone = rnd()
      const edgeBias = spec.zone === 'south' ? 0.52 : spec.zone === 'center' ? 0.96 : 0.92
      return {
        key: `${biome}-deck-${spec.zone}-${i}`,
        zone: spec.zone,
        x: spec.x * width + (rnd() - 0.5) * 0.18,
        y: -0.215 - rnd() * 0.018,
        z: spec.z * depth + (rnd() - 0.5) * 0.18,
        rot: (rnd() - 0.5) * 0.16,
        width: width * spec.width * (0.88 + rnd() * 0.22) * edgeBias,
        depth: depth * spec.depth * (0.9 + rnd() * 0.2) * edgeBias,
        height: 0.062 + rnd() * 0.032,
        tone,
        variant: (i + Math.floor(tone * 17)) % 9,
      }
    })
}

export function buildDioramaSupportBlocks(layout: BoardLayout, biome: BiomeId, graphics: 'high' | 'medium' | 'low'): DioramaSupportBlockInfo[] {
  if (graphics === 'low') return []
  const pitch = layout.tileSize + layout.gap
  const width = layout.cols * pitch + 0.3
  const depth = layout.rows * pitch + 0.3
  const rnd = seeded(hashText(`support:${biome}:${layout.cols}:${layout.rows}`))
  const high = graphics === 'high'
  const specs: { side: RimSide; t: number; high: number; medium: number; radius: number; scale: number; offset: number }[] = [
    { side: 'north', t: 0.13, high: 3, medium: 2, radius: 0.56, scale: 1.06, offset: 0.03 },
    { side: 'north', t: 0.5, high: 4, medium: 2, radius: 0.72, scale: 1.16, offset: 0.04 },
    { side: 'north', t: 0.87, high: 3, medium: 1, radius: 0.54, scale: 1.08, offset: 0.03 },
    { side: 'west', t: 0.22, high: 2, medium: 1, radius: 0.48, scale: 0.94, offset: 0.02 },
    { side: 'west', t: 0.74, high: 2, medium: 1, radius: 0.48, scale: 0.94, offset: 0.02 },
    { side: 'east', t: 0.24, high: 2, medium: 1, radius: 0.48, scale: 0.94, offset: 0.02 },
    { side: 'east', t: 0.72, high: 2, medium: 1, radius: 0.48, scale: 0.94, offset: 0.02 },
    { side: 'south', t: 0.32, high: 1, medium: 0, radius: 0.18, scale: 0.56, offset: 0.01 },
  ]
  const blocks: DioramaSupportBlockInfo[] = []
  let index = 0
  for (const spec of specs) {
    const count = high ? spec.high : spec.medium
    const span = spec.side === 'north' || spec.side === 'south' ? width : depth
    for (let slot = 0; slot < count; slot++) {
      const t = clusteredSideT(spec.t, slot, count, spec.radius, span, rnd)
      const p = sidePoint(spec.side, width, depth, t, spec.offset + rnd() * 0.14, rnd)
      const long = (0.54 + rnd() * 0.5) * spec.scale
      const short = (0.34 + rnd() * 0.3) * spec.scale
      const heightScale = spec.side === 'south' ? 0.74 : 1
      const height = (0.3 + rnd() * 0.28) * spec.scale * heightScale
      const horizontal = spec.side === 'north' || spec.side === 'south'
      const tone = rnd()
      const blockWidth = horizontal ? long : short
      const blockDepth = horizontal ? short : long
      const capWidth = Math.max(0.22, blockWidth * (0.68 + rnd() * 0.14) * (spec.side === 'south' ? 0.86 : 1))
      const capDepth = Math.max(0.2, blockDepth * (0.68 + rnd() * 0.14) * (spec.side === 'south' ? 0.86 : 1))
      const capLimit = Math.min(0.074, height * 0.2, Math.min(capWidth, capDepth) * 0.22)
      const capHeight = Math.max(0.022, Math.min(capLimit, height * (0.1 + rnd() * 0.045)))
      const cornerRadius = Math.min(0.052, Math.min(blockWidth, blockDepth, height) * 0.16)
      const capRadius = Math.min(0.024, Math.min(capWidth, capDepth, capHeight) * 0.46)
      blocks.push({
        key: `${biome}-support-${spec.side}-${index}`,
        side: spec.side,
        x: p.x + (rnd() - 0.5) * 0.16,
        y: -0.25 - height / 2 - rnd() * 0.05,
        z: p.z + (rnd() - 0.5) * 0.16,
        rot: p.rot + (rnd() - 0.5) * 0.14,
        width: blockWidth,
        depth: blockDepth,
        height,
        cornerRadius,
        capKind: supportCapKindForBiome(biome),
        capWidth,
        capDepth,
        capHeight,
        capRadius,
        tone,
        variant: (index + Math.floor(tone * 19)) % 11,
      })
      index += 1
    }
  }
  return blocks
}

export function buildPlatformSkirtPanels(layout: BoardLayout, biome: BiomeId, graphics: 'high' | 'medium' | 'low'): PlatformSkirtPanelInfo[] {
  if (graphics === 'low') return []
  const pitch = layout.tileSize + layout.gap
  const width = layout.cols * pitch + 0.96
  const depth = layout.rows * pitch + 0.96
  const rnd = seeded(hashText(`skirt:${biome}:${layout.cols}:${layout.rows}`))
  const high = graphics === 'high'
  const specs: { side: RimSide; ts: number[]; thickness: number; length: number; height: number; offset: number }[] = [
    { side: 'north', ts: high ? [0.06, 0.2, 0.35, 0.5, 0.65, 0.8, 0.94] : [0.14, 0.38, 0.62, 0.86], thickness: 0.46, length: 1.82, height: 1.34, offset: 0.04 },
    { side: 'west', ts: high ? [0.14, 0.36, 0.62, 0.86] : [0.24, 0.52, 0.8], thickness: 0.42, length: 1.48, height: 1.18, offset: 0.035 },
    { side: 'east', ts: high ? [0.14, 0.38, 0.64, 0.86] : [0.22, 0.5, 0.78], thickness: 0.42, length: 1.48, height: 1.18, offset: 0.035 },
    { side: 'south', ts: high ? [0.34, 0.76] : [], thickness: 0.3, length: 1.0, height: 0.68, offset: 0.035 },
  ]
  const panels: PlatformSkirtPanelInfo[] = []
  specs.forEach((spec) => {
    const horizontal = spec.side === 'north' || spec.side === 'south'
    const span = horizontal ? width : depth
    spec.ts.forEach((t, slot) => {
      const point = sidePoint(spec.side, width, depth, Math.max(0.04, Math.min(0.96, t + (rnd() - 0.5) * 0.022)), spec.offset + rnd() * 0.06, rnd)
      const tone = rnd()
      const height = (0.58 + rnd() * 0.28) * spec.height + (biome === 'dungeon' || biome === 'cave' ? 0.08 : biome === 'ice' ? 0.06 : 0)
      const length = Math.min(span * 0.36, (spec.length + rnd() * 0.48) * (spec.side === 'south' ? 0.82 : 1))
      const thickness = spec.thickness * (0.86 + rnd() * 0.22)
      const panelWidth = horizontal ? length : thickness
      const panelDepth = horizontal ? thickness : length
      panels.push({
        key: `${biome}-skirt-${spec.side}-${slot}`,
        side: spec.side,
        shapeKind: platformSkirtShapeForBiome(biome),
        x: point.x + (rnd() - 0.5) * 0.08,
        y: -0.18 - height / 2 - rnd() * 0.035 - (spec.side === 'south' ? 0.035 : 0),
        z: point.z + (rnd() - 0.5) * 0.08,
        rot: point.rot + (rnd() - 0.5) * 0.06,
        width: panelWidth,
        depth: panelDepth,
        height,
        cornerRadius: chunkCornerRadius(biome, panelWidth, height, panelDepth),
        tone,
        variant: (panels.length + Math.floor(tone * 23)) % 13,
      })
    })
  })
  return panels.sort((a, b) => {
    const depthA = a.side === 'south' ? 0 : a.side === 'east' || a.side === 'west' ? 1 : 2
    const depthB = b.side === 'south' ? 0 : b.side === 'east' || b.side === 'west' ? 1 : 2
    return depthA - depthB || a.key.localeCompare(b.key)
  })
}

function DioramaDeckPatch({ patch, biome, material }: { patch: DioramaDeckPatchInfo; biome: BiomeId; material: ReturnType<typeof dioramaMaterialForBiome> }) {
  const geom = useMemo(() => makeRoundedBox(patch.width, patch.height, patch.depth, 0.12, 3), [patch.depth, patch.height, patch.width])
  const color = useMemo(() => new THREE.Color(material.top).lerp(new THREE.Color(material.glow), 0.035 + patch.tone * 0.12).getStyle(), [material.glow, material.top, patch.tone])
  const side = useMemo(() => new THREE.Color(material.side).lerp(new THREE.Color(material.top), 0.18 + patch.tone * 0.18).getStyle(), [material.side, material.top, patch.tone])
  return (
    <group position={[patch.x, patch.y, patch.z]} rotation={[0, patch.rot, 0]}>
      <mesh geometry={geom} receiveShadow castShadow>
        <meshStandardMaterial color={color} roughness={material.roughness} metalness={material.metalness} emissive={material.glow} emissiveIntensity={biome === 'lava' ? 0.08 : biome === 'magical' ? 0.06 : 0.018} />
      </mesh>
      {biome === 'forest' && (
        <mesh position={[0, patch.height * 0.62, 0]} scale={[patch.width * 0.48, 0.14, patch.depth * 0.92]} receiveShadow>
          <boxGeometry args={[0.24, 0.055, 0.22]} />
          <meshStandardMaterial color={patch.variant % 2 ? '#6f9a4a' : '#86aa60'} roughness={0.94} />
        </mesh>
      )}
      {biome === 'lava' && (
        <mesh position={[0, patch.height * 0.62, 0]} rotation={[0, 0, (patch.variant - 4) * 0.08]}>
          <boxGeometry args={[patch.width * 0.42, 0.025, 0.032]} />
          <meshBasicMaterial color={patch.variant % 2 ? '#ffd45c' : '#ff6a45'} transparent opacity={0.82} toneMapped={false} />
        </mesh>
      )}
      {biome === 'ice' && (
        <mesh position={[0, patch.height * 0.68, 0]} scale={[patch.width * 0.52, 0.16, patch.depth * 0.9]} receiveShadow>
          <boxGeometry args={[0.22, 0.05, 0.22]} />
          <meshStandardMaterial color="#f4fbff" roughness={0.52} />
        </mesh>
      )}
      {biome === 'magical' && (
        <mesh position={[0, patch.height * 0.7, 0]} rotation={[Math.PI / 2, 0, patch.tone * Math.PI]}>
          <torusGeometry args={[Math.min(patch.width, patch.depth) * 0.18, 0.008, 6, 24, Math.PI * 1.42]} />
          <meshBasicMaterial color={material.glow} transparent opacity={0.48} toneMapped={false} />
        </mesh>
      )}
      {biome === 'cave' && patch.variant % 2 === 0 && (
        <mesh position={[patch.width * 0.24, patch.height * 0.9, patch.depth * 0.12]} rotation={[0.16, patch.tone * Math.PI, 0.12]} castShadow>
          <coneGeometry args={[0.048, 0.18, 5]} />
          <meshStandardMaterial color={material.glow} emissive={material.glow} emissiveIntensity={0.28} roughness={0.34} transparent opacity={0.88} />
        </mesh>
      )}
      {biome === 'dungeon' && (
        <mesh position={[0, patch.height * 0.68, patch.depth * 0.18]}>
          <boxGeometry args={[patch.width * 0.54, 0.024, 0.024]} />
          <meshStandardMaterial color="#d0a24c" metalness={0.22} roughness={0.52} />
        </mesh>
      )}
      <mesh position={[0, -patch.height * 0.52, 0]} scale={[0.98, 0.65, 0.96]} receiveShadow>
        <boxGeometry args={[patch.width, patch.height, patch.depth]} />
        <meshStandardMaterial color={side} roughness={material.roughness} metalness={material.metalness} />
      </mesh>
    </group>
  )
}

function PlatformSkirtPanel({ panel, biome, theme, material, reducedMotion }: { panel: PlatformSkirtPanelInfo; biome: BiomeId; theme: BiomeTheme; material: ReturnType<typeof dioramaMaterialForBiome>; reducedMotion: boolean }) {
  const geom = useMemo(() => makeRoundedBox(panel.width, panel.height, panel.depth, panel.cornerRadius, 2), [panel.cornerRadius, panel.depth, panel.height, panel.width])
  const side = useMemo(() => new THREE.Color(material.side).lerp(new THREE.Color(material.top), 0.2 + panel.tone * 0.2).getStyle(), [material.side, material.top, panel.tone])
  const cap = useMemo(() => new THREE.Color(material.top).lerp(new THREE.Color(material.glow), 0.04 + panel.tone * 0.1).getStyle(), [material.glow, material.top, panel.tone])
  const course = useMemo(() => new THREE.Color(side).lerp(new THREE.Color(material.glow), biome === 'lava' || biome === 'magical' ? 0.22 : 0.08 + panel.tone * 0.08).getStyle(), [biome, material.glow, panel.tone, side])
  const courseCount = biome === 'dungeon' ? 4 : biome === 'forest' ? 2 : 3
  const horizontal = panel.side === 'north' || panel.side === 'south'
  return (
    <group position={[panel.x, panel.y, panel.z]} rotation={[0, panel.rot, 0]}>
      <mesh geometry={geom} receiveShadow castShadow>
        <meshStandardMaterial color={biome === 'lava' ? '#190907' : side} roughness={material.roughness} metalness={material.metalness} emissive={material.glow} emissiveIntensity={biome === 'lava' ? 0.075 : biome === 'magical' ? 0.12 : biome === 'cave' || biome === 'ice' ? 0.045 : 0.018} />
      </mesh>
      <mesh position={[0, panel.height / 2 + 0.008, 0]} scale={[0.96, 1, 0.94]} receiveShadow>
        <boxGeometry args={[panel.width, 0.035, panel.depth]} />
        <meshStandardMaterial color={cap} roughness={material.roughness} metalness={material.metalness} emissive={material.glow} emissiveIntensity={biome === 'lava' ? 0.08 : biome === 'magical' ? 0.08 : 0.02} />
      </mesh>
      {Array.from({ length: courseCount }, (_, i) => {
        const lengthScale = 0.58 + ((panel.variant + i) % 3) * 0.12
        const y = panel.height * (0.26 - i * (biome === 'dungeon' ? 0.15 : 0.19))
        const hot = biome === 'lava' && i === 1
        return horizontal ? (
          <mesh key={`course-${i}`} position={[((i % 2) - 0.5) * panel.width * 0.08, y, panel.depth * 0.55]}>
            <boxGeometry args={[panel.width * lengthScale, 0.024, 0.026]} />
            {hot
              ? <meshBasicMaterial color="#ff5a1f" transparent opacity={0.74} toneMapped={false} />
              : <meshStandardMaterial color={course} roughness={material.roughness} metalness={material.metalness} emissive={material.glow} emissiveIntensity={biome === 'magical' ? 0.12 : biome === 'cave' || biome === 'ice' ? 0.045 : 0.018} />}
          </mesh>
        ) : (
          <mesh key={`course-${i}`} position={[panel.width * 0.55, y, ((i % 2) - 0.5) * panel.depth * 0.08]}>
            <boxGeometry args={[0.026, 0.024, panel.depth * lengthScale]} />
            {hot
              ? <meshBasicMaterial color="#ff5a1f" transparent opacity={0.74} toneMapped={false} />
              : <meshStandardMaterial color={course} roughness={material.roughness} metalness={material.metalness} emissive={material.glow} emissiveIntensity={biome === 'magical' ? 0.12 : biome === 'cave' || biome === 'ice' ? 0.045 : 0.018} />}
          </mesh>
        )
      })}
      {biome === 'forest' && (
        <>
          <mesh position={[0, panel.height / 2 + 0.035, 0]} scale={[panel.width * 0.78, 0.24, panel.depth * 1.6]} receiveShadow>
            <sphereGeometry args={[0.16, 12, 7]} />
            <meshStandardMaterial color={panel.variant % 2 ? '#6f9a4a' : '#86aa60'} roughness={0.94} />
          </mesh>
          {[-0.28, 0.08, 0.34].map((x, i) => (
            <mesh key={i} position={[x * panel.width, -panel.height * (0.1 + i * 0.1), panel.depth * 0.54]} rotation={[Math.PI / 2, 0, (i - 1) * 0.36]} castShadow>
              <capsuleGeometry args={[0.028, panel.height * (0.46 + i * 0.08), 4, 8]} />
              <meshStandardMaterial color={i === 1 ? '#70442b' : '#51351f'} roughness={0.97} />
            </mesh>
          ))}
        </>
      )}
      {biome === 'cave' && panel.variant % 2 === 0 && (
        <mesh position={[panel.width * 0.22, panel.height * 0.12, panel.depth * 0.54]} rotation={[0.18, panel.tone * Math.PI, 0.1]} castShadow>
          <coneGeometry args={[0.07 + panel.tone * 0.035, 0.36 + panel.tone * 0.18, 5]} />
          <meshStandardMaterial color={theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.4} roughness={0.28} transparent opacity={0.9} />
        </mesh>
      )}
      {biome === 'lava' && (
        <>
          {[0, 1, 2].map((i) => (
            <mesh key={i} position={[(i - 1) * panel.width * 0.22, panel.height * (0.16 - i * 0.16), panel.depth * 0.535]} rotation={[0, 0, (panel.variant - 6) * 0.04 + i * 0.08]}>
              <boxGeometry args={[panel.width * (0.26 + i * 0.08), 0.032, 0.026]} />
              <meshBasicMaterial color={i === 1 ? '#ffd45c' : '#ff5a1f'} transparent opacity={0.86} toneMapped={false} />
            </mesh>
          ))}
          {panel.variant % 5 === 0 && (
            <group position={[panel.width * 0.24, panel.height * 0.54, panel.depth * 0.38]}>
              <FlickerFlame color="#ff7a3c" hot="#ffd45c" reducedMotion={reducedMotion} distance={1.35} intensity={0.3} scale={0.38} />
            </group>
          )}
        </>
      )}
      {biome === 'ice' && (
        <>
          <mesh position={[0, panel.height / 2 + 0.045, 0]} scale={[panel.width * 0.72, 0.18, panel.depth * 1.45]} receiveShadow>
            <boxGeometry args={[0.3, 0.055, 0.22]} />
            <meshStandardMaterial color="#f4fbff" roughness={0.52} />
          </mesh>
          {[-0.26, 0.22].map((x, i) => (
            <mesh key={i} position={[x * panel.width, -panel.height * 0.22, panel.depth * 0.54]} rotation={[Math.PI, 0, (i - 0.5) * 0.08]} castShadow>
              <coneGeometry args={[0.048, panel.height * 0.58, 6]} />
              <meshStandardMaterial color={i ? '#ffffff' : theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.16} roughness={0.28} transparent opacity={0.9} />
            </mesh>
          ))}
        </>
      )}
      {biome === 'magical' && (
        <>
          <mesh position={[0, panel.height * 0.15, panel.depth * 0.54]} rotation={[Math.PI / 2, 0, panel.tone * Math.PI]}>
            <torusGeometry args={[Math.min(panel.width, 0.62) * 0.2, 0.009, 6, 28, Math.PI * 1.45]} />
            <meshBasicMaterial color={theme.biomeSoft} transparent opacity={0.58} toneMapped={false} />
          </mesh>
          {panel.variant % 2 === 0 && (
            <mesh position={[panel.width * 0.24, panel.height * 0.3, panel.depth * 0.52]} rotation={[0.36, panel.tone * Math.PI, 0.18]} castShadow>
              <octahedronGeometry args={[0.07 + panel.tone * 0.035, 0]} />
              <meshStandardMaterial color={panel.variant % 4 === 0 ? '#7de3ff' : theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.56} roughness={0.24} transparent opacity={0.9} />
            </mesh>
          )}
        </>
      )}
      {biome === 'dungeon' && (
        <>
          {[-0.28, 0, 0.28].map((x, i) => (
            <mesh key={i} position={[x * panel.width, panel.height * 0.12, panel.depth * 0.535]}>
              <boxGeometry args={[0.034, panel.height * (0.52 - Math.abs(i - 1) * 0.08), 0.024]} />
              <meshStandardMaterial color="#d0a24c" metalness={0.28} roughness={0.48} />
            </mesh>
          ))}
          {panel.variant % 3 === 0 && (
            <mesh position={[panel.width * 0.26, -panel.height * 0.18, panel.depth * 0.56]} rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[0.064, 0.012, 6, 14]} />
              <meshStandardMaterial color="#d0a24c" metalness={0.36} roughness={0.44} />
            </mesh>
          )}
        </>
      )}
    </group>
  )
}

function DioramaSupportBlock({ block, biome, material }: { block: DioramaSupportBlockInfo; biome: BiomeId; material: ReturnType<typeof dioramaMaterialForBiome> }) {
  const geom = useMemo(() => makeRoundedBox(block.width, block.height, block.depth, block.cornerRadius, 2), [block.cornerRadius, block.depth, block.height, block.width])
  const capGeom = useMemo(() => makeRoundedBox(block.capWidth, block.capHeight, block.capDepth, block.capRadius, 1), [block.capDepth, block.capHeight, block.capRadius, block.capWidth])
  const color = useMemo(() => new THREE.Color(material.side).lerp(new THREE.Color(material.top), 0.2 + block.tone * 0.22).getStyle(), [block.tone, material.side, material.top])
  const cap = useMemo(() => new THREE.Color(material.top).lerp(new THREE.Color(material.glow), 0.05 + block.tone * 0.1).getStyle(), [block.tone, material.glow, material.top])
  const emissiveIntensity = biome === 'lava' ? 0.1 : biome === 'magical' ? 0.07 : biome === 'cave' ? 0.035 : 0.018
  return (
    <group position={[block.x, block.y, block.z]} rotation={[0, block.rot, 0]}>
      <mesh geometry={geom} receiveShadow castShadow>
        <meshStandardMaterial color={color} roughness={material.roughness} metalness={material.metalness} emissive={material.glow} emissiveIntensity={emissiveIntensity} />
      </mesh>
      <mesh geometry={capGeom} position={[0, block.height / 2 + block.capHeight / 2 - 0.012, 0]} rotation={[0, (block.variant - 5) * 0.035, 0]} receiveShadow castShadow>
        <meshStandardMaterial color={cap} roughness={material.roughness} metalness={material.metalness} emissive={material.glow} emissiveIntensity={emissiveIntensity * 0.6} />
      </mesh>
      {biome === 'forest' && (
        <mesh position={[block.capWidth * 0.18, block.height / 2 + block.capHeight + 0.006, block.capDepth * 0.06]} rotation={[0, (block.variant - 4) * 0.05, 0]} receiveShadow>
          <boxGeometry args={[block.capWidth * 0.42, 0.035, block.capDepth * 0.58]} />
          <meshStandardMaterial color={block.variant % 2 ? '#6f9a4a' : '#86aa60'} roughness={0.96} />
        </mesh>
      )}
      {biome === 'cave' && block.variant % 2 === 0 && (
        <mesh position={[block.width * 0.22, block.height * 0.74, block.depth * 0.12]} rotation={[0.16, block.tone * Math.PI, 0.1]} castShadow>
          <coneGeometry args={[0.05, 0.2, 5]} />
          <meshStandardMaterial color={material.glow} emissive={material.glow} emissiveIntensity={0.34} roughness={0.36} transparent opacity={0.9} />
        </mesh>
      )}
      {biome === 'lava' && (
        <mesh position={[0, block.height * 0.55, block.depth * 0.2]} rotation={[0, 0, (block.variant - 5) * 0.06]}>
          <boxGeometry args={[block.width * 0.5, 0.026, 0.032]} />
          <meshBasicMaterial color={block.variant % 2 ? '#ffd45c' : '#ff6a45'} transparent opacity={0.86} toneMapped={false} />
        </mesh>
      )}
      {biome === 'ice' && (
        <mesh position={[0, block.height / 2 + block.capHeight + 0.004, 0]} rotation={[0, (block.variant - 5) * 0.06, 0]} receiveShadow>
          <boxGeometry args={[block.capWidth * 0.58, 0.04, block.capDepth * 0.68]} />
          <meshStandardMaterial color="#f4fbff" roughness={0.54} />
        </mesh>
      )}
      {biome === 'magical' && (
        <mesh position={[0, block.height * 0.7, 0]} rotation={[Math.PI / 2, 0, block.tone * Math.PI]}>
          <torusGeometry args={[Math.min(block.width, block.depth) * 0.18, 0.008, 6, 24, Math.PI * 1.35]} />
          <meshBasicMaterial color={material.glow} transparent opacity={0.5} toneMapped={false} />
        </mesh>
      )}
      {biome === 'dungeon' && (
        <mesh position={[0, block.height * 0.58, block.depth * 0.16]}>
          <boxGeometry args={[block.width * 0.5, 0.028, 0.028]} />
          <meshStandardMaterial color="#d0a24c" metalness={0.2} roughness={0.54} />
        </mesh>
      )}
    </group>
  )
}

function DioramaBase({ layout, theme, biome, reducedMotion, graphics }: { layout: BoardLayout; theme: BiomeTheme; biome: BiomeId; reducedMotion: boolean; graphics: 'high' | 'medium' | 'low' }) {
  const pitch = layout.tileSize + layout.gap
  const width = layout.cols * pitch + 0.2
  const depth = layout.rows * pitch + 0.2
  const glowWidth = layout.cols * pitch + 0.72
  const glowDepth = layout.rows * pitch + 0.72
  const baseGeom = useMemo(() => makeRoundedBox(width, 0.28, depth, 0.14, 4), [depth, width])
  const material = useMemo(() => dioramaMaterialForBiome(biome), [biome])
  const deckPatches = useMemo(() => buildDioramaDeckPatches(layout, biome, graphics), [layout, biome, graphics])
  const supportBlocks = useMemo(() => buildDioramaSupportBlocks(layout, biome, graphics), [layout, biome, graphics])
  const skirtPanels = useMemo(() => buildPlatformSkirtPanels(layout, biome, graphics), [layout, biome, graphics])
  return (
    <group>
      {supportBlocks.map((block) => (
        <DioramaSupportBlock key={block.key} block={block} biome={biome} material={material} />
      ))}
      {skirtPanels.map((panel) => (
        <PlatformSkirtPanel key={panel.key} panel={panel} biome={biome} theme={theme} material={material} reducedMotion={reducedMotion} />
      ))}
      <mesh geometry={baseGeom} position={[0, -0.39, 0]} receiveShadow castShadow>
        <meshStandardMaterial color={material.side} roughness={material.roughness} metalness={material.metalness} emissive={material.glow} emissiveIntensity={biome === 'lava' ? 0.08 : biome === 'magical' ? 0.06 : 0.022} />
      </mesh>
      {deckPatches.map((patch) => (
        <DioramaDeckPatch key={patch.key} patch={patch} biome={biome} material={material} />
      ))}
      <mesh position={[0, -0.62, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[glowWidth * 0.5, glowDepth * 0.5, 1]}>
        <circleGeometry args={[1, 56]} />
        <meshBasicMaterial color={material.glow} map={getSoftDiscTexture() ?? undefined} transparent opacity={biome === 'lava' ? 0.18 : biome === 'magical' ? 0.13 : 0.07} depthWrite={false} toneMapped={false} />
      </mesh>
    </group>
  )
}

export function buildBackdropPieces(layout: BoardLayout, biome: BiomeId, graphics: 'high' | 'medium' | 'low'): BackdropPieceInfo[] {
  if (graphics === 'low') return []
  const pitch = layout.tileSize + layout.gap
  const width = layout.cols * pitch + 2.6
  const depth = layout.rows * pitch + 2.2
  const count = graphics === 'high' ? 9 : 5
  const seed = biome.charCodeAt(0) * 1777 + layout.cols * 431 + layout.rows * 619
  const rnd = seeded(seed)
  return Array.from({ length: count }, (_, i) => {
    const t = i / (count - 1)
    const edgeBias = Math.abs(t - 0.5)
    const tone = rnd()
    return {
      key: `${biome}-backdrop-${i}`,
      x: -width / 2 + t * width + (rnd() - 0.5) * 0.28,
      y: -0.02 + rnd() * 0.04,
      z: -depth / 2 - 0.72 - edgeBias * 0.18 - rnd() * 0.36,
      rot: (rnd() - 0.5) * 0.48,
      scale: 0.78 + rnd() * 0.72,
      height: 0.62 + rnd() * 0.72 + (biome === 'forest' || biome === 'ice' || biome === 'dungeon' ? 0.2 : 0),
      tone,
      variant: (i + Math.floor(tone * 10)) % 7,
    }
  })
}

function landmarkSpecsForBiome(biome: BiomeId): { kind: BiomeLandmarkKind; side: RimSide; t: number; offset: number; scale: number; height: number; keepMedium: boolean }[] {
  if (biome === 'forest') {
    return [
      { kind: 'forest-canopy-grove', side: 'north', t: 0.48, offset: 1.38, scale: 1.48, height: 1.72, keepMedium: true },
      { kind: 'forest-hollow-log', side: 'west', t: 0.24, offset: 1.16, scale: 1.28, height: 1.14, keepMedium: true },
      { kind: 'forest-pine-bridge', side: 'north', t: 0.82, offset: 1.22, scale: 1.12, height: 1.16, keepMedium: false },
    ]
  }
  if (biome === 'cave') {
    return [
      { kind: 'cave-crystal-spires', side: 'north', t: 0.48, offset: 1.38, scale: 1.48, height: 1.74, keepMedium: true },
      { kind: 'cave-crystal-door', side: 'north', t: 0.76, offset: 1.18, scale: 1.18, height: 1.26, keepMedium: true },
      { kind: 'cave-glow-pool', side: 'west', t: 0.32, offset: 1.08, scale: 1.08, height: 1.08, keepMedium: false },
    ]
  }
  if (biome === 'lava') {
    return [
      { kind: 'lava-basalt-columns', side: 'north', t: 0.5, offset: 1.36, scale: 1.48, height: 1.74, keepMedium: true },
      { kind: 'lava-volcano-rift', side: 'north', t: 0.2, offset: 1.2, scale: 1.22, height: 1.3, keepMedium: true },
      { kind: 'lava-smoke-vent', side: 'east', t: 0.34, offset: 1.08, scale: 1.08, height: 1.14, keepMedium: false },
    ]
  }
  if (biome === 'ice') {
    return [
      { kind: 'ice-glacier-spires', side: 'north', t: 0.52, offset: 1.36, scale: 1.48, height: 1.72, keepMedium: true },
      { kind: 'ice-aurora-pines', side: 'north', t: 0.22, offset: 1.2, scale: 1.2, height: 1.32, keepMedium: true },
      { kind: 'ice-frozen-slabs', side: 'east', t: 0.32, offset: 1.08, scale: 1.08, height: 1.1, keepMedium: false },
    ]
  }
  if (biome === 'magical') {
    return [
      { kind: 'magical-floating-monoliths', side: 'north', t: 0.5, offset: 1.36, scale: 1.44, height: 1.72, keepMedium: true },
      { kind: 'magical-portal-arch', side: 'north', t: 0.24, offset: 1.18, scale: 1.2, height: 1.24, keepMedium: true },
      { kind: 'magical-rune-orbs', side: 'west', t: 0.36, offset: 1.08, scale: 1.02, height: 1.08, keepMedium: false },
    ]
  }
  return [
    { kind: 'dungeon-torch-wall', side: 'north', t: 0.5, offset: 1.36, scale: 1.44, height: 1.7, keepMedium: true },
    { kind: 'dungeon-gate-wall', side: 'north', t: 0.22, offset: 1.18, scale: 1.2, height: 1.28, keepMedium: true },
    { kind: 'dungeon-chain-torches', side: 'east', t: 0.38, offset: 1.08, scale: 1.06, height: 1.1, keepMedium: false },
  ]
}

export function buildBiomeLandmarks(layout: BoardLayout, biome: BiomeId, graphics: 'high' | 'medium' | 'low'): BiomeLandmarkInfo[] {
  if (graphics === 'low') return []
  const pitch = layout.tileSize + layout.gap
  const width = layout.cols * pitch + 2.34
  const depth = layout.rows * pitch + 2.34
  const rnd = seeded(hashText(`landmark:${biome}:${layout.cols}:${layout.rows}`))
  return landmarkSpecsForBiome(biome)
    .filter((spec) => graphics === 'high' || spec.keepMedium)
    .map((spec, i) => {
      const t = Math.max(0.08, Math.min(0.92, spec.t + (rnd() - 0.5) * 0.035))
      const point = sidePoint(spec.side, width, depth, t, spec.offset + rnd() * 0.18, rnd)
      const tone = rnd()
      return {
        key: `${biome}-landmark-${spec.kind}-${i}`,
        kind: spec.kind,
        side: spec.side,
        x: point.x,
        y: -0.02 + rnd() * 0.04,
        z: point.z,
        rot: point.rot,
        scale: spec.scale * (0.94 + rnd() * 0.12),
        height: spec.height * (0.94 + rnd() * 0.12),
        tone,
        variant: (i * 5 + Math.floor(tone * 19)) % 11,
      }
    })
}

export function buildPlatformEdgePieces(layout: BoardLayout, biome: BiomeId, graphics: 'high' | 'medium' | 'low'): PlatformEdgePieceInfo[] {
  if (graphics === 'low') return []
  const pitch = layout.tileSize + layout.gap
  const width = layout.cols * pitch + 1.78
  const depth = layout.rows * pitch + 1.78
  const seed = biome.charCodeAt(0) * 2909 + layout.cols * 719 + layout.rows * 487
  const rnd = seeded(seed)
  const high = graphics === 'high'
  const pieces: PlatformEdgePieceInfo[] = []
  const clusters: { side: RimSide; t: number; count: number; radius: number; scale: number; drop: number; length: number }[] = [
    { side: 'north', t: 0.16, count: high ? 3 : 2, radius: 0.48, scale: 1, drop: 1.08, length: 1.04 },
    { side: 'north', t: 0.5, count: high ? 3 : 2, radius: 0.54, scale: 1.04, drop: 1.12, length: 1.08 },
    { side: 'north', t: 0.84, count: high ? 3 : 1, radius: 0.48, scale: 1, drop: 1.08, length: 1.04 },
    { side: 'west', t: 0.22, count: high ? 2 : 1, radius: 0.38, scale: 0.9, drop: 0.95, length: 0.94 },
    { side: 'west', t: 0.76, count: high ? 2 : 1, radius: 0.4, scale: 0.9, drop: 0.95, length: 0.94 },
    { side: 'east', t: 0.2, count: high ? 2 : 1, radius: 0.38, scale: 0.9, drop: 0.95, length: 0.94 },
    { side: 'east', t: 0.74, count: high ? 2 : 1, radius: 0.4, scale: 0.9, drop: 0.95, length: 0.94 },
    { side: 'south', t: 0.26, count: high ? 1 : 0, radius: 0.16, scale: 0.48, drop: 0.48, length: 0.52 },
    { side: 'south', t: 0.78, count: 0, radius: 0.16, scale: 0.48, drop: 0.48, length: 0.52 },
  ]
  const push = (cluster: (typeof clusters)[number], clusterIndex: number, slot: number) => {
    const span = cluster.side === 'north' || cluster.side === 'south' ? width : depth
    const t = clusteredSideT(cluster.t, slot, Math.max(1, cluster.count), cluster.radius, span, rnd)
    const p = sidePoint(cluster.side, width, depth, t, 0.05 + rnd() * 0.16, rnd)
    const tone = rnd()
    pieces.push({
      key: `${biome}-edge-${cluster.side}-${clusterIndex}-${slot}`,
      side: cluster.side,
      shapeKind: platformEdgeShapeForBiome(biome),
      x: p.x,
      y: -0.43 - rnd() * 0.08,
      z: p.z,
      rot: p.rot,
      scale: (0.68 + rnd() * 0.34) * cluster.scale,
      length: (0.34 + rnd() * 0.3) * cluster.length,
      drop: (0.24 + rnd() * 0.46 + (biome === 'forest' || biome === 'ice' || biome === 'dungeon' ? 0.12 : 0)) * cluster.drop,
      tone,
      variant: (clusterIndex * 3 + slot + Math.floor(tone * 11)) % 8,
    })
  }

  clusters.forEach((cluster, clusterIndex) => {
    for (let slot = 0; slot < cluster.count; slot++) {
      push(cluster, clusterIndex, slot)
    }
  })

  return pieces
}

export function buildBaseSilhouettePieces(layout: BoardLayout, biome: BiomeId, graphics: 'high' | 'medium' | 'low'): BaseSilhouettePieceInfo[] {
  if (graphics === 'low') return []
  const pitch = layout.tileSize + layout.gap
  const width = layout.cols * pitch + 1.98
  const depth = layout.rows * pitch + 1.98
  const seed = hashText(`base:${biome}:${layout.cols}:${layout.rows}`)
  const rnd = seeded(seed)
  const high = graphics === 'high'
  const pieces: BaseSilhouettePieceInfo[] = []
  const clusters: { side: RimSide; t: number; count: number; radius: number; width: number; depth: number; height: number }[] = [
    { side: 'north', t: 0.15, count: high ? 3 : 2, radius: 0.58, width: 1.06, depth: 1.02, height: 1.08 },
    { side: 'north', t: 0.5, count: high ? 3 : 2, radius: 0.62, width: 1.12, depth: 1.08, height: 1.12 },
    { side: 'north', t: 0.85, count: high ? 3 : 1, radius: 0.58, width: 1.06, depth: 1.02, height: 1.08 },
    { side: 'west', t: 0.24, count: high ? 2 : 1, radius: 0.44, width: 0.92, depth: 0.94, height: 0.92 },
    { side: 'west', t: 0.74, count: high ? 2 : 1, radius: 0.44, width: 0.92, depth: 0.94, height: 0.92 },
    { side: 'east', t: 0.22, count: high ? 2 : 1, radius: 0.44, width: 0.92, depth: 0.94, height: 0.92 },
    { side: 'east', t: 0.72, count: high ? 2 : 1, radius: 0.44, width: 0.92, depth: 0.94, height: 0.92 },
    { side: 'south', t: 0.24, count: high ? 1 : 0, radius: 0.16, width: 0.56, depth: 0.48, height: 0.48 },
    { side: 'south', t: 0.82, count: 0, radius: 0.16, width: 0.56, depth: 0.48, height: 0.48 },
  ]
  const push = (cluster: (typeof clusters)[number], clusterIndex: number, slot: number) => {
    const span = cluster.side === 'north' || cluster.side === 'south' ? width : depth
    const t = clusteredSideT(cluster.t, slot, Math.max(1, cluster.count), cluster.radius, span, rnd)
    const p = sidePoint(cluster.side, width, depth, t, 0.02 + rnd() * 0.14, rnd)
    const tone = rnd()
    const heightBoost = biome === 'dungeon' || biome === 'cave' ? 0.08 : biome === 'ice' || biome === 'forest' ? 0.05 : 0
    const height = (0.22 + rnd() * 0.3 + heightBoost) * cluster.height
    const pieceWidth = (0.5 + rnd() * 0.42) * cluster.width
    const pieceDepth = (0.28 + rnd() * 0.24) * cluster.depth
    pieces.push({
      key: `${biome}-base-${cluster.side}-${clusterIndex}-${slot}`,
      side: cluster.side,
      shapeKind: baseSilhouetteShapeForBiome(biome),
      cornerRadius: chunkCornerRadius(biome, pieceWidth, height, pieceDepth),
      x: p.x,
      y: -0.43 - height * 0.12 - rnd() * 0.08 - (cluster.side === 'south' ? 0.05 : 0),
      z: p.z,
      rot: p.rot,
      width: pieceWidth,
      depth: pieceDepth,
      height,
      drop: height + 0.18 + rnd() * 0.24,
      tone,
      variant: (clusterIndex * 3 + slot + Math.floor(tone * 17)) % 10,
    })
  }

  clusters.forEach((cluster, clusterIndex) => {
    for (let slot = 0; slot < cluster.count; slot++) {
      push(cluster, clusterIndex, slot)
    }
  })

  const ordered = pieces.sort((a, b) => {
    const frontA = a.side === 'south' ? 0 : a.side === 'east' || a.side === 'west' ? 1 : 2
    const frontB = b.side === 'south' ? 0 : b.side === 'east' || b.side === 'west' ? 1 : 2
    return frontA - frontB || a.key.localeCompare(b.key)
  })
  return ordered
}

export function buildPlatformContourPieces(grid: string[], layout: BoardLayout, biome: BiomeId, graphics: 'high' | 'medium' | 'low'): PlatformContourPieceInfo[] {
  if (graphics === 'low') return []
  const seed = hashText(`${biome}:${layout.cols}:${layout.rows}:${grid.join('/')}`)
  const pitch = layout.tileSize + layout.gap
  const offset = layout.tileSize / 2 + layout.gap * 0.18
  const pieces: PlatformContourPieceInfo[] = []
  const dirs: PlatformContourPieceInfo['side'][] = ['north', 'east', 'south', 'west']

  for (let cellY = 0; cellY < grid.length; cellY++) {
    for (let cellX = 0; cellX < grid[cellY].length; cellX++) {
      if (!isPlatformCell(grid, cellX, cellY)) continue
      for (const side of dirs) {
        const nx = cellX + (side === 'east' ? 1 : side === 'west' ? -1 : 0)
        const ny = cellY + (side === 'south' ? 1 : side === 'north' ? -1 : 0)
        if (isPlatformCell(grid, nx, ny)) continue
        const rnd = seeded(seed ^ Math.imul(cellX + 17, 73856093) ^ Math.imul(cellY + 31, 19349663) ^ hashText(side))
        const [wx, , wz] = layout.toWorld(cellX, cellY)
        const along = (rnd() - 0.5) * layout.tileSize * 0.12
        const outward = offset + rnd() * layout.gap * 0.55
        const horizontal = side === 'north' || side === 'south'
        const tone = rnd()
        pieces.push({
          key: `${biome}-contour-${cellX}-${cellY}-${side}`,
          side,
          cellX,
          cellY,
          x: wx + (side === 'east' ? outward : side === 'west' ? -outward : along),
          y: -0.21 - rnd() * 0.04,
          z: wz + (side === 'south' ? outward : side === 'north' ? -outward : along),
          rot: horizontal ? 0 : Math.PI / 2,
          width: layout.tileSize * (0.93 + rnd() * 0.25),
          depth: pitch * (0.35 + rnd() * 0.12),
          height: 0.36 + rnd() * 0.22 + (biome === 'dungeon' || biome === 'cave' ? 0.05 : biome === 'ice' ? 0.03 : 0),
          tone,
          variant: (cellX * 3 + cellY * 5 + Math.floor(tone * 13)) % 9,
        })
      }
    }
  }

  const sideLimits: Record<RimSide, number> =
    graphics === 'high'
      ? { north: 22, east: 10, south: 1, west: 10 }
      : { north: 10, east: 5, south: 0, west: 5 }
  const selected = dirs.flatMap((side) => {
    const candidates = pieces
      .filter((piece) => piece.side === side)
      .sort((a, b) => (side === 'north' || side === 'south' ? a.cellX - b.cellX || a.cellY - b.cellY : a.cellY - b.cellY || a.cellX - b.cellX))
    const limit = sideLimits[side]
    if (candidates.length <= limit) return candidates
    const stride = candidates.length / limit
    return Array.from({ length: limit }, (_, i) => candidates[Math.min(candidates.length - 1, Math.floor((i + 0.5) * stride))])
  })

  const ordered = selected.sort((a, b) => {
    const frontA = a.side === 'south' ? 0 : a.side === 'east' || a.side === 'west' ? 1 : 2
    const frontB = b.side === 'south' ? 0 : b.side === 'east' || b.side === 'west' ? 1 : 2
    return frontA - frontB || b.cellY - a.cellY || a.cellX - b.cellX
  })
  return ordered
}

export function buildStageGround(layout: BoardLayout, biome: BiomeId, graphics: 'high' | 'medium' | 'low'): StageGroundInfo {
  const warmGlow = biome === 'lava' || biome === 'magical'
  const coldGlow = biome === 'ice' || biome === 'cave'
  const high = graphics === 'high'
  const medium = graphics === 'medium'
  return {
    radius: layout.extent * (high ? 0.94 : medium ? 0.84 : 0.68),
    shadowRadius: layout.extent * (high ? 0.66 : medium ? 0.56 : 0.44),
    washRadius: layout.extent * (high ? (warmGlow ? 0.6 : coldGlow ? 0.55 : 0.5) : medium ? (warmGlow ? 0.46 : coldGlow ? 0.42 : 0.38) : 0.28),
    segments: high ? 64 : medium ? 40 : 24,
    baseOpacity: high ? 0.145 : medium ? 0.095 : 0.035,
    shadowOpacity: high ? 0.055 : medium ? 0.036 : 0.018,
    washOpacity: high ? (warmGlow ? 0.17 : coldGlow ? 0.115 : 0.086) : medium ? (warmGlow ? 0.102 : coldGlow ? 0.066 : 0.048) : 0.018,
    shadowSquash: high ? 0.42 : medium ? 0.38 : 0.34,
    washSquash: high ? 0.48 : medium ? 0.42 : 0.36,
  }
}

export function buildBiomeArtBackdrop(layout: BoardLayout, biome: BiomeId, graphics: 'high' | 'medium' | 'low'): BiomeArtBackdropInfo | null {
  if (graphics === 'low') return null
  const src = biomeArt(biome)
  if (!src) return null
  const high = graphics === 'high'
  const luminous = biome === 'lava' || biome === 'magical'
  const cold = biome === 'cave' || biome === 'ice'
  return {
    src,
    width: layout.extent * (high ? 2.64 : 2.32),
    height: layout.extent * (high ? 1.2 : 1.02),
    y: layout.extent * (high ? 0.42 : 0.37),
    z: -layout.extent * (high ? 1.38 : 1.3),
    opacity: high ? (luminous ? 0.16 : cold ? 0.14 : 0.125) : luminous ? 0.085 : cold ? 0.075 : 0.065,
    softMaskOpacity: high ? 0.92 : 0.72,
    washOpacity: high ? (luminous ? 0.145 : cold ? 0.105 : 0.09) : luminous ? 0.075 : cold ? 0.056 : 0.046,
    vignetteOpacity: high ? 0.4 : 0.28,
    glowScale: high ? (luminous ? 0.92 : 0.78) : luminous ? 0.7 : 0.58,
  }
}

export function buildStageFloorAccents(layout: BoardLayout, biome: BiomeId, graphics: 'high' | 'medium' | 'low'): StageFloorAccentInfo[] {
  if (graphics === 'low') return []
  const pitch = layout.tileSize + layout.gap
  const halfWidth = (layout.cols * pitch) / 2
  const halfDepth = (layout.rows * pitch) / 2
  const high = graphics === 'high'
  const rnd = seeded(hashText(`stage-floor:${biome}:${layout.cols}:${layout.rows}`))
  const kind = stageFloorAccentKindForBiome(biome)
  const counts: Record<StageFloorAccentZone, number> = high ? { rear: 7, side: 5, front: 2 } : { rear: 4, side: 3, front: 0 }
  const pieces: StageFloorAccentInfo[] = []
  const rearMaxZ = Math.max(halfDepth + 0.74, layout.extent * (high ? 0.56 : 0.5))
  const sideMaxX = Math.max(halfWidth + 0.72, layout.extent * (high ? 0.78 : 0.68))
  const frontMaxZ = Math.max(halfDepth + 0.7, layout.extent * 0.54)
  const baseWidth = biome === 'forest' ? 0.36 : biome === 'lava' ? 0.52 : biome === 'magical' ? 0.46 : biome === 'ice' ? 0.4 : 0.34
  const baseDepth = biome === 'lava' ? 0.08 : biome === 'magical' ? 0.13 : biome === 'forest' ? 0.18 : 0.14
  const baseOpacity = biome === 'lava' ? 0.76 : biome === 'magical' ? 0.68 : biome === 'cave' ? 0.58 : biome === 'ice' ? 0.52 : 0.46
  const push = (zone: StageFloorAccentZone, index: number, count: number) => {
    const t = count <= 1 ? 0.5 : (index + 0.5) / count
    const tone = rnd()
    let x = 0
    let z = 0
    if (zone === 'rear') {
      x = (t - 0.5) * layout.extent * 1.26 + (rnd() - 0.5) * 0.34
      z = -Math.min(rearMaxZ, halfDepth + 0.55 + rnd() * 0.52 + Math.abs(t - 0.5) * 0.28)
    } else if (zone === 'side') {
      const side = index % 2 === 0 ? -1 : 1
      const sideSlot = Math.floor(index / 2)
      const sideCount = Math.max(1, Math.ceil(count / 2))
      const sideT = sideCount <= 1 ? 0.5 : sideSlot / (sideCount - 1)
      x = side * Math.min(sideMaxX, halfWidth + 0.58 + rnd() * Math.max(0.38, layout.extent * 0.12))
      z = (sideT - 0.5) * halfDepth * 1.35 + (rnd() - 0.5) * 0.38
    } else {
      x = (t - 0.5) * Math.min(halfWidth * 1.2, layout.extent * 0.5) + (rnd() - 0.5) * 0.16
      z = Math.min(frontMaxZ, halfDepth + 0.62 + rnd() * 0.28)
    }
    const scale = 0.68 + rnd() * 0.42 + (zone === 'rear' ? 0.08 : 0)
    pieces.push({
      key: `${biome}-stage-floor-${zone}-${index}`,
      kind,
      zone,
      x,
      z,
      rot: rnd() * Math.PI * 2,
      scale,
      width: baseWidth * (0.82 + rnd() * 0.36),
      depth: baseDepth * (0.84 + rnd() * 0.34),
      opacity: baseOpacity * (high ? 1 : 0.68) * (0.86 + rnd() * 0.18),
      tone,
      variant: (index * 7 + Math.floor(tone * 17)) % 11,
    })
  }
  const zones: StageFloorAccentZone[] = ['rear', 'side', 'front']
  zones.forEach((zone) => {
    for (let index = 0; index < counts[zone]; index++) push(zone, index, counts[zone])
  })
  return pieces
}

export function buildBiomeSetDressingClusters(layout: BoardLayout, biome: BiomeId, graphics: 'high' | 'medium' | 'low'): BiomeSetDressingClusterInfo[] {
  if (graphics === 'low') return []
  const pitch = layout.tileSize + layout.gap
  const halfWidth = (layout.cols * pitch) / 2
  const halfDepth = (layout.rows * pitch) / 2
  const high = graphics === 'high'
  const rnd = seeded(hashText(`set-dressing:${biome}:${layout.cols}:${layout.rows}`))
  const kind = setDressingKindForBiome(biome)
  const counts: Record<BiomeSetDressingZone, number> = high ? { rear: 5, side: 4, front: 1 } : { rear: 3, side: 2, front: 0 }
  const baseWidth = biome === 'forest' ? 0.82 : biome === 'lava' ? 0.72 : biome === 'dungeon' ? 0.78 : biome === 'ice' ? 0.76 : 0.68
  const baseDepth = biome === 'forest' ? 0.58 : biome === 'lava' ? 0.42 : biome === 'magical' ? 0.52 : 0.48
  const baseHeight = biome === 'forest' ? 0.92 : biome === 'cave' ? 0.98 : biome === 'ice' ? 0.9 : biome === 'dungeon' ? 0.74 : 0.68
  const maxAbsZ = layout.extent * 0.62
  const pieces: BiomeSetDressingClusterInfo[] = []
  const push = (zone: BiomeSetDressingZone, index: number, count: number) => {
    const t = count <= 1 ? 0.5 : (index + 0.5) / count
    const sideSign = index % 2 === 0 ? -1 : 1
    let x = 0
    let z = 0
    if (zone === 'rear') {
      x = (t - 0.5) * layout.extent * 1.2 + (rnd() - 0.5) * 0.38
      z = -Math.min(maxAbsZ, halfDepth + 0.84 + rnd() * 0.62 + Math.abs(t - 0.5) * 0.38)
    } else if (zone === 'side') {
      const sideSlot = Math.floor(index / 2)
      const sideCount = Math.max(1, Math.ceil(count / 2))
      const sideT = sideCount <= 1 ? 0.5 : sideSlot / (sideCount - 1)
      x = sideSign * (halfWidth + 0.72 + rnd() * Math.max(0.34, layout.extent * 0.12))
      z = (sideT - 0.5) * halfDepth * 1.12 + (rnd() - 0.5) * 0.42
    } else {
      x = (t - 0.5) * Math.min(halfWidth * 0.92, layout.extent * 0.42) + (rnd() - 0.5) * 0.18
      z = Math.min(maxAbsZ, halfDepth + 0.86 + rnd() * 0.22)
    }
    const tone = rnd()
    const zoneScale = zone === 'rear' ? 1.08 : zone === 'front' ? 0.72 : 0.92
    pieces.push({
      key: `${biome}-set-dressing-${zone}-${index}`,
      kind,
      zone,
      x,
      z,
      rot: rnd() * Math.PI * 2,
      scale: (0.76 + rnd() * 0.3) * zoneScale,
      width: baseWidth * (0.84 + rnd() * 0.34),
      depth: baseDepth * (0.82 + rnd() * 0.36),
      height: Math.max(0.54, baseHeight * (0.86 + rnd() * 0.42) * zoneScale),
      tone,
      variant: (index * 5 + Math.floor(tone * 19)) % 13,
    })
  }
  for (const zone of ['rear', 'side', 'front'] as const) {
    for (let index = 0; index < counts[zone]; index++) push(zone, index, counts[zone])
  }
  return pieces
}

export function terrainCompositionZone(piece: Pick<WorldcraftTerrainPiece, 'z'>, layout: Pick<BoardLayout, 'extent'>): TerrainCompositionZone {
  if (piece.z > layout.extent * 0.16) return 'front'
  if (piece.z < -layout.extent * 0.12) return 'rear'
  return 'side'
}

export function selectWorldcraftTerrainPieces(
  terrain: WorldcraftTerrainPiece[],
  layout: Pick<BoardLayout, 'extent'>,
  graphics: 'high' | 'medium' | 'low',
): WorldcraftTerrainPiece[] {
  if (graphics === 'low') return []
  const candidates = terrain.filter((p) => p.cornerCase !== 'full' || p.variant === 3)
  const step = graphics === 'high' ? 1 : 2
  const sampled = candidates.filter((_, i) => i % step === 0)
  const limits = graphics === 'high'
    ? { rear: 20, side: 9, front: 1 }
    : { rear: 10, side: 5, front: 0 }
  const buckets: Record<TerrainCompositionZone, WorldcraftTerrainPiece[]> = { rear: [], side: [], front: [] }
  for (const piece of sampled) {
    const zone = terrainCompositionZone(piece, layout)
    if (buckets[zone].length < limits[zone]) buckets[zone].push(piece)
  }
  return [...buckets.front, ...buckets.side, ...buckets.rear]
}

function BackdropWash({ layout, theme, biome, graphics }: { layout: BoardLayout; theme: BiomeTheme; biome: BiomeId; graphics: 'high' | 'medium' | 'low' }) {
  if (graphics === 'low') return null
  const width = layout.extent * 2.45
  const height = layout.extent * 1.05
  const opacity = biome === 'magical' ? 0.1 : biome === 'lava' ? 0.09 : biome === 'ice' ? 0.08 : 0.07
  return (
    <group>
      <mesh position={[0, height * 0.32, -layout.extent * 1.22]} scale={[width * 0.5, height * 0.5, 1]} renderOrder={-20}>
        <circleGeometry args={[1, 56]} />
        <meshBasicMaterial color={theme.biome} map={getSoftDiscTexture() ?? undefined} transparent opacity={opacity * 0.85} depthWrite={false} toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, -0.34, -layout.extent * 1.02]} rotation={[-Math.PI / 2, 0, 0]} scale={[layout.extent * 0.92, layout.extent * 0.22, 1]} renderOrder={-19}>
        <circleGeometry args={[1, 48]} />
        <meshBasicMaterial color={theme.surfaceSunken} map={getSoftDiscTexture() ?? undefined} transparent opacity={0.09} depthWrite={false} />
      </mesh>
    </group>
  )
}

function BiomeArtBackdrop({ backdrop, theme, graphics }: { backdrop: BiomeArtBackdropInfo; theme: BiomeTheme; graphics: 'high' | 'medium' }) {
  const texture = useLoader(THREE.TextureLoader, backdrop.src)
  const softMask = getSoftAlphaMaskTexture()
  const map = useMemo(() => {
    texture.colorSpace = THREE.SRGBColorSpace
    texture.anisotropy = graphics === 'high' ? 4 : 1
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.needsUpdate = true
    return texture
  }, [graphics, texture])

  return (
    <group>
      <mesh position={[0, backdrop.y, backdrop.z - 0.04]} scale={[backdrop.width, backdrop.height, 1]} renderOrder={-32}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial map={map} alphaMap={softMask ?? undefined} transparent opacity={backdrop.opacity * backdrop.softMaskOpacity} depthWrite={false} toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, backdrop.y * 0.92, backdrop.z + 0.01]} scale={[backdrop.width * backdrop.glowScale, backdrop.height * 0.62, 1]} renderOrder={-31}>
        <circleGeometry args={[1, 64]} />
        <meshBasicMaterial color={theme.biome} map={getSoftDiscTexture() ?? undefined} transparent opacity={backdrop.washOpacity} depthWrite={false} toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, backdrop.y, backdrop.z + 0.03]} scale={[backdrop.width * 0.68, backdrop.height * 0.58, 1]} renderOrder={-30}>
        <circleGeometry args={[1, 64]} />
        <meshBasicMaterial color={theme.ink} map={getSoftDiscTexture() ?? undefined} transparent opacity={backdrop.vignetteOpacity} depthWrite={false} toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
    </group>
  )
}

function BackdropPiece({ piece, theme, biome, reducedMotion }: { piece: BackdropPieceInfo; theme: BiomeTheme; biome: BiomeId; reducedMotion: boolean }) {
  const shadow = new THREE.Color(theme.ink).lerp(new THREE.Color(theme.surfaceRaised), 0.38 + piece.tone * 0.22).getStyle()
  const glow = new THREE.Color(theme.biome).lerp(new THREE.Color(theme.biomeSoft), 0.28 + piece.tone * 0.38).getStyle()
  if (biome === 'forest') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
        <mesh position={[0, piece.height * 0.34, 0]} castShadow>
          <cylinderGeometry args={[0.09, 0.16, piece.height * 0.72, 7]} />
          <meshStandardMaterial color="#5b3925" roughness={0.9} />
        </mesh>
        {[0, 1, 2].map((i) => (
          <mesh key={`root-${i}`} position={[(i - 1) * 0.16, piece.height * 0.08, 0.1 + Math.sin(i + piece.tone) * 0.08]} rotation={[Math.PI / 2, 0, (i - 1) * 0.42]} castShadow>
            <capsuleGeometry args={[0.032 + i * 0.004, 0.38 + i * 0.06, 5, 9]} />
            <meshStandardMaterial color={i === 1 ? '#70442b' : '#47301f'} roughness={0.97} />
          </mesh>
        ))}
        {Array.from({ length: 8 }, (_, i) => {
          const a = (i / 7) * Math.PI * 2 + piece.tone
          const crownX = i === 0 ? 0 : Math.cos(a) * (0.2 + (i % 2) * 0.06)
          const crownZ = i === 0 ? 0 : Math.sin(a) * 0.12
          return (
            <mesh key={`crown-${i}`} position={[crownX, piece.height * (0.66 + (i % 3) * 0.045), crownZ]} rotation={[0.12, a, (i - 3) * 0.035]} scale={[1.28 + (i % 2) * 0.18, 0.84, 1.02 + (i % 3) * 0.1]} castShadow>
              <dodecahedronGeometry args={[0.2 + (i === 0 ? 0.06 : 0), 0]} />
              <meshStandardMaterial color={i % 3 === 0 ? '#5f8f3f' : i % 2 ? '#9dc76a' : glow} roughness={0.96} emissive={theme.biome} emissiveIntensity={0.035} flatShading />
            </mesh>
          )
        })}
        {[0, 1].map((i) => (
          <mesh key={`moss-band-${i}`} position={[(i - 0.5) * 0.12, piece.height * (0.24 + i * 0.2), 0.095]} rotation={[0.08, 0, (i - 0.5) * 0.24]} scale={[1.1, 0.32, 0.62]} receiveShadow>
            <dodecahedronGeometry args={[0.095, 0]} />
            <meshStandardMaterial color={i ? '#7ea85a' : glow} roughness={0.98} emissive={theme.biome} emissiveIntensity={0.025} flatShading />
          </mesh>
        ))}
      </group>
    )
  }
  if (biome === 'cave') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
        <mesh position={[0, piece.height * 0.18, -0.02]} scale={[1.16, 0.42, 0.82]} castShadow receiveShadow>
          <dodecahedronGeometry args={[0.26 + piece.tone * 0.08, 0]} />
          <meshStandardMaterial color={shadow} roughness={0.9} flatShading />
        </mesh>
        {Array.from({ length: piece.variant % 2 === 0 ? 4 : 3 }, (_, i) => (
          <group key={i} position={[(i - 1.5) * 0.12, piece.height * (0.28 + i * 0.065), Math.sin(i + piece.tone) * 0.08]} rotation={[0.12, i * 0.44, (i - 1.5) * 0.12]}>
            <mesh scale={[0.74 + (i % 2) * 0.18, piece.height * (2.1 + i * 0.24), 0.74]} castShadow>
              <octahedronGeometry args={[0.08 + (i % 3) * 0.018, 0]} />
              <meshStandardMaterial color={i % 2 ? glow : theme.biomeSoft} emissive={theme.biomeSoft} emissiveIntensity={0.38 + i * 0.05} roughness={0.3} transparent opacity={0.9} flatShading />
            </mesh>
          </group>
        ))}
        {piece.variant % 2 === 0 && <PulseLight color={theme.biomeSoft} distance={1.8} intensity={0.2} reducedMotion={reducedMotion} phase={piece.tone * 7} />}
      </group>
    )
  }
  if (biome === 'lava') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
        {Array.from({ length: 4 }, (_, i) => (
          <mesh key={`course-${i}`} position={[(i - 1.5) * 0.035, piece.height * (0.14 + i * 0.15), Math.sin(i + piece.tone) * 0.035]} rotation={[0, piece.tone * 0.8 + i * 0.18, (i - 1.5) * 0.025]} scale={[1 - i * 0.08, 1, 0.92 + (i % 2) * 0.12]} castShadow receiveShadow>
            <cylinderGeometry args={[0.22 - i * 0.018, 0.29 - i * 0.018, piece.height * 0.24, 7]} />
            <meshStandardMaterial color={i === 0 ? '#1b0c08' : '#2b1712'} roughness={0.93} emissive="#ff3d16" emissiveIntensity={0.08 + i * 0.025} flatShading />
          </mesh>
        ))}
        {[0, 1, 2].map((i) => (
          <mesh key={`rift-${i}`} position={[(i - 1) * 0.085, piece.height * (0.34 + i * 0.12), 0.18]} rotation={[0, 0, (i - 1) * 0.16]}>
            <boxGeometry args={[0.24 - i * 0.035, 0.028, 0.035]} />
            <meshBasicMaterial color={i === 1 ? '#ffd45c' : '#ff6a45'} transparent opacity={0.86} toneMapped={false} />
          </mesh>
        ))}
        {piece.variant % 3 === 0 && (
          <group position={[0, piece.height * 0.78, 0]}>
            <FlickerFlame color="#ff7a3c" hot="#ffd45c" reducedMotion={reducedMotion} distance={1.55} intensity={0.42} scale={0.56} />
          </group>
        )}
      </group>
    )
  }
  if (biome === 'ice') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
        <mesh position={[0, 0.08, 0]} scale={[1.7, 0.35, 1]} receiveShadow>
          <dodecahedronGeometry args={[0.2, 0]} />
          <meshStandardMaterial color="#f4fbff" roughness={0.48} />
        </mesh>
        {Array.from({ length: 4 }, (_, i) => (
          <mesh key={i} position={[(i - 1.5) * 0.11, piece.height * (0.26 + i * 0.07), Math.sin(i + piece.tone) * 0.09]} rotation={[0.12, piece.tone + i * 0.42, (i - 1.5) * 0.09]} scale={[0.72 + (i % 2) * 0.18, piece.height * (2.1 + i * 0.26), 0.72]} castShadow>
            <octahedronGeometry args={[0.085 + (i % 2) * 0.022, 0]} />
            <meshStandardMaterial color={i % 2 ? '#f1fdff' : theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.16 + i * 0.035} roughness={0.28} transparent opacity={0.88} flatShading />
          </mesh>
        ))}
        {[0, 1].map((i) => (
          <mesh key={`snow-${i}`} position={[(i - 0.5) * 0.12, piece.height * (0.48 + i * 0.11), 0.08]} scale={[1.1, 0.28, 0.74]} receiveShadow>
            <sphereGeometry args={[0.07, 8, 5]} />
            <meshStandardMaterial color="#ffffff" roughness={0.56} />
          </mesh>
        ))}
      </group>
    )
  }
  if (biome === 'magical') {
    return (
      <group position={[piece.x, piece.y + 0.08 + piece.tone * 0.06, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
        {[-0.18, 0.04, 0.22].map((x, i) => (
          <mesh key={i} position={[x, piece.height * (0.3 + i * 0.08), Math.sin(i + piece.tone) * 0.08]} rotation={[0.36 + i * 0.12, piece.tone * Math.PI + i * 0.5, 0.2]} castShadow>
            <octahedronGeometry args={[0.12 + i * 0.04 + piece.tone * 0.03, 0]} />
            <meshStandardMaterial color={i % 2 ? '#7de3ff' : glow} emissive={theme.biome} emissiveIntensity={0.48 + i * 0.08} roughness={0.24} transparent opacity={0.88} flatShading />
          </mesh>
        ))}
        <mesh position={[0, piece.height * 0.34, 0]} rotation={[Math.PI / 2, 0, piece.tone * Math.PI]}>
          <torusGeometry args={[0.34, 0.011, 6, 32, Math.PI * 1.55]} />
          <meshBasicMaterial color={theme.biomeSoft} transparent opacity={0.5} toneMapped={false} />
        </mesh>
        {[0, 1, 2, 3].map((i) => {
          const a = i * Math.PI * 0.5 + piece.tone
          return (
            <mesh key={`petal-${i}`} position={[Math.cos(a) * 0.26, piece.height * 0.12, Math.sin(a) * 0.18]} rotation={[0.25, -a, a * 0.2]} scale={[0.78, 1, 0.6]}>
              <boxGeometry args={[0.11, 0.025, 0.05]} />
              <meshStandardMaterial color={i % 2 ? '#7de3ff' : theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.32} roughness={0.34} />
            </mesh>
          )
        })}
        {piece.variant % 3 === 0 && <PulseLight color={theme.biomeSoft} distance={1.7} intensity={0.24} reducedMotion={reducedMotion} phase={piece.tone * 8} />}
      </group>
    )
  }
  return (
    <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
      <mesh position={[0, piece.height * 0.33, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.38, piece.height * 0.76, 0.34]} />
        <meshStandardMaterial color={shadow} roughness={0.88} metalness={0.04} emissive={theme.biome} emissiveIntensity={0.04} />
      </mesh>
      {Array.from({ length: 4 }, (_, i) => (
        <mesh key={`course-${i}`} position={[(i - 1.5) * 0.11, piece.height * (0.16 + i * 0.14), 0.19]} castShadow receiveShadow>
          <boxGeometry args={[0.16 + (i % 2) * 0.04, 0.07, 0.065]} />
          <meshStandardMaterial color={i % 2 ? '#725537' : shadow} roughness={0.92} metalness={0.03} emissive={theme.biome} emissiveIntensity={0.035} />
        </mesh>
      ))}
      {piece.variant % 3 === 0 && (
        <group position={[0, piece.height * 0.74, 0.04]}>
          <FlickerFlame color="#ff9a45" reducedMotion={reducedMotion} distance={1.45} intensity={0.36} scale={0.5} />
        </group>
      )}
      {piece.variant % 4 === 0 && (
        <mesh position={[0, piece.height * 0.42, 0.2]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.14, 0.018, 6, 14]} />
          <meshStandardMaterial color="#d0a24c" metalness={0.32} roughness={0.46} />
        </mesh>
      )}
    </group>
  )
}

function BiomeBackdrop({ layout, theme, biome, graphics, reducedMotion }: { layout: BoardLayout; theme: BiomeTheme; biome: BiomeId; graphics: 'high' | 'medium' | 'low'; reducedMotion: boolean }) {
  const pieces = useMemo(() => buildBackdropPieces(layout, biome, graphics), [layout, biome, graphics])
  const artBackdrop = useMemo(() => buildBiomeArtBackdrop(layout, biome, graphics), [layout, biome, graphics])
  if (graphics === 'low') return null
  return (
    <group>
      {artBackdrop && <BiomeArtBackdrop backdrop={artBackdrop} theme={theme} graphics={graphics} />}
      <BackdropWash layout={layout} theme={theme} biome={biome} graphics={graphics} />
      {pieces.map((piece) => (
        <BackdropPiece key={piece.key} piece={piece} theme={theme} biome={biome} reducedMotion={reducedMotion} />
      ))}
    </group>
  )
}

function LandmarkPine({ x, z, scale, color, snow = false }: { x: number; z: number; scale: number; color: string; snow?: boolean }) {
  const crown = snow ? ['#4a927d', '#7fcfd5', '#f4fbff', '#ffffff'] : ['#5f8f3f', color, '#93bd5f', '#b7d475']
  return (
    <group position={[x, 0, z]} scale={scale}>
      <mesh position={[0, 0.22, 0]} castShadow>
        <cylinderGeometry args={[0.05, 0.08, 0.44, 6]} />
        <meshStandardMaterial color={snow ? '#65594c' : '#6d472d'} roughness={0.86} />
      </mesh>
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={i} position={[Math.sin(i * 1.7) * 0.025, 0.38 + i * 0.105, Math.cos(i * 1.3) * 0.018]} rotation={[0, i * 0.26, (i - 2) * 0.035]} castShadow>
          <coneGeometry args={[0.31 - i * 0.043, 0.26, 8]} />
          <meshStandardMaterial color={crown[Math.min(i, crown.length - 1)]} roughness={snow ? 0.62 : 0.88} emissive={snow ? '#5fd6ff' : '#5fd38b'} emissiveIntensity={snow ? 0.04 : 0.015} />
        </mesh>
      ))}
      {[0, 1, 2].map((i) => (
        <mesh key={`branch-${i}`} position={[(i - 1) * 0.1, 0.43 + i * 0.1, 0.04]} rotation={[Math.PI / 2, 0, (i - 1) * 0.42]} castShadow>
          <capsuleGeometry args={[0.018, 0.22 - i * 0.03, 4, 7]} />
          <meshStandardMaterial color={snow ? '#f4fbff' : '#436b34'} roughness={snow ? 0.58 : 0.9} />
        </mesh>
      ))}
      {snow && (
        <mesh position={[0.02, 0.92, 0]} scale={[1.22, 0.32, 1]}>
          <dodecahedronGeometry args={[0.08, 0]} />
          <meshStandardMaterial color="#ffffff" roughness={0.58} />
        </mesh>
      )}
    </group>
  )
}

function LandmarkCrystal({ x, z, scale, color, glow, height = 0.72 }: { x: number; z: number; scale: number; color: string; glow: string; height?: number }) {
  return (
    <group position={[x, 0, z]} scale={scale}>
      <mesh position={[0, 0.065, 0]} scale={[1.24, 0.34, 0.86]} receiveShadow>
        <dodecahedronGeometry args={[0.16, 0]} />
        <meshStandardMaterial color={new THREE.Color(color).lerp(new THREE.Color('#131224'), 0.42).getStyle()} emissive={glow} emissiveIntensity={0.08} roughness={0.82} flatShading />
      </mesh>
      <mesh position={[0, height * 0.44, 0]} rotation={[0.12, x * 1.3, -z * 0.28]} scale={[0.86, height * 2.9, 0.86]} castShadow>
        <octahedronGeometry args={[0.16, 0]} />
        <meshStandardMaterial color={color} emissive={glow} emissiveIntensity={0.5} roughness={0.24} transparent opacity={0.92} flatShading />
      </mesh>
      <mesh position={[0.16, height * 0.25, 0.08]} rotation={[0.28, x * 0.8 + 0.5, 0.18]} scale={[0.54, height * 1.55, 0.54]} castShadow>
        <octahedronGeometry args={[0.11, 0]} />
        <meshStandardMaterial color={new THREE.Color(color).lerp(new THREE.Color('#ffffff'), 0.16).getStyle()} emissive={glow} emissiveIntensity={0.34} roughness={0.28} transparent opacity={0.86} flatShading />
      </mesh>
      <mesh position={[-0.14, height * 0.2, -0.04]} rotation={[-0.18, z * 1.1, -0.22]} scale={[0.42, height * 1.25, 0.42]} castShadow>
        <octahedronGeometry args={[0.1, 0]} />
        <meshStandardMaterial color={glow} emissive={glow} emissiveIntensity={0.36} roughness={0.3} transparent opacity={0.78} flatShading />
      </mesh>
      <mesh position={[0.02, height * 0.12, 0.18]} rotation={[0.32, x + z, 0.16]} scale={[0.34, height * 0.86, 0.34]} castShadow>
        <octahedronGeometry args={[0.08, 0]} />
        <meshStandardMaterial color={new THREE.Color(glow).lerp(new THREE.Color('#ffffff'), 0.2).getStyle()} emissive={glow} emissiveIntensity={0.3} roughness={0.34} transparent opacity={0.72} flatShading />
      </mesh>
    </group>
  )
}

function BiomeLandmark({ piece, theme, graphics, reducedMotion }: { piece: BiomeLandmarkInfo; theme: BiomeTheme; graphics: 'high' | 'medium' | 'low'; reducedMotion: boolean }) {
  const high = graphics === 'high'
  const soft = useMemo(() => new THREE.Color(theme.biome).lerp(new THREE.Color(theme.biomeSoft), 0.38 + piece.tone * 0.32).getStyle(), [piece.tone, theme.biome, theme.biomeSoft])
  const stone = useMemo(() => new THREE.Color(theme.surfaceRaised).lerp(new THREE.Color(theme.edge), 0.2 + piece.tone * 0.28).getStyle(), [piece.tone, theme.edge, theme.surfaceRaised])
  const landmarkScale: [number, number, number] = [piece.scale, piece.scale * piece.height, piece.scale]
  const materialProfile = landmarkMaterialProfileForKind(piece.kind)

  if (piece.kind === 'forest-hollow-log') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        <mesh position={[0, 0.27, 0]} rotation={[0, 0, Math.PI / 2]} castShadow receiveShadow>
          <cylinderGeometry args={[0.25, 0.32, 1.05, 14]} />
          <meshStandardMaterial color="#6d472d" roughness={0.96} />
        </mesh>
        {[-1, 1].map((s) => (
          <mesh key={s} position={[s * 0.54, 0.27, 0]} rotation={[0, Math.PI / 2, 0]}>
            <circleGeometry args={[0.24, 18]} />
            <meshStandardMaterial color="#241711" emissive="#0b0604" emissiveIntensity={0.08} roughness={0.98} />
          </mesh>
        ))}
        {Array.from({ length: materialProfile.organicLobes }, (_, i) => (
          <mesh key={`moss-${i}`} position={[-0.45 + i * 0.22, 0.5 + (i % 2) * 0.035, -0.05 + Math.sin(i + piece.tone) * 0.09]} scale={[1.26, 0.38, 0.82]} receiveShadow>
            <dodecahedronGeometry args={[0.13 + (i % 3) * 0.018, 0]} />
            <meshStandardMaterial color={i % 2 ? '#6f9a4a' : soft} emissive={theme.biome} emissiveIntensity={0.025} roughness={0.96} flatShading />
          </mesh>
        ))}
        {[-0.54, -0.25, 0.24, 0.5].map((x, i) => (
          <mesh key={i} position={[x, 0.12, 0.26 + Math.sin(i) * 0.05]} rotation={[Math.PI / 2, 0, (i - 1.5) * 0.28]}>
            <capsuleGeometry args={[0.027, 0.42, 4, 8]} />
            <meshStandardMaterial color={i % 2 ? '#70442b' : '#51351f'} roughness={0.96} />
          </mesh>
        ))}
        {high && <LandmarkPine x={-0.7} z={-0.28} scale={0.72} color="#7fac55" />}
      </group>
    )
  }

  if (piece.kind === 'forest-pine-bridge') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        {[-0.62, 0.58].map((x, i) => <LandmarkPine key={i} x={x} z={-0.08 + i * 0.08} scale={0.86 - i * 0.05} color={i ? '#6f9a4a' : soft} />)}
        <mesh position={[0, 0.2, 0.22]} rotation={[0.16, 0, 0]} castShadow receiveShadow>
          <boxGeometry args={[0.92, 0.12, 0.36]} />
          <meshStandardMaterial color="#7b5639" roughness={0.9} />
        </mesh>
        {[-0.36, -0.12, 0.12, 0.36].map((x, i) => (
          <mesh key={i} position={[x, 0.3, 0.22]} rotation={[0.16, 0, 0]}>
            <boxGeometry args={[0.12, 0.055, 0.42]} />
            <meshStandardMaterial color={i % 2 ? '#b28a54' : '#8c663d'} roughness={0.88} />
          </mesh>
        ))}
      </group>
    )
  }

  if (piece.kind === 'forest-canopy-grove') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        {[-0.58, 0, 0.58].map((x, i) => (
          <group key={i} position={[x, 0, i === 1 ? -0.16 : 0.06]}>
            <mesh position={[0, 0.36, 0]} castShadow>
              <cylinderGeometry args={[0.075, 0.14, 0.72 + i * 0.08, 7]} />
              <meshStandardMaterial color={i === 1 ? '#654025' : '#53331f'} roughness={0.94} />
            </mesh>
            {Array.from({ length: materialProfile.organicLobes }, (_, j) => (
              <mesh key={j} position={[(j - (materialProfile.organicLobes - 1) / 2) * 0.095, 0.78 + (j % 3) * 0.07 + i * 0.045, Math.sin(j * 1.4 + i) * 0.14]} scale={[1.18 + (j % 2) * 0.16, 0.78, 1.02]} rotation={[0, j * 0.18, 0]} castShadow>
                <dodecahedronGeometry args={[0.18 + (j === 3 ? 0.045 : 0), 0]} />
                <meshStandardMaterial color={j % 3 === 0 ? '#5f8f3f' : j % 2 ? '#91bd5d' : soft} emissive={theme.biome} emissiveIntensity={0.025} roughness={0.96} flatShading />
              </mesh>
            ))}
          </group>
        ))}
        {[-0.78, -0.38, 0.4, 0.78].map((x, i) => (
          <mesh key={i} position={[x, 0.12, 0.34 + Math.sin(i) * 0.04]} rotation={[Math.PI / 2, 0, (i - 1.5) * 0.22]}>
            <capsuleGeometry args={[0.026, 0.46, 4, 8]} />
            <meshStandardMaterial color={i % 2 ? '#70442b' : '#51351f'} roughness={0.96} />
          </mesh>
        ))}
      </group>
    )
  }

  if (piece.kind === 'cave-crystal-door') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        {[-0.45, 0, 0.45].map((x, i) => (
          <mesh key={i} position={[x, 0.42 + i * 0.08, 0]} castShadow receiveShadow>
            <boxGeometry args={[0.28, 0.84 + i * 0.12, 0.3]} />
            <meshStandardMaterial color={stone} roughness={0.92} flatShading />
          </mesh>
        ))}
        <mesh position={[0, 0.88, 0]} rotation={[0, 0, Math.PI / 2]} castShadow receiveShadow>
          <boxGeometry args={[0.24, 1.18, 0.3]} />
          <meshStandardMaterial color={stone} roughness={0.92} flatShading />
        </mesh>
        {[-0.36, 0.36].map((x, i) => <LandmarkCrystal key={i} x={x} z={0.18} scale={i ? 0.78 : 0.9} color={i ? '#7de3ff' : soft} glow={theme.biomeSoft} height={0.82} />)}
        {high && <PulseLight color={theme.biomeSoft} distance={2.6} intensity={0.32} reducedMotion={reducedMotion} phase={piece.tone * 7} />}
      </group>
    )
  }

  if (piece.kind === 'cave-glow-pool') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        <mesh position={[0, 0.06, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.48, 34]} />
          <meshStandardMaterial color="#55d6ff" emissive="#3bb7ff" emissiveIntensity={0.58} roughness={0.18} transparent opacity={0.76} />
        </mesh>
        <mesh position={[0, 0.075, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.48, 0.58, 34]} />
          <meshStandardMaterial color={stone} roughness={0.86} />
        </mesh>
        {[-0.42, -0.2, 0.3, 0.52].map((x, i) => <LandmarkCrystal key={i} x={x} z={i % 2 ? 0.34 : -0.34} scale={0.56 + i * 0.08} color={i % 2 ? '#7de3ff' : soft} glow={theme.biomeSoft} height={0.5 + i * 0.08} />)}
        {high && <PulseLight color="#55d6ff" distance={2.2} intensity={0.4} reducedMotion={reducedMotion} phase={piece.tone * 5} />}
      </group>
    )
  }

  if (piece.kind === 'cave-crystal-spires') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        <mesh position={[0, 0.12, -0.02]} scale={[1.35, 0.28, 0.92]} receiveShadow>
          <dodecahedronGeometry args={[0.38, 0]} />
          <meshStandardMaterial color={stone} roughness={0.94} flatShading />
        </mesh>
        {Array.from({ length: materialProfile.facetedSpikes }, (_, i) => {
          const spread = i / Math.max(1, materialProfile.facetedSpikes - 1) - 0.5
          const x = spread * 1.34
          return (
            <LandmarkCrystal key={i} x={x} z={i % 2 ? 0.18 : -0.12} scale={0.72 + (i % 3) * 0.11} color={i % 2 ? '#7de3ff' : soft} glow={theme.biomeSoft} height={0.72 + (i === 3 ? 0.54 : (i % 4) * 0.1)} />
          )
        })}
        <mesh position={[0.12, 0.09, 0.34]} rotation={[-Math.PI / 2, 0, piece.tone * Math.PI]}>
          <circleGeometry args={[0.34, 28]} />
          <meshBasicMaterial color="#55d6ff" transparent opacity={0.34} toneMapped={false} />
        </mesh>
        {high && <PulseLight color={theme.biomeSoft} distance={3.2} intensity={0.48} reducedMotion={reducedMotion} phase={piece.tone * 6} />}
      </group>
    )
  }

  if (piece.kind === 'lava-volcano-rift') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        <mesh position={[0, 0.42, 0]} castShadow receiveShadow>
          <coneGeometry args={[0.72, 0.88, 9]} />
          <meshStandardMaterial color="#23110d" roughness={0.92} emissive="#ff3d16" emissiveIntensity={0.12} flatShading />
        </mesh>
        <mesh position={[0, 0.9, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.26, 24]} />
          <meshBasicMaterial color="#ff7a3c" toneMapped={false} />
        </mesh>
        <mesh position={[0, 0.92, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.14, 20]} />
          <meshBasicMaterial color="#ffd45c" transparent opacity={0.88} toneMapped={false} />
        </mesh>
        {Array.from({ length: materialProfile.emissiveCuts }, (_, i) => {
          const x = -0.54 + i * (1.08 / Math.max(1, materialProfile.emissiveCuts - 1))
          return (
          <mesh key={i} position={[x, 0.13, 0.42 - Math.abs(x) * 0.18]} rotation={[0, 0, (i - 1.5) * 0.22]}>
            <boxGeometry args={[0.36 - (i % 3) * 0.04, 0.035, 0.04]} />
            <meshBasicMaterial color={i % 2 ? '#ffd45c' : '#ff6a35'} transparent opacity={0.86} toneMapped={false} />
          </mesh>
          )
        })}
        <group position={[0, 0.98, 0]}>
          <FlickerFlame color="#ff7a3c" hot="#ffd45c" reducedMotion={reducedMotion} distance={high ? 3.1 : 1.8} intensity={high ? 1.15 : 0.44} scale={0.9} />
        </group>
        {high && [0, 1, 2].map((i) => (
          <mesh key={i} position={[(i - 1) * 0.18, 1.18 + i * 0.1, -0.04]} scale={[1.1, 1.55, 1.1]}>
            <sphereGeometry args={[0.09 + i * 0.015, 9, 6]} />
            <meshBasicMaterial color="#6e5d5a" transparent opacity={0.26 - i * 0.04} />
          </mesh>
        ))}
      </group>
    )
  }

  if (piece.kind === 'lava-smoke-vent') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        {[-0.36, 0, 0.36].map((x, i) => (
          <group key={i} position={[x, 0, Math.abs(x) * 0.18]}>
            <mesh position={[0, 0.32 + i * 0.08, 0]} castShadow receiveShadow>
              <cylinderGeometry args={[0.17, 0.23, 0.64 + i * 0.16, 7]} />
              <meshStandardMaterial color="#251512" roughness={0.9} emissive="#ff3d16" emissiveIntensity={0.12} />
            </mesh>
            <mesh position={[0, 0.46 + i * 0.08, 0.18]} rotation={[0, 0, 0.12]}>
              <boxGeometry args={[0.26, 0.035, 0.04]} />
              <meshBasicMaterial color={i % 2 ? '#ffd45c' : '#ff6a35'} toneMapped={false} />
            </mesh>
            {high && i !== 1 && <FlickerFlame color="#ff7a3c" hot="#ffd45c" reducedMotion={reducedMotion} distance={1.6} intensity={0.42} scale={0.45} />}
          </group>
        ))}
      </group>
    )
  }

  if (piece.kind === 'lava-basalt-columns') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        {[-0.68, -0.34, 0, 0.34, 0.68].map((x, i) => {
          const h = 0.72 + (i % 3) * 0.18 + (i === 2 ? 0.28 : 0)
          return (
            <group key={i} position={[x, 0, Math.abs(x) * 0.16]}>
              {Array.from({ length: materialProfile.materialBands }, (_, band) => {
                const bandY = h * (0.09 + band * 0.105)
                const radius = 0.245 - band * 0.011 + (band % 2) * 0.024
                return (
                  <mesh key={`band-${band}`} position={[(band % 2 ? 0.025 : -0.015), bandY, Math.sin(band + i) * 0.02]} rotation={[0, piece.tone * 0.7 + band * 0.18, (band - 3) * 0.012]} scale={[1.05 + (band % 2) * 0.1, 1, 0.88 + (band % 3) * 0.08]} castShadow receiveShadow>
                    <cylinderGeometry args={[radius * 0.78, radius, h * 0.14, 7]} />
                    <meshStandardMaterial color={band % 3 === 0 ? '#160907' : '#21100c'} roughness={0.94} emissive="#ff3d16" emissiveIntensity={0.08 + band * 0.012} flatShading />
                  </mesh>
                )
              })}
              {Array.from({ length: Math.min(4, materialProfile.emissiveCuts) }, (_, cut) => (
                <mesh key={cut} position={[(cut - 1.5) * 0.052, h * (0.28 + cut * 0.12), 0.228]} rotation={[0, 0, (cut - 1.5) * 0.08]}>
                  <boxGeometry args={[0.028 + (cut % 2) * 0.01, h * (0.34 - cut * 0.035), 0.026]} />
                  <meshBasicMaterial color={cut === 1 ? '#ffd45c' : '#ff6a35'} transparent opacity={0.74} toneMapped={false} />
                </mesh>
              ))}
              <mesh position={[0, h * 0.82, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                <circleGeometry args={[0.14, 18]} />
                <meshBasicMaterial color={i % 2 ? '#ffd45c' : '#ff6a35'} transparent opacity={0.86} toneMapped={false} />
              </mesh>
              {Array.from({ length: 3 }, (_, chip) => (
                <mesh key={`chip-${chip}`} position={[(chip - 1) * 0.16, 0.08 + chip * 0.035, -0.12 + Math.sin(chip + i) * 0.08]} rotation={[0.12, chip * 0.44, (chip - 1) * 0.08]} castShadow receiveShadow>
                  <dodecahedronGeometry args={[0.08 + chip * 0.014, 0]} />
                  <meshStandardMaterial color="#1a0c09" roughness={0.96} emissive="#ff3d16" emissiveIntensity={0.08} flatShading />
                </mesh>
              ))}
              {high && i % 2 === 0 && <FlickerFlame color="#ff7a3c" hot="#ffd45c" reducedMotion={reducedMotion} distance={1.8} intensity={0.5} scale={0.42} />}
            </group>
          )
        })}
        {high && <PulseLight color="#ff6a35" distance={3.4} intensity={0.42} reducedMotion={reducedMotion} phase={piece.tone * 4} />}
      </group>
    )
  }

  if (piece.kind === 'ice-aurora-pines') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        {[-0.54, 0.04, 0.54].map((x, i) => <LandmarkPine key={i} x={x} z={i === 1 ? -0.18 : 0.02} scale={0.9 - i * 0.05} color={i % 2 ? '#8fe8ef' : '#4a927d'} snow />)}
        {high && (
          <>
            <mesh position={[0, 1.05, -0.12]} rotation={[Math.PI / 2, 0.16, 0.2]}>
              <torusGeometry args={[0.72, 0.016, 8, 54, Math.PI * 1.18]} />
              <meshBasicMaterial color="#7de3ff" transparent opacity={0.48} toneMapped={false} />
            </mesh>
            <mesh position={[0.16, 1.18, -0.16]} rotation={[Math.PI / 2, 0.28, -0.14]}>
              <torusGeometry args={[0.66, 0.014, 8, 50, Math.PI * 1.08]} />
              <meshBasicMaterial color="#ff8bea" transparent opacity={0.36} toneMapped={false} />
            </mesh>
          </>
        )}
      </group>
    )
  }

  if (piece.kind === 'ice-frozen-slabs') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        {[-0.42, 0, 0.38].map((x, i) => (
          <mesh key={i} position={[x, 0.08 + i * 0.01, i % 2 ? 0.12 : -0.12]} scale={[1.25, 0.22, 0.86]} receiveShadow>
            <sphereGeometry args={[0.22, 16, 8]} />
            <meshStandardMaterial color={i === 1 ? '#f4fbff' : '#a7ebff'} emissive={theme.biome} emissiveIntensity={0.1} roughness={0.38} transparent opacity={0.88} />
          </mesh>
        ))}
        {[-0.56, 0.52].map((x, i) => <LandmarkCrystal key={i} x={x} z={0.28 - i * 0.45} scale={0.72} color="#d9f8ff" glow={theme.biome} height={0.62} />)}
      </group>
    )
  }

  if (piece.kind === 'ice-glacier-spires') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        {Array.from({ length: materialProfile.facetedSpikes }, (_, i) => {
          const spread = i / Math.max(1, materialProfile.facetedSpikes - 1) - 0.5
          return (
            <LandmarkCrystal key={i} x={spread * 1.34} z={i % 2 ? 0.1 : -0.14} scale={0.68 + (i % 3) * 0.1} color={i % 2 ? '#f4fbff' : theme.biomeSoft} glow={theme.biome} height={0.64 + (i === 3 ? 0.46 : (i % 4) * 0.1)} />
          )
        })}
        {[-0.48, 0.4].map((x, i) => <LandmarkPine key={i} x={x} z={0.28 - i * 0.12} scale={0.66} color="#6a9d8e" snow />)}
      </group>
    )
  }

  if (piece.kind === 'magical-portal-arch') {
    return (
      <group position={[piece.x, piece.y + 0.04, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        <mesh position={[0, 0.62, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.54, 0.045, 10, 64, Math.PI * 1.75]} />
          <meshStandardMaterial color={theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.8} roughness={0.2} transparent opacity={0.92} />
        </mesh>
        <mesh position={[0, 0.56, 0.02]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.38, 42]} />
          <meshBasicMaterial color={theme.biomeSoft} transparent opacity={0.26} toneMapped={false} side={THREE.DoubleSide} />
        </mesh>
        {[-0.48, 0.48].map((x, i) => (
          <mesh key={i} position={[x, 0.16, 0]} castShadow>
            <boxGeometry args={[0.22, 0.32, 0.22]} />
            <meshStandardMaterial color={stone} emissive={theme.biome} emissiveIntensity={0.18} roughness={0.72} />
          </mesh>
        ))}
        {high && <PulseLight color={theme.biomeSoft} distance={2.9} intensity={0.5} reducedMotion={reducedMotion} phase={piece.tone * 9} />}
      </group>
    )
  }

  if (piece.kind === 'magical-rune-orbs') {
    return (
      <group position={[piece.x, piece.y + 0.04, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        <mesh position={[0, 0.12, 0]} rotation={[-Math.PI / 2, 0, piece.tone * Math.PI]}>
          <cylinderGeometry args={[0.48, 0.52, 0.12, 8]} />
          <meshStandardMaterial color={stone} emissive={theme.biome} emissiveIntensity={0.16} roughness={0.72} />
        </mesh>
        <mesh position={[0, 0.2, 0]} rotation={[Math.PI / 2, 0, piece.tone * Math.PI]}>
          <torusGeometry args={[0.38, 0.012, 8, 38, Math.PI * 1.55]} />
          <meshBasicMaterial color={theme.biomeSoft} transparent opacity={0.72} toneMapped={false} />
        </mesh>
        {Array.from({ length: materialProfile.floatingSatellites }, (_, i) => {
          const spread = i / Math.max(1, materialProfile.floatingSatellites - 1) - 0.5
          const x = spread * 0.84
          return (
          <mesh key={i} position={[x, 0.48 + i * 0.08, Math.sin(i) * 0.16]} rotation={[0.35, i, 0.2]}>
            <octahedronGeometry args={[0.12 - (i % 3) * 0.012, 0]} />
            <meshStandardMaterial color={i % 2 ? '#7de3ff' : theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.56} roughness={0.22} transparent opacity={0.9} />
          </mesh>
          )
        })}
        {high && <PulseLight color={theme.biomeSoft} distance={2.1} intensity={0.34} reducedMotion={reducedMotion} phase={piece.tone * 4} />}
      </group>
    )
  }

  if (piece.kind === 'magical-floating-monoliths') {
    return (
      <group position={[piece.x, piece.y + 0.05, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        {[-0.52, 0, 0.52].map((x, i) => (
          <group key={i} position={[x, 0, Math.sin(i + piece.tone) * 0.12]} rotation={[0.28, piece.tone * Math.PI + i * 0.46, 0.18]}>
            <mesh position={[0, 0.54 + i * 0.14, 0]} castShadow>
              <dodecahedronGeometry args={[0.24, 0]} />
              <meshStandardMaterial color={stone} emissive={theme.biome} emissiveIntensity={0.2 + i * 0.07} roughness={0.72} flatShading />
            </mesh>
            <mesh position={[0, 0.74 + i * 0.14, 0]} scale={[0.72, 2.7 + i * 0.46, 0.62]} castShadow>
              <octahedronGeometry args={[0.14, 0]} />
              <meshStandardMaterial color={new THREE.Color(stone).lerp(new THREE.Color(theme.biomeSoft), 0.18).getStyle()} emissive={theme.biome} emissiveIntensity={0.28 + i * 0.08} roughness={0.58} flatShading />
            </mesh>
            {Array.from({ length: materialProfile.emissiveCuts }, (_, cut) => (
              <mesh key={cut} position={[(cut - (materialProfile.emissiveCuts - 1) / 2) * 0.044, 0.42 + i * 0.14 + cut * 0.105, 0.112]} rotation={[0, 0, (cut - 2) * 0.1]}>
                <boxGeometry args={[0.03 + (cut % 2) * 0.014, 0.13, 0.018]} />
                <meshBasicMaterial color={cut % 2 ? '#7de3ff' : theme.biomeSoft} transparent opacity={0.64} toneMapped={false} />
              </mesh>
            ))}
            {[0, 1].map((chip) => (
              <mesh key={`chip-${chip}`} position={[(chip - 0.5) * 0.26, 0.18 + chip * 0.08, -0.08]} rotation={[0.34, chip + piece.tone, 0.18]} castShadow>
                <octahedronGeometry args={[0.08 + chip * 0.022, 0]} />
                <meshStandardMaterial color={chip ? '#7de3ff' : theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.52} roughness={0.26} transparent opacity={0.88} />
              </mesh>
            ))}
          </group>
        ))}
        <mesh position={[0, 0.88, 0]} rotation={[Math.PI / 2, 0.22, 0.1]}>
          <torusGeometry args={[0.72, 0.016, 8, 58, Math.PI * 1.25]} />
          <meshBasicMaterial color={theme.biomeSoft} transparent opacity={0.64} toneMapped={false} />
        </mesh>
        <mesh position={[-0.34, 0.26, 0.26]} rotation={[0.18, piece.tone * Math.PI, -0.18]} scale={[0.84, 0.28, 1.12]} receiveShadow>
          <dodecahedronGeometry args={[0.16, 0]} />
          <meshStandardMaterial color={new THREE.Color(stone).lerp(new THREE.Color(theme.biome), 0.22).getStyle()} emissive={theme.biome} emissiveIntensity={0.18} roughness={0.78} flatShading />
        </mesh>
        {[0, 1, 2, 3, 4, 5].map((i) => {
          const a = i / 6 * Math.PI * 2
          return (
            <mesh key={`flower-${i}`} position={[Math.cos(a) * 0.48, 0.13, Math.sin(a) * 0.28]} rotation={[0.24, -a, a * 0.18]} scale={[0.74, 1, 0.58]}>
              <boxGeometry args={[0.12, 0.026, 0.05]} />
              <meshStandardMaterial color={i % 2 ? '#7de3ff' : theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.28} roughness={0.38} />
            </mesh>
          )
        })}
        {Array.from({ length: materialProfile.floatingSatellites }, (_, i) => {
          const a = i / Math.max(1, materialProfile.floatingSatellites) * Math.PI * 2 + piece.tone
          return (
          <mesh key={i} position={[Math.cos(a) * 0.82, 0.72 + (i % 3) * 0.12, Math.sin(a) * 0.18]} rotation={[0.35, i, 0.2]}>
            <octahedronGeometry args={[0.1 + (i % 2) * 0.025, 0]} />
            <meshStandardMaterial color={i ? '#7de3ff' : theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.7} roughness={0.22} transparent opacity={0.92} />
          </mesh>
          )
        })}
        {high && <PulseLight color={theme.biomeSoft} distance={3.2} intensity={0.58} reducedMotion={reducedMotion} phase={piece.tone * 8} />}
      </group>
    )
  }

  if (piece.kind === 'dungeon-gate-wall') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        {[-0.56, -0.28, 0, 0.28, 0.56].map((x, i) => (
          <mesh key={i} position={[x, 0.36 + (i % 2) * 0.08, 0]} castShadow receiveShadow>
            <boxGeometry args={[0.26, 0.72 + (i % 2) * 0.16, 0.34]} />
            <meshStandardMaterial color={stone} roughness={0.9} metalness={0.04} emissive={theme.biome} emissiveIntensity={0.04} />
          </mesh>
        ))}
        {Array.from({ length: materialProfile.masonryCourses }, (_, i) => (
          <mesh key={`course-${i}`} position={[(i / Math.max(1, materialProfile.masonryCourses - 1) - 0.5) * 1.18, 0.18 + (i % 4) * 0.14, 0.24]} castShadow receiveShadow>
            <boxGeometry args={[0.16 + (i % 3) * 0.025, 0.09, 0.08]} />
            <meshStandardMaterial color={new THREE.Color(stone).lerp(new THREE.Color('#d0a24c'), i % 2 ? 0.08 : 0.02).getStyle()} roughness={0.92} metalness={0.03} emissive={theme.biome} emissiveIntensity={0.035} />
          </mesh>
        ))}
        {[-0.24, 0, 0.24].map((x, i) => (
          <mesh key={i} position={[x, 0.43, 0.22]} castShadow>
            <boxGeometry args={[0.055, 0.76, 0.08]} />
            <meshStandardMaterial color="#c28b3c" metalness={0.38} roughness={0.42} />
          </mesh>
        ))}
        <mesh position={[0, 0.78, 0.22]}>
          <boxGeometry args={[0.78, 0.08, 0.09]} />
          <meshStandardMaterial color="#d0a24c" metalness={0.32} roughness={0.44} />
        </mesh>
        {[-0.72, 0.72].map((x, i) => (
          <group key={i} position={[x, 0.76, 0.24]}>
            <FlickerFlame color="#ff9a45" reducedMotion={reducedMotion} distance={high ? 1.8 : 1.1} intensity={high ? 0.55 : 0.24} scale={0.46} />
          </group>
        ))}
      </group>
    )
  }

  if (piece.kind === 'dungeon-torch-wall') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
        {[-0.72, -0.36, 0, 0.36, 0.72].map((x, i) => (
          <mesh key={i} position={[x, 0.4 + (i % 2) * 0.08, 0]} castShadow receiveShadow>
            <boxGeometry args={[0.26, 0.78 + (i === 2 ? 0.24 : 0), 0.38]} />
            <meshStandardMaterial color={stone} roughness={0.9} metalness={0.04} emissive={theme.biome} emissiveIntensity={0.06} />
          </mesh>
        ))}
        {Array.from({ length: materialProfile.masonryCourses }, (_, i) => (
          <mesh key={`course-${i}`} position={[(i / Math.max(1, materialProfile.masonryCourses - 1) - 0.5) * 1.48, 0.2 + (i % 4) * 0.15, 0.25]} castShadow receiveShadow>
            <boxGeometry args={[0.18 + (i % 3) * 0.025, 0.1, 0.085]} />
            <meshStandardMaterial color={new THREE.Color(stone).lerp(new THREE.Color('#d0a24c'), i % 2 ? 0.1 : 0.03).getStyle()} roughness={0.92} metalness={0.035} emissive={theme.biome} emissiveIntensity={0.04} />
          </mesh>
        ))}
        {[-0.84, 0.84].map((x, i) => (
          <group key={i} position={[x, 0.9, 0.23]}>
            <mesh position={[0, -0.22, 0]} castShadow>
              <cylinderGeometry args={[0.055, 0.07, 0.48, 8]} />
              <meshStandardMaterial color="#c28b3c" metalness={0.36} roughness={0.42} />
            </mesh>
            <FlickerFlame color="#ff9a45" reducedMotion={reducedMotion} distance={high ? 2.4 : 1.4} intensity={high ? 0.72 : 0.32} scale={0.52} />
          </group>
        ))}
        <mesh position={[0, 0.84, 0.24]}>
          <boxGeometry args={[1.42, 0.08, 0.1]} />
          <meshStandardMaterial color="#d0a24c" metalness={0.34} roughness={0.44} />
        </mesh>
        {[-0.42, 0, 0.42].map((x, i) => (
          <mesh key={i} position={[x, 0.48 - Math.abs(i - 1) * 0.08, 0.26]} rotation={[Math.PI / 2, 0, i * Math.PI / 2]}>
            <torusGeometry args={[0.1, 0.016, 6, 14]} />
            <meshStandardMaterial color="#d0a24c" metalness={0.42} roughness={0.38} />
          </mesh>
        ))}
      </group>
    )
  }

  return (
    <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={landmarkScale}>
      {[-0.42, 0.42].map((x, i) => (
        <group key={i} position={[x, 0, 0]}>
          <mesh position={[0, 0.4, 0]} castShadow>
            <cylinderGeometry args={[0.07, 0.09, 0.8, 8]} />
            <meshStandardMaterial color={theme.edge} metalness={0.18} roughness={0.62} />
          </mesh>
          <group position={[0, 0.84, 0]}>
            <FlickerFlame color="#ff9a45" reducedMotion={reducedMotion} distance={high ? 1.9 : 1.1} intensity={high ? 0.6 : 0.24} scale={0.48} />
          </group>
        </group>
      ))}
      {[-0.26, 0, 0.26].map((x, i) => (
        <mesh key={i} position={[x, 0.55 - Math.abs(i - 1) * 0.08, 0]} rotation={[Math.PI / 2, 0, i * Math.PI / 2]}>
          <torusGeometry args={[0.09, 0.016, 6, 14]} />
          <meshStandardMaterial color="#d0a24c" metalness={0.42} roughness={0.38} />
        </mesh>
      ))}
      {high && (
        <mesh position={[0, 0.18, -0.18]} receiveShadow>
          <boxGeometry args={[1.1, 0.12, 0.22]} />
          <meshStandardMaterial color={stone} roughness={0.9} />
        </mesh>
      )}
    </group>
  )
}

function BiomeLandmarks({ layout, theme, biome, graphics, reducedMotion }: { layout: BoardLayout; theme: BiomeTheme; biome: BiomeId; graphics: 'high' | 'medium' | 'low'; reducedMotion: boolean }) {
  const pieces = useMemo(() => buildBiomeLandmarks(layout, biome, graphics), [layout, biome, graphics])
  if (pieces.length === 0) return null
  return (
    <group>
      {pieces.map((piece) => (
        <BiomeLandmark key={piece.key} piece={piece} theme={theme} graphics={graphics} reducedMotion={reducedMotion} />
      ))}
    </group>
  )
}

function PlatformEdgePiece({ piece, theme, biome, reducedMotion }: { piece: PlatformEdgePieceInfo; theme: BiomeTheme; biome: BiomeId; reducedMotion: boolean }) {
  const sideStone = useMemo(() => new THREE.Color(theme.surfaceSunken).lerp(new THREE.Color(theme.edge), 0.18 + piece.tone * 0.34).getStyle(), [piece.tone, theme.edge, theme.surfaceSunken])
  const soft = useMemo(() => new THREE.Color(theme.biome).lerp(new THREE.Color(theme.biomeSoft), 0.25 + piece.tone * 0.42).getStyle(), [piece.tone, theme.biome, theme.biomeSoft])
  if (biome === 'forest') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[(i - 1) * 0.12, -piece.drop * (0.28 + i * 0.08), 0.02]} rotation={[0.12, 0, (i - 1) * 0.22]} castShadow>
            <capsuleGeometry args={[0.032 + i * 0.004, piece.drop * (0.74 - i * 0.08), 5, 9]} />
            <meshStandardMaterial color={i === 1 ? '#70442b' : '#51351f'} roughness={0.95} />
          </mesh>
        ))}
        <mesh position={[0, 0.08, 0.02]} scale={[1.35, 0.42, 0.82]} receiveShadow>
          <sphereGeometry args={[0.18, 12, 7]} />
          <meshStandardMaterial color={soft} emissive={theme.biome} emissiveIntensity={0.05} roughness={0.86} />
        </mesh>
      </group>
    )
  }
  if (biome === 'cave') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
        <mesh position={[0, -0.08, 0]} scale={[piece.length * 1.1, 0.7, 0.42]} castShadow receiveShadow>
          <dodecahedronGeometry args={[0.26, 0]} />
          <meshStandardMaterial color={sideStone} roughness={0.92} flatShading />
        </mesh>
        {piece.variant % 2 === 0 && (
          <group position={[0.16, -piece.drop * 0.38, 0.04]}>
            <mesh rotation={[Math.PI, 0, 0]} castShadow>
              <coneGeometry args={[0.09, piece.drop * 0.64, 5]} />
              <meshStandardMaterial color={soft} emissive={theme.biomeSoft} emissiveIntensity={0.42} roughness={0.26} transparent opacity={0.9} />
            </mesh>
            <PulseLight color={theme.biomeSoft} distance={1.35} intensity={0.16} reducedMotion={reducedMotion} phase={piece.tone * 6} />
          </group>
        )}
      </group>
    )
  }
  if (biome === 'lava') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
        <mesh position={[0, -0.08, 0]} scale={[piece.length * 1.2, 0.68, 0.38]} castShadow receiveShadow>
          <boxGeometry args={[0.48, 0.46, 0.08]} />
          <meshStandardMaterial color="#1d0d09" roughness={0.9} emissive="#ff3d16" emissiveIntensity={0.08} />
        </mesh>
        {[0, 1].map((i) => (
          <mesh key={i} position={[(i - 0.5) * piece.length * 0.34, -0.06 - i * 0.1, 0.056]} rotation={[0, 0, (piece.variant - 3) * 0.08]}>
            <boxGeometry args={[piece.length * (0.42 - i * 0.08), 0.035, 0.028]} />
            <meshBasicMaterial color={i === 0 ? '#ff6a35' : '#ffd45c'} toneMapped={false} />
          </mesh>
        ))}
        {piece.variant % 4 === 0 && (
          <group position={[0.18, 0.1, 0.05]}>
            <FlickerFlame color="#ff7a3c" hot="#ffd45c" reducedMotion={reducedMotion} distance={1.4} intensity={0.34} scale={0.48} />
          </group>
        )}
      </group>
    )
  }
  if (biome === 'ice') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
        <mesh position={[0, 0.08, 0]} scale={[piece.length * 1.45, 0.28, 0.55]} receiveShadow>
          <boxGeometry args={[0.3, 0.05, 0.22]} />
          <meshStandardMaterial color="#f4fbff" emissive={theme.biome} emissiveIntensity={0.08} roughness={0.48} />
        </mesh>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[(i - 1) * 0.14, -piece.drop * (0.22 + i * 0.05), 0.04]} rotation={[Math.PI, 0, (i - 1) * 0.08]} castShadow>
            <coneGeometry args={[0.07 - i * 0.006, piece.drop * (0.64 - i * 0.06), 6]} />
            <meshStandardMaterial color={i % 2 ? '#ffffff' : theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.16} roughness={0.28} transparent opacity={0.9} />
          </mesh>
        ))}
      </group>
    )
  }
  if (biome === 'magical') {
    return (
      <group position={[piece.x, piece.y + 0.04 + piece.tone * 0.06, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
        {[0, 1].map((i) => (
          <mesh key={i} position={[(i - 0.5) * 0.26, -0.04 + i * 0.12, 0.04]} rotation={[0.42, piece.tone * Math.PI + i, 0.2]} castShadow>
            <octahedronGeometry args={[0.11 + i * 0.035, 0]} />
            <meshStandardMaterial color={i ? '#7de3ff' : soft} emissive={theme.biome} emissiveIntensity={0.58} roughness={0.22} transparent opacity={0.9} />
          </mesh>
        ))}
        <mesh position={[0, -0.02, 0.02]} rotation={[Math.PI / 2, 0, piece.tone * Math.PI]}>
          <torusGeometry args={[0.24, 0.01, 6, 28, Math.PI * 1.45]} />
          <meshBasicMaterial color={theme.biomeSoft} transparent opacity={0.54} toneMapped={false} />
        </mesh>
        {piece.variant % 3 === 0 && <PulseLight color={theme.biomeSoft} distance={1.35} intensity={0.2} reducedMotion={reducedMotion} phase={piece.tone * 9} />}
      </group>
    )
  }
  return (
    <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
      <mesh position={[0, -0.08, 0]} scale={[piece.length * 1.08, 0.82, 0.42]} castShadow receiveShadow>
        <boxGeometry args={[0.5, 0.42, 0.1]} />
        <meshStandardMaterial color={sideStone} roughness={0.88} metalness={0.04} emissive={theme.biome} emissiveIntensity={0.04} />
      </mesh>
      {[0, 1, 2].map((i) => (
        <mesh key={i} position={[(i - 1) * 0.09, -piece.drop * 0.34 - i * 0.08, 0.055]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.055, 0.012, 6, 12]} />
          <meshStandardMaterial color="#d0a24c" metalness={0.34} roughness={0.46} />
        </mesh>
      ))}
      {piece.variant % 5 === 0 && (
        <group position={[0.2, 0.14, 0.05]}>
          <FlickerFlame color="#ff9a45" reducedMotion={reducedMotion} distance={1.35} intensity={0.32} scale={0.42} />
        </group>
      )}
    </group>
  )
}

function PlatformEdgeDressings({ layout, theme, biome, graphics, reducedMotion }: { layout: BoardLayout; theme: BiomeTheme; biome: BiomeId; graphics: 'high' | 'medium' | 'low'; reducedMotion: boolean }) {
  const pieces = useMemo(() => buildPlatformEdgePieces(layout, biome, graphics), [layout, biome, graphics])
  if (pieces.length === 0) return null
  return (
    <group>
      {pieces.map((piece) => (
        <PlatformEdgePiece key={piece.key} piece={piece} theme={theme} biome={biome} reducedMotion={reducedMotion} />
      ))}
    </group>
  )
}

function BaseSilhouettePiece({ piece, theme, biome, reducedMotion }: { piece: BaseSilhouettePieceInfo; theme: BiomeTheme; biome: BiomeId; reducedMotion: boolean }) {
  const geom = useMemo(() => makeRoundedBox(piece.width, piece.height, piece.depth, piece.cornerRadius, 2), [piece.cornerRadius, piece.depth, piece.height, piece.width])
  const sideColor = useMemo(() => new THREE.Color(theme.surfaceSunken).lerp(new THREE.Color(theme.edge), 0.18 + piece.tone * 0.38).getStyle(), [piece.tone, theme.edge, theme.surfaceSunken])
  const topColor = useMemo(() => new THREE.Color(theme.surfaceRaised).lerp(new THREE.Color(theme.biome), 0.16 + piece.tone * 0.26).getStyle(), [piece.tone, theme.biome, theme.surfaceRaised])
  const soft = useMemo(() => new THREE.Color(theme.biome).lerp(new THREE.Color(theme.biomeSoft), 0.32 + piece.tone * 0.4).getStyle(), [piece.tone, theme.biome, theme.biomeSoft])
  if (biome === 'forest') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]}>
        <mesh geometry={geom} castShadow receiveShadow>
          <meshStandardMaterial color="#2d2115" roughness={0.98} />
        </mesh>
        <mesh position={[0, piece.height * 0.52, 0]} scale={[piece.width * 1.25, 0.32, piece.depth * 1.8]} receiveShadow>
          <sphereGeometry args={[0.18, 12, 7]} />
          <meshStandardMaterial color={soft} emissive={theme.biome} emissiveIntensity={0.04} roughness={0.92} />
        </mesh>
        {piece.variant % 2 === 0 && (
          <mesh position={[piece.width * 0.22, -piece.height * 0.28, piece.depth * 0.18]} rotation={[Math.PI / 2, 0, 0.42]} castShadow>
            <capsuleGeometry args={[0.032, piece.drop * 0.58, 5, 8]} />
            <meshStandardMaterial color="#6d472d" roughness={0.97} />
          </mesh>
        )}
      </group>
    )
  }
  if (biome === 'cave') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]}>
        <mesh geometry={geom} castShadow receiveShadow>
          <meshStandardMaterial color={sideColor} roughness={0.94} flatShading />
        </mesh>
        {piece.variant % 2 === 0 && (
          <mesh position={[piece.width * 0.18, piece.height * 0.58, piece.depth * 0.12]} rotation={[0.16, piece.tone * Math.PI, 0.08]} castShadow>
            <coneGeometry args={[0.065 + piece.tone * 0.03, 0.28 + piece.tone * 0.18, 5]} />
            <meshStandardMaterial color={soft} emissive={theme.biomeSoft} emissiveIntensity={0.36} roughness={0.3} transparent opacity={0.9} />
          </mesh>
        )}
      </group>
    )
  }
  if (biome === 'lava') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]}>
        <mesh geometry={geom} castShadow receiveShadow>
          <meshStandardMaterial color="#1a0c09" roughness={0.91} emissive="#ff3d16" emissiveIntensity={0.1 + piece.tone * 0.06} />
        </mesh>
        {[0, 1].map((i) => (
          <mesh key={i} position={[(i - 0.5) * piece.width * 0.38, piece.height * (0.2 - i * 0.18), piece.depth * 0.52]} rotation={[0, 0, (piece.variant - 4) * 0.06]}>
            <boxGeometry args={[piece.width * (0.42 - i * 0.08), 0.03, 0.026]} />
            <meshBasicMaterial color={i === 0 ? '#ff6a35' : '#ffd45c'} transparent opacity={0.86} toneMapped={false} />
          </mesh>
        ))}
        {piece.variant % 6 === 0 && (
          <group position={[piece.width * 0.25, piece.height * 0.7, piece.depth * 0.1]}>
            <FlickerFlame color="#ff7a3c" hot="#ffd45c" reducedMotion={reducedMotion} distance={1.25} intensity={0.26} scale={0.36} />
          </group>
        )}
      </group>
    )
  }
  if (biome === 'ice') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]}>
        <mesh geometry={geom} castShadow receiveShadow>
          <meshStandardMaterial color="#82cfe1" emissive={theme.biome} emissiveIntensity={0.1} roughness={0.42} transparent opacity={0.94} />
        </mesh>
        <mesh position={[0, piece.height * 0.54, 0]} scale={[piece.width * 1.35, 0.3, piece.depth * 1.75]} receiveShadow>
          <boxGeometry args={[0.28, 0.05, 0.22]} />
          <meshStandardMaterial color="#f4fbff" roughness={0.55} />
        </mesh>
        {piece.variant % 3 === 0 && (
          <mesh position={[piece.width * 0.2, -piece.height * 0.36, piece.depth * 0.18]} rotation={[Math.PI, 0, -0.08]} castShadow>
            <coneGeometry args={[0.05, piece.drop * 0.52, 6]} />
            <meshStandardMaterial color={theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.16} roughness={0.28} transparent opacity={0.9} />
          </mesh>
        )}
      </group>
    )
  }
  if (biome === 'magical') {
    return (
      <group position={[piece.x, piece.y + piece.tone * 0.035, piece.z]} rotation={[0, piece.rot, 0]}>
        <mesh geometry={geom} castShadow receiveShadow>
          <meshStandardMaterial color={topColor} roughness={0.76} emissive={theme.biome} emissiveIntensity={0.18} />
        </mesh>
        <mesh position={[0, piece.height * 0.58, piece.depth * 0.12]} rotation={[Math.PI / 2, 0, piece.tone * Math.PI]}>
          <torusGeometry args={[Math.min(piece.width, 0.48) * 0.28, 0.009, 6, 28, Math.PI * 1.48]} />
          <meshBasicMaterial color={theme.biomeSoft} transparent opacity={0.62} toneMapped={false} />
        </mesh>
        {piece.variant % 2 === 0 && (
          <mesh position={[piece.width * 0.22, piece.height * 0.82, 0]} rotation={[0.38, piece.tone * Math.PI, 0.2]} castShadow>
            <octahedronGeometry args={[0.07 + piece.tone * 0.035, 0]} />
            <meshStandardMaterial color={piece.variant % 4 === 0 ? '#7de3ff' : soft} emissive={theme.biome} emissiveIntensity={0.55} roughness={0.24} transparent opacity={0.9} />
          </mesh>
        )}
      </group>
    )
  }
  return (
    <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]}>
      <mesh geometry={geom} castShadow receiveShadow>
        <meshStandardMaterial color={sideColor} roughness={0.9} metalness={0.04} emissive={theme.biome} emissiveIntensity={0.04} />
      </mesh>
      <mesh position={[0, piece.height * 0.54, piece.depth * 0.16]}>
        <boxGeometry args={[piece.width * 0.72, 0.032, 0.028]} />
        <meshStandardMaterial color="#d0a24c" metalness={0.28} roughness={0.5} />
      </mesh>
      {piece.variant % 4 === 0 && (
        <mesh position={[piece.width * 0.22, piece.height * 0.18, piece.depth * 0.34]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.052, 0.01, 6, 14]} />
          <meshStandardMaterial color="#d0a24c" metalness={0.36} roughness={0.48} />
        </mesh>
      )}
    </group>
  )
}

function BaseSilhouetteDressings({ layout, theme, biome, graphics, reducedMotion }: { layout: BoardLayout; theme: BiomeTheme; biome: BiomeId; graphics: 'high' | 'medium' | 'low'; reducedMotion: boolean }) {
  const pieces = useMemo(() => buildBaseSilhouettePieces(layout, biome, graphics), [layout, biome, graphics])
  if (pieces.length === 0) return null
  return (
    <group>
      {pieces.map((piece) => (
        <BaseSilhouettePiece key={piece.key} piece={piece} theme={theme} biome={biome} reducedMotion={reducedMotion} />
      ))}
    </group>
  )
}

function PlatformContourPiece({ piece, theme, biome, reducedMotion }: { piece: PlatformContourPieceInfo; theme: BiomeTheme; biome: BiomeId; reducedMotion: boolean }) {
  const stone = useMemo(() => new THREE.Color(theme.surfaceRaised).lerp(new THREE.Color(theme.edge), 0.25 + piece.tone * 0.36).getStyle(), [piece.tone, theme.edge, theme.surfaceRaised])
  const soft = useMemo(() => new THREE.Color(theme.biome).lerp(new THREE.Color(theme.biomeSoft), 0.28 + piece.tone * 0.44).getStyle(), [piece.tone, theme.biome, theme.biomeSoft])
  const geom = useMemo(() => makeRoundedBox(piece.width, piece.height, piece.depth, 0.055, 3), [piece.depth, piece.height, piece.width])
  if (biome === 'forest') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]}>
        <mesh geometry={geom} castShadow receiveShadow>
          <meshStandardMaterial color="#3b2e1b" roughness={0.96} />
        </mesh>
        <mesh position={[0, piece.height * 0.5 + 0.018, 0]} scale={[piece.width * 1.28, 0.28, piece.depth * 2.1]} receiveShadow>
          <sphereGeometry args={[0.18, 12, 7]} />
          <meshStandardMaterial color={soft} emissive={theme.biome} emissiveIntensity={0.05} roughness={0.9} />
        </mesh>
        {piece.variant % 3 === 0 && (
          <mesh position={[-piece.width * 0.24, -piece.height * 0.24, piece.depth * 0.28]} rotation={[Math.PI / 2, 0, 0.3]} castShadow>
            <capsuleGeometry args={[0.024, piece.width * 0.35, 4, 7]} />
            <meshStandardMaterial color="#70442b" roughness={0.96} />
          </mesh>
        )}
      </group>
    )
  }
  if (biome === 'cave') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]}>
        <mesh geometry={geom} castShadow receiveShadow>
          <meshStandardMaterial color={stone} roughness={0.93} flatShading />
        </mesh>
        {piece.variant % 2 === 0 && (
          <mesh position={[piece.width * 0.18, piece.height * 0.48, 0]} rotation={[0.08, piece.tone * Math.PI, 0.12]} castShadow>
            <coneGeometry args={[0.06 + piece.tone * 0.025, 0.28 + piece.tone * 0.12, 5]} />
            <meshStandardMaterial color={soft} emissive={theme.biomeSoft} emissiveIntensity={0.42} roughness={0.28} transparent opacity={0.9} />
          </mesh>
        )}
      </group>
    )
  }
  if (biome === 'lava') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]}>
        <mesh geometry={geom} castShadow receiveShadow>
          <meshStandardMaterial color="#21100d" roughness={0.9} emissive="#ff3d16" emissiveIntensity={0.08} />
        </mesh>
        <mesh position={[0, piece.height * 0.53, 0.002]} rotation={[0, 0, (piece.variant - 4) * 0.05]}>
          <boxGeometry args={[piece.width * 0.54, 0.025, 0.024]} />
          <meshBasicMaterial color={piece.variant % 2 ? '#ffd45c' : '#ff6a45'} toneMapped={false} />
        </mesh>
        {piece.variant % 7 === 0 && (
          <group position={[piece.width * 0.28, piece.height * 0.7, 0]}>
            <FlickerFlame color="#ff7a3c" hot="#ffd45c" reducedMotion={reducedMotion} distance={1.15} intensity={0.22} scale={0.34} />
          </group>
        )}
      </group>
    )
  }
  if (biome === 'ice') {
    return (
      <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]}>
        <mesh geometry={geom} castShadow receiveShadow>
          <meshStandardMaterial color="#b8edf8" emissive={theme.biome} emissiveIntensity={0.08} roughness={0.36} transparent opacity={0.92} />
        </mesh>
        <mesh position={[0, piece.height * 0.54, 0]} scale={[piece.width * 1.45, 0.25, piece.depth * 2.2]} receiveShadow>
          <boxGeometry args={[0.26, 0.045, 0.18]} />
          <meshStandardMaterial color="#f4fbff" roughness={0.52} />
        </mesh>
        {piece.variant % 3 === 0 && (
          <mesh position={[piece.width * 0.18, -piece.height * 0.28, piece.depth * 0.24]} rotation={[Math.PI, 0, 0.04]} castShadow>
            <coneGeometry args={[0.045, 0.25 + piece.tone * 0.16, 6]} />
            <meshStandardMaterial color={theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.14} roughness={0.28} transparent opacity={0.9} />
          </mesh>
        )}
      </group>
    )
  }
  if (biome === 'magical') {
    return (
      <group position={[piece.x, piece.y + piece.tone * 0.025, piece.z]} rotation={[0, piece.rot, 0]}>
        <mesh geometry={geom} castShadow receiveShadow>
          <meshStandardMaterial color={stone} roughness={0.74} emissive={theme.biome} emissiveIntensity={0.18} />
        </mesh>
        <mesh position={[0, piece.height * 0.56, 0]} rotation={[Math.PI / 2, 0, piece.tone * Math.PI]}>
          <torusGeometry args={[Math.min(piece.width, 0.42) * 0.32, 0.009, 6, 28, Math.PI * 1.48]} />
          <meshBasicMaterial color={theme.biomeSoft} transparent opacity={0.62} toneMapped={false} />
        </mesh>
        {piece.variant % 2 === 0 && (
          <mesh position={[piece.width * 0.18, piece.height * 0.78, 0]} rotation={[0.32, piece.tone * Math.PI, 0.24]} castShadow>
            <octahedronGeometry args={[0.065 + piece.tone * 0.03, 0]} />
            <meshStandardMaterial color={piece.variant % 4 === 0 ? '#7de3ff' : soft} emissive={theme.biome} emissiveIntensity={0.55} roughness={0.24} transparent opacity={0.9} />
          </mesh>
        )}
      </group>
    )
  }
  return (
    <group position={[piece.x, piece.y, piece.z]} rotation={[0, piece.rot, 0]}>
      <mesh geometry={geom} castShadow receiveShadow>
        <meshStandardMaterial color={stone} roughness={0.88} metalness={0.04} emissive={theme.biome} emissiveIntensity={0.04} />
      </mesh>
      <mesh position={[0, piece.height * 0.52, piece.depth * 0.1]}>
        <boxGeometry args={[piece.width * 0.74, 0.028, 0.026]} />
        <meshStandardMaterial color="#d0a24c" metalness={0.28} roughness={0.46} />
      </mesh>
      {piece.variant % 5 === 0 && (
        <mesh position={[piece.width * 0.24, piece.height * 0.16, piece.depth * 0.24]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.055, 0.01, 6, 14]} />
          <meshStandardMaterial color="#d0a24c" metalness={0.34} roughness={0.48} />
        </mesh>
      )}
    </group>
  )
}

function PlatformContourDressings({ grid, layout, theme, biome, graphics, reducedMotion }: { grid: string[]; layout: BoardLayout; theme: BiomeTheme; biome: BiomeId; graphics: 'high' | 'medium' | 'low'; reducedMotion: boolean }) {
  const pieces = useMemo(() => buildPlatformContourPieces(grid, layout, biome, graphics), [grid, layout, biome, graphics])
  if (pieces.length === 0) return null
  return (
    <group>
      {pieces.map((piece) => (
        <PlatformContourPiece key={piece.key} piece={piece} theme={theme} biome={biome} reducedMotion={reducedMotion} />
      ))}
    </group>
  )
}

export function buildRimPieces(layout: BoardLayout, biome: BiomeId, graphics: 'high' | 'medium' | 'low'): RimPieceInfo[] {
  if (graphics === 'low') return []
  const pitch = layout.tileSize + layout.gap
  const width = layout.cols * pitch + 1.34
  const depth = layout.rows * pitch + 1.34
  const seed = biome.charCodeAt(0) * 911 + layout.cols * 131 + layout.rows * 313
  const rnd = seeded(seed)
  const high = graphics === 'high'
  const halfWidth = width / 2
  const halfDepth = depth / 2
  const pieces: RimPieceInfo[] = []

  const clusters: { side: RimSide; t: number; count: number; radius: number; scale: number; height: number }[] = [
    { side: 'north', t: 0.14, count: high ? 4 : 2, radius: 0.58, scale: 1.08, height: 1.12 },
    { side: 'north', t: 0.5, count: high ? 4 : 2, radius: 0.62, scale: 1.18, height: 1.18 },
    { side: 'north', t: 0.86, count: high ? 4 : 2, radius: 0.58, scale: 1.08, height: 1.12 },
    { side: 'west', t: 0.22, count: high ? 3 : 1, radius: 0.48, scale: 0.92, height: 0.96 },
    { side: 'west', t: 0.76, count: high ? 3 : 2, radius: 0.5, scale: 0.96, height: 1 },
    { side: 'east', t: 0.2, count: high ? 3 : 2, radius: 0.48, scale: 0.96, height: 1 },
    { side: 'east', t: 0.73, count: high ? 3 : 1, radius: 0.5, scale: 0.92, height: 0.96 },
    { side: 'south', t: 0.24, count: high ? 1 : 0, radius: 0.22, scale: 0.54, height: 0.46 },
    { side: 'south', t: 0.8, count: 0, radius: 0.22, scale: 0.54, height: 0.46 },
  ]

  const push = (side: RimSide, cluster: number, slot: number, count: number, centerT: number, radius: number, scaleBias: number, heightBias: number) => {
    const tone = rnd()
    const spread = count === 1 ? 0 : slot / (count - 1) - 0.5
    const alongSpan = side === 'north' || side === 'south' ? width : depth
    const along = Math.max(0.04, Math.min(0.96, centerT + (spread * radius + (rnd() - 0.5) * radius * 0.32) / alongSpan))
    const offset = 0.08 + rnd() * 0.18
    const sideInset = (rnd() - 0.5) * 0.1
    const x = side === 'west' ? -halfWidth - offset : side === 'east' ? halfWidth + offset : -halfWidth + along * width
    const z = side === 'north' ? -halfDepth - offset : side === 'south' ? halfDepth + offset : -halfDepth + along * depth
    const rot = side === 'north' ? sideInset : side === 'south' ? Math.PI + sideInset : side === 'west' ? Math.PI / 2 + sideInset : -Math.PI / 2 + sideInset
    const biomeHeight = biome === 'forest' || biome === 'ice' ? 0.18 : biome === 'dungeon' ? 0.1 : 0
    pieces.push({
      key: `${side}-${cluster}-${slot}`,
      side,
      shapeKind: rimShapeForBiome(biome),
      cornerRadius: biome === 'forest' ? 0.05 : 0.028,
      x,
      z,
      rot,
      scale: (0.72 + rnd() * 0.3) * scaleBias,
      height: (0.32 + rnd() * 0.38 + biomeHeight) * heightBias,
      tone,
      variant: (cluster * 5 + slot + Math.floor(tone * 10)) % 10,
    })
  }

  clusters.forEach((cluster, clusterIndex) => {
    for (let slot = 0; slot < cluster.count; slot++) {
      push(cluster.side, clusterIndex, slot, cluster.count, cluster.t, cluster.radius, cluster.scale, cluster.height)
    }
  })

  return pieces
}

function RimPiece({ piece, theme, biome, reducedMotion }: { piece: RimPieceInfo; theme: BiomeTheme; biome: BiomeId; reducedMotion: boolean }) {
  const stoneColor = new THREE.Color(theme.surfaceRaised).lerp(new THREE.Color(theme.edge), 0.26 + piece.tone * 0.28).getStyle()
  const softColor = new THREE.Color(theme.biome).lerp(new THREE.Color(theme.biomeSoft), 0.35 + piece.tone * 0.34).getStyle()
  if (biome === 'forest') {
    return (
      <group position={[piece.x, 0, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
        <mesh position={[0, 0.18, 0]} castShadow>
          <cylinderGeometry args={[0.07, 0.12, 0.42, 7]} />
          <meshStandardMaterial color="#70442b" roughness={0.9} />
        </mesh>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[(i - 1) * 0.16, 0.54 + i * 0.05, Math.sin(i + piece.tone) * 0.08]} scale={[1.18, 0.8, 1]} castShadow>
            <sphereGeometry args={[0.23, 14, 10]} />
            <meshStandardMaterial color={i === 1 ? '#8ab85f' : softColor} roughness={0.82} emissive={theme.biome} emissiveIntensity={0.05} />
          </mesh>
        ))}
      </group>
    )
  }
  if (biome === 'cave') {
    return (
      <group position={[piece.x, 0, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
        <mesh position={[0, 0.16, 0]} castShadow receiveShadow>
          <dodecahedronGeometry args={[0.24, 0]} />
          <meshStandardMaterial color={stoneColor} roughness={0.9} flatShading />
        </mesh>
        {piece.variant % 2 === 0 && (
          <group position={[0.04, 0.38, 0.02]}>
            <mesh castShadow>
              <coneGeometry args={[0.1, 0.52, 5]} />
              <meshStandardMaterial color={softColor} emissive={theme.biomeSoft} emissiveIntensity={0.44} roughness={0.28} transparent opacity={0.9} />
            </mesh>
            <PulseLight color={theme.biomeSoft} distance={1.35} intensity={0.22} reducedMotion={reducedMotion} phase={piece.tone * 5} />
          </group>
        )}
      </group>
    )
  }
  if (biome === 'lava') {
    return (
      <group position={[piece.x, 0, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
        <mesh position={[0, 0.18, 0]} castShadow receiveShadow>
          <cylinderGeometry args={[0.22, 0.3, 0.36 + piece.height * 0.22, 7]} />
          <meshStandardMaterial color="#2c201e" roughness={0.9} emissive="#ff3d16" emissiveIntensity={0.1} />
        </mesh>
        <mesh position={[0, 0.39, 0.02]} rotation={[0, 0.4, 0]}>
          <boxGeometry args={[0.36, 0.035, 0.045]} />
          <meshBasicMaterial color={piece.variant % 2 ? '#ffd45c' : '#ff6a45'} toneMapped={false} />
        </mesh>
        {piece.variant % 4 === 0 && <FlickerFlame color="#ff7a3c" hot="#ffd45c" reducedMotion={reducedMotion} distance={1.45} intensity={0.48} scale={0.58} />}
      </group>
    )
  }
  if (biome === 'ice') {
    return (
      <group position={[piece.x, 0, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
        <mesh position={[0, 0.1, 0]} scale={[1.5, 0.36, 1]} receiveShadow>
          <boxGeometry args={[0.34, 0.06, 0.24]} />
          <meshStandardMaterial color="#f4fbff" roughness={0.5} />
        </mesh>
        <mesh position={[0.02, 0.39, 0]} rotation={[0, 0, (piece.variant - 2) * 0.08]} castShadow>
          <coneGeometry args={[0.13, 0.72 + piece.height * 0.18, 6]} />
          <meshStandardMaterial color={piece.variant % 2 ? '#d9f8ff' : theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.2} roughness={0.24} transparent opacity={0.88} />
        </mesh>
      </group>
    )
  }
  if (biome === 'magical') {
    return (
      <group position={[piece.x, 0.06 + Math.sin(piece.tone * Math.PI) * 0.04, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
        <mesh position={[0, 0.18, 0]} rotation={[0.3, piece.tone * Math.PI, 0.2]} castShadow>
          <octahedronGeometry args={[0.2, 0]} />
          <meshStandardMaterial color={piece.variant % 2 ? '#7de3ff' : softColor} emissive={theme.biome} emissiveIntensity={0.55} roughness={0.24} transparent opacity={0.9} />
        </mesh>
        <mesh position={[0, 0.18, 0]} rotation={[Math.PI / 2, 0, piece.tone * Math.PI]}>
          <torusGeometry args={[0.28, 0.01, 6, 30, Math.PI * 1.45]} />
          <meshBasicMaterial color={theme.biomeSoft} transparent opacity={0.55} toneMapped={false} />
        </mesh>
        {piece.variant % 3 === 0 && <PulseLight color={theme.biomeSoft} distance={1.5} intensity={0.28} reducedMotion={reducedMotion} phase={piece.tone * 6} />}
      </group>
    )
  }
  return (
    <group position={[piece.x, 0, piece.z]} rotation={[0, piece.rot, 0]} scale={piece.scale}>
      <mesh position={[0, 0.22 + piece.height * 0.18, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.42, 0.46 + piece.height * 0.36, 0.34]} />
        <meshStandardMaterial color={stoneColor} roughness={0.88} metalness={0.04} emissive="#d0a24c" emissiveIntensity={0.04} />
      </mesh>
      {piece.variant % 4 === 0 && (
        <group position={[0, 0.72, 0.02]}>
          <FlickerFlame color="#ff9a45" reducedMotion={reducedMotion} distance={1.5} intensity={0.48} scale={0.56} />
        </group>
      )}
      {piece.variant % 5 === 0 && (
        <mesh position={[0, 0.42, 0.21]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.16, 0.018, 6, 16]} />
          <meshStandardMaterial color="#d0a24c" metalness={0.35} roughness={0.42} />
        </mesh>
      )}
    </group>
  )
}

function BiomeRim({ layout, theme, biome, graphics, reducedMotion }: { layout: BoardLayout; theme: BiomeTheme; biome: BiomeId; graphics: 'high' | 'medium' | 'low'; reducedMotion: boolean }) {
  const pieces = useMemo(() => buildRimPieces(layout, biome, graphics), [layout, biome, graphics])
  if (pieces.length === 0) return null
  return (
    <group>
      {pieces.map((piece) => (
        <RimPiece key={piece.key} piece={piece} theme={theme} biome={biome} reducedMotion={reducedMotion} />
      ))}
    </group>
  )
}

function DecorProp({ p, theme, biome, reducedMotion }: { p: WorldPropPlacement; theme: BiomeTheme; biome: BiomeId; reducedMotion: boolean }) {
  const color = useMemo(() => {
    const base = new THREE.Color(theme.biome)
    const soft = new THREE.Color(theme.biomeSoft)
    return base.clone().lerp(soft, p.tone)
  }, [theme.biome, theme.biomeSoft, p.tone])

  if (p.kind === 'forest-tree') {
    return (
      <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        <mesh position={[0, 0.34, 0]} castShadow>
          <cylinderGeometry args={[0.1, 0.15, 0.72, 7]} />
          <meshStandardMaterial color="#70442b" roughness={0.86} />
        </mesh>
        {[0, 1, 2, 3].map((i) => {
          const a = (i / 4) * Math.PI * 2 + p.tone
          return (
            <mesh key={i} position={[Math.cos(a) * 0.16, 0.76 + (i % 2) * 0.08, Math.sin(a) * 0.13]} scale={[1.1, 0.82, 1]} castShadow>
              <sphereGeometry args={[0.28, 16, 12]} />
              <meshStandardMaterial color={i % 2 ? theme.biomeSoft : color} emissive={theme.biome} emissiveIntensity={0.08} roughness={0.75} />
            </mesh>
          )
        })}
        {[-1, 1].map((s) => (
          <mesh key={s} position={[s * 0.16, 0.08, 0.08]} rotation={[Math.PI / 2, 0, s * 0.5]}>
            <capsuleGeometry args={[0.035, 0.36, 4, 8]} />
            <meshStandardMaterial color="#5b3925" roughness={0.9} />
          </mesh>
        ))}
      </group>
    )
  }
  if (p.kind === 'fern') {
    return (
      <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        {Array.from({ length: 7 }, (_, i) => {
          const a = -0.75 + i * 0.25
          return (
            <mesh key={i} position={[Math.sin(a) * 0.11, 0.18 + Math.cos(a) * 0.04, Math.cos(a) * 0.08]} rotation={[0.5, 0, a]}>
              <capsuleGeometry args={[0.025, 0.36, 4, 8]} />
              <meshStandardMaterial color={i % 2 ? theme.biomeSoft : color} roughness={0.8} />
            </mesh>
          )
        })}
      </group>
    )
  }
  if (p.kind === 'root') {
    return (
      <group position={[p.x, 0.08, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[(i - 1) * 0.15, 0, Math.sin(i + p.tone) * 0.08]} rotation={[Math.PI / 2, 0, (i - 1) * 0.55]}>
            <capsuleGeometry args={[0.04, 0.46 - i * 0.05, 5, 10]} />
            <meshStandardMaterial color={i === 1 ? '#765037' : '#5b3925'} roughness={0.92} />
          </mesh>
        ))}
      </group>
    )
  }
  if (p.kind === 'mushroom') {
    return (
      <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        {[-1, 0, 1].map((s) => (
          <group key={s} position={[s * 0.13, 0, Math.abs(s) * 0.04]} scale={s === 0 ? 1 : 0.72}>
            <mesh position={[0, 0.13, 0]} castShadow>
              <cylinderGeometry args={[0.045, 0.06, 0.26, 8]} />
              <meshStandardMaterial color="#ead9b6" roughness={0.8} />
            </mesh>
            <mesh position={[0, 0.3, 0]} castShadow>
              <sphereGeometry args={[0.14, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
              <meshStandardMaterial color={s === 0 ? '#ef6a42' : theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.1} roughness={0.62} />
            </mesh>
          </group>
        ))}
      </group>
    )
  }
  if (p.kind === 'cave-stalagmite') {
    return (
      <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        {[0, 1, 2, 3].map((i) => (
          <mesh key={i} position={[(i - 1.5) * 0.13, 0.2 + i * 0.035, Math.sin(i + p.tone) * 0.09]} rotation={[0, 0, (i - 1.5) * 0.1]} castShadow>
            <coneGeometry args={[0.08 + i * 0.015, 0.44 + i * 0.08, 6]} />
            <meshStandardMaterial color={i % 2 ? theme.edge : theme.surfaceRaised} emissive={theme.biome} emissiveIntensity={0.08} roughness={0.82} />
          </mesh>
        ))}
      </group>
    )
  }
  if (p.kind === 'cave-crystal') {
    return (
      <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        <mesh position={[0, 0.32, 0]} castShadow>
          <coneGeometry args={[0.14, 0.7, 5]} />
          <meshStandardMaterial color={color} emissive={theme.biomeSoft} emissiveIntensity={0.35} roughness={0.3} metalness={0.2} transparent opacity={0.92} />
        </mesh>
        <mesh position={[0.16, 0.18, 0.06]} rotation={[0, 0, 0.3]}>
          <coneGeometry args={[0.08, 0.4, 5]} />
          <meshStandardMaterial color={color} emissive={theme.biomeSoft} emissiveIntensity={0.3} roughness={0.3} transparent opacity={0.9} />
        </mesh>
        <PulseLight color={theme.biomeSoft} distance={1.8} intensity={0.42} reducedMotion={reducedMotion} phase={p.tone * 6} />
      </group>
    )
  }
  if (p.kind === 'mineral-pool') {
    return (
      <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        <mesh position={[0, 0.055, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.33, 24]} />
          <meshStandardMaterial color="#55d6ff" emissive="#3bb7ff" emissiveIntensity={0.45} roughness={0.18} transparent opacity={0.76} />
        </mesh>
        <mesh position={[0, 0.062, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.33, 0.39, 24]} />
          <meshStandardMaterial color={theme.edge} roughness={0.72} />
        </mesh>
        <PulseLight color="#55d6ff" distance={1.6} intensity={0.36} reducedMotion={reducedMotion} phase={p.tone * 4} />
      </group>
    )
  }
  if (p.kind === 'lava-vent') {
    return (
      <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        <mesh position={[0, 0.16, 0]} castShadow>
          <cylinderGeometry args={[0.18, 0.24, 0.32, 7]} />
          <meshStandardMaterial color="#271613" roughness={0.86} emissive="#ff451f" emissiveIntensity={0.12} />
        </mesh>
        <group position={[0, 0.33, 0]}>
          <FlickerFlame color="#ff7a3c" hot="#ffd45c" reducedMotion={reducedMotion} distance={1.9} intensity={0.95} scale={0.9} />
        </group>
        {[0, 1].map((i) => (
          <mesh key={i} position={[(i - 0.5) * 0.18, 0.55 + i * 0.08, 0]} rotation={[0.25, i * 0.7, 0.15]} scale={[0.82, 1.28, 0.82]}>
            <octahedronGeometry args={[0.075, 0]} />
            <meshBasicMaterial color="#5a4b4b" transparent opacity={0.22} />
          </mesh>
        ))}
      </group>
    )
  }
  if (p.kind === 'ash-rock') {
    return (
      <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        <mesh position={[0, 0.15, 0]} castShadow receiveShadow>
          <dodecahedronGeometry args={[0.24, 0]} />
          <meshStandardMaterial color="#2b2424" roughness={0.95} metalness={0.02} flatShading emissive="#ff3d1d" emissiveIntensity={0.12} />
        </mesh>
        <mesh position={[0.08, 0.22, 0.12]} rotation={[0.2, 0.4, 0]}>
          <boxGeometry args={[0.24, 0.035, 0.035]} />
          <meshBasicMaterial color="#ff7a3c" toneMapped={false} />
        </mesh>
      </group>
    )
  }
  if (p.kind === 'ember-crack') {
    return (
      <group position={[p.x, 0.055, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[(i - 1) * 0.16, 0, Math.sin(i + p.tone) * 0.05]} rotation={[0, 0, (i - 1) * 0.35]}>
            <boxGeometry args={[0.28, 0.035, 0.045]} />
            <meshBasicMaterial color={i === 1 ? '#ffd45c' : '#ff6a45'} toneMapped={false} />
          </mesh>
        ))}
      </group>
    )
  }
  if (p.kind === 'ice-pine') {
    return (
      <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        <mesh position={[0, 0.2, 0]} castShadow>
          <cylinderGeometry args={[0.055, 0.07, 0.4, 6]} />
          <meshStandardMaterial color="#5f5145" roughness={0.82} />
        </mesh>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[0, 0.43 + i * 0.16, 0]} castShadow>
            <coneGeometry args={[0.28 - i * 0.055, 0.34, 9]} />
            <meshStandardMaterial color={i === 2 ? '#f4fbff' : color} emissive={theme.biome} emissiveIntensity={0.09} roughness={0.58} />
          </mesh>
        ))}
      </group>
    )
  }
  if (p.kind === 'ice-spire') {
    return (
      <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[(i - 1) * 0.13, 0.28 + i * 0.05, Math.cos(i + p.tone) * 0.08]} rotation={[0, 0, (i - 1) * 0.16]} castShadow>
            <coneGeometry args={[0.1 + i * 0.025, 0.58 + i * 0.1, 5]} />
            <meshStandardMaterial color={i === 1 ? '#d9f8ff' : theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.28} roughness={0.22} transparent opacity={0.86} />
          </mesh>
        ))}
      </group>
    )
  }
  if (p.kind === 'snow-drift') {
    return (
      <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[(i - 1) * 0.13, 0.075 + i * 0.015, Math.sin(i + p.tone) * 0.07]} rotation={[0, (i - 1) * 0.18, 0]}>
            <boxGeometry args={[0.36 - i * 0.035, 0.045, 0.22 + i * 0.025]} />
            <meshStandardMaterial color={i === 1 ? '#ffffff' : theme.biomeSoft} roughness={0.45} />
          </mesh>
        ))}
      </group>
    )
  }
  if (p.kind === 'rune-stone') {
    return (
      <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        <mesh position={[0, 0.35, 0]} castShadow>
          <boxGeometry args={[0.28, 0.7, 0.16]} />
          <meshStandardMaterial color={theme.surfaceRaised} emissive={theme.biome} emissiveIntensity={0.16} roughness={0.74} />
        </mesh>
        <mesh position={[0, 0.42, 0.09]} rotation={[0, 0, Math.PI / 4]}>
          <torusGeometry args={[0.09, 0.012, 8, 24]} />
          <meshBasicMaterial color={theme.biomeSoft} toneMapped={false} />
        </mesh>
      </group>
    )
  }
  if (p.kind === 'star-flower') {
    return (
      <group position={[p.x, 0.05, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        {Array.from({ length: 6 }, (_, i) => {
          const a = (i / 6) * Math.PI * 2
          return (
            <mesh key={i} position={[Math.cos(a) * 0.14, 0.12, Math.sin(a) * 0.14]} rotation={[0.28, -a, a]}>
              <boxGeometry args={[0.14, 0.035, 0.055]} />
              <meshStandardMaterial color={i % 2 ? '#7de3ff' : theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.32} roughness={0.35} />
            </mesh>
          )
        })}
        <mesh position={[0, 0.14, 0]} rotation={[0.25, p.tone * Math.PI, 0.2]}>
          <octahedronGeometry args={[0.06, 0]} />
          <meshBasicMaterial color="#ffe68a" toneMapped={false} />
        </mesh>
      </group>
    )
  }
  if (p.kind === 'floating-shard') {
    return (
      <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[(i - 1) * 0.17, 0.36 + i * 0.09, Math.sin(i + p.tone) * 0.08]} rotation={[0.4 + i * 0.2, 0.6, 0.2]}>
            <octahedronGeometry args={[0.12 - i * 0.015, 0]} />
            <meshStandardMaterial color={i === 0 ? theme.biomeSoft : '#7de3ff'} emissive={theme.biome} emissiveIntensity={0.55} roughness={0.2} transparent opacity={0.9} />
          </mesh>
        ))}
        <mesh position={[0, 0.28, 0]} rotation={[Math.PI / 2, 0, p.tone * Math.PI]}>
          <torusGeometry args={[0.32, 0.01, 6, 36, Math.PI * 1.5]} />
          <meshBasicMaterial color={theme.biomeSoft} transparent opacity={0.65} toneMapped={false} />
        </mesh>
        <PulseLight color={theme.biomeSoft} distance={1.9} intensity={0.48} reducedMotion={reducedMotion} phase={p.tone * 5} />
      </group>
    )
  }
  if (p.kind === 'torch') {
    return (
      <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        <mesh position={[0, 0.26, 0]} castShadow>
          <cylinderGeometry args={[0.07, 0.1, 0.52, 8]} />
          <meshStandardMaterial color={theme.edge} roughness={0.8} />
        </mesh>
        <group position={[0, 0.55, 0]}>
          <FlickerFlame color="#ff9a45" reducedMotion={reducedMotion} distance={1.8} intensity={0.78} scale={0.86} />
        </group>
      </group>
    )
  }
  if (p.kind === 'chain-post') {
    return (
      <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        {[-1, 1].map((s) => (
          <mesh key={`post-${s}`} position={[s * 0.18, 0.3, 0]} castShadow>
            <cylinderGeometry args={[0.055, 0.07, 0.6, 8]} />
            <meshStandardMaterial color={theme.edge} metalness={0.2} roughness={0.62} />
          </mesh>
        ))}
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[(i - 1) * 0.12, 0.42 - Math.abs(i - 1) * 0.08, 0]} rotation={[Math.PI / 2, 0, i * Math.PI / 2]}>
            <torusGeometry args={[0.085, 0.016, 6, 14]} />
            <meshStandardMaterial color="#d6a247" metalness={0.45} roughness={0.35} />
          </mesh>
        ))}
      </group>
    )
  }
  if (p.kind === 'gate-fragment') {
    return (
      <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
        {[-1, 0, 1].map((s) => (
          <mesh key={s} position={[s * 0.13, 0.32, 0]} castShadow>
            <boxGeometry args={[0.07, 0.64 - Math.abs(s) * 0.12, 0.08]} />
            <meshStandardMaterial color="#9f7434" metalness={0.25} roughness={0.5} />
          </mesh>
        ))}
        <mesh position={[0, 0.48, 0]}>
          <boxGeometry args={[0.42, 0.06, 0.09]} />
          <meshStandardMaterial color="#d0a24c" metalness={0.3} roughness={0.42} />
        </mesh>
      </group>
    )
  }
  return (
    <group position={[p.x, 0, p.z]} rotation={[0, p.rot, 0]} scale={p.scale}>
      <mesh position={[0, 0.16, 0]} castShadow receiveShadow>
        <dodecahedronGeometry args={[0.24, 0]} />
        <meshStandardMaterial color={biome === 'dungeon' ? theme.edge : color} roughness={0.95} metalness={0.02} flatShading />
      </mesh>
    </group>
  )
}

function TerrainAccent({ p, theme, biome, tileSize }: { p: WorldcraftTerrainPiece; theme: BiomeTheme; biome: BiomeId; tileSize: number }) {
  const x = (p.variant - 1.5) * tileSize * 0.12
  const z = tileSize * (p.variant % 2 === 0 ? 0.17 : -0.17)
  if (biome === 'forest') {
    return (
      <group position={[x, 0.13, z]} rotation={[0, p.tone * Math.PI * 2, 0]}>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[(i - 1) * 0.045, 0.05 + i * 0.018, Math.sin(i + p.tone) * 0.035]} rotation={[0.35, 0, (i - 1) * 0.45]}>
            <capsuleGeometry args={[tileSize * 0.012, tileSize * 0.18, 4, 6]} />
            <meshStandardMaterial color={i === 1 ? theme.biomeSoft : theme.biome} roughness={0.8} />
          </mesh>
        ))}
      </group>
    )
  }
  if (biome === 'cave') {
    return (
      <group position={[x, 0.14, z]} rotation={[0, p.tone * Math.PI * 2, 0]}>
        <mesh>
          <coneGeometry args={[tileSize * 0.045, tileSize * 0.22, 5]} />
          <meshStandardMaterial color={theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.34} roughness={0.28} transparent opacity={0.88} />
        </mesh>
        <mesh position={[tileSize * 0.07, -0.035, tileSize * 0.03]}>
          <dodecahedronGeometry args={[tileSize * 0.045, 0]} />
          <meshStandardMaterial color={theme.edge} roughness={0.82} />
        </mesh>
      </group>
    )
  }
  if (biome === 'lava') {
    return (
      <group position={[x, 0.13, z]} rotation={[0, p.tone * Math.PI * 2, 0]}>
        <mesh>
          <boxGeometry args={[tileSize * 0.28, tileSize * 0.028, tileSize * 0.035]} />
          <meshBasicMaterial color="#ff7a3c" toneMapped={false} />
        </mesh>
        <mesh position={[tileSize * 0.12, 0.012, tileSize * 0.03]} rotation={[0, 0.4, 0]}>
          <boxGeometry args={[tileSize * 0.15, tileSize * 0.024, tileSize * 0.03]} />
          <meshBasicMaterial color="#ffd45c" toneMapped={false} />
        </mesh>
      </group>
    )
  }
  if (biome === 'ice') {
    return (
      <group position={[x, 0.12, z]} rotation={[0, p.tone * Math.PI * 2, 0]}>
        <mesh rotation={[0, p.variant * 0.2, 0]}>
          <boxGeometry args={[tileSize * 0.26, tileSize * 0.045, tileSize * 0.16]} />
          <meshStandardMaterial color="#f4fbff" roughness={0.42} />
        </mesh>
        <mesh position={[tileSize * 0.08, 0.08, 0]}>
          <coneGeometry args={[tileSize * 0.045, tileSize * 0.2, 5]} />
          <meshStandardMaterial color={theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.2} roughness={0.22} transparent opacity={0.9} />
        </mesh>
      </group>
    )
  }
  if (biome === 'magical') {
    return (
      <group position={[x, 0.16, z]} rotation={[0, p.tone * Math.PI * 2, 0]}>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[tileSize * 0.11, tileSize * 0.008, 6, 26, Math.PI * 1.45]} />
          <meshBasicMaterial color={theme.biomeSoft} transparent opacity={0.72} toneMapped={false} />
        </mesh>
        <mesh position={[0, tileSize * 0.06, 0]}>
          <octahedronGeometry args={[tileSize * 0.055, 0]} />
          <meshStandardMaterial color="#7de3ff" emissive={theme.biome} emissiveIntensity={0.5} roughness={0.25} />
        </mesh>
      </group>
    )
  }
  return (
    <group position={[x, 0.13, z]} rotation={[0, p.tone * Math.PI * 2, 0]}>
      <mesh>
        <boxGeometry args={[tileSize * 0.15, tileSize * 0.06, tileSize * 0.12]} />
        <meshStandardMaterial color={theme.edge} roughness={0.82} />
      </mesh>
      <mesh position={[tileSize * 0.09, tileSize * 0.05, 0]} rotation={[0.2, p.tone * Math.PI, 0.1]}>
        <octahedronGeometry args={[tileSize * 0.038, 0]} />
        <meshBasicMaterial color="#ffb454" toneMapped={false} />
      </mesh>
    </group>
  )
}

function TerrainPiece({ p, theme, biome, tileSize, graphics }: { p: WorldcraftTerrainPiece; theme: BiomeTheme; biome: BiomeId; tileSize: number; graphics: 'high' | 'medium' | 'low' }) {
  const color = useMemo(() => {
    const base = new THREE.Color(theme.surfaceRaised)
    const biome = new THREE.Color(theme.biome)
    const soft = new THREE.Color(theme.biomeSoft)
    return base.lerp(biome, 0.18 + p.tone * 0.22).lerp(soft, p.cornerCase === 'full' ? 0.06 : 0.16)
  }, [p.cornerCase, p.tone, theme.biome, theme.biomeSoft, theme.surfaceRaised])
  const terrainShape = terrainCornerShapeForBiome(biome)
  const fullGeom = useMemo(() => makeRoundedBox(tileSize * 0.86, 0.16, tileSize * 0.86, 0.14, 3), [tileSize])
  const cornerGeom = useMemo(() => {
    if (terrainShape === 'organic-mound') return null
    if (terrainShape === 'ice-slab') return makeRoundedBox(tileSize * 0.46, 0.09, tileSize * 0.28, 0.016, 2)
    if (terrainShape === 'crystal-block') return makeRoundedBox(tileSize * 0.34, 0.17, tileSize * 0.34, 0.018, 2)
    if (terrainShape === 'basalt-block') return makeRoundedBox(tileSize * 0.38, 0.17, tileSize * 0.32, 0.018, 2)
    if (terrainShape === 'rune-block') return makeRoundedBox(tileSize * 0.34, 0.16, tileSize * 0.34, 0.018, 2)
    return makeRoundedBox(tileSize * 0.4, 0.15, tileSize * 0.32, 0.018, 2)
  }, [terrainShape, tileSize])
  const bridgeGeomX = useMemo(() => makeRoundedBox(tileSize * 0.58, 0.12, tileSize * 0.22, 0.06, 2), [tileSize])
  const bridgeGeomZ = useMemo(() => makeRoundedBox(tileSize * 0.22, 0.12, tileSize * 0.58, 0.06, 2), [tileSize])
  const d = tileSize * 0.25
  const bits = [
    { bit: 1, x: -d, z: -d },
    { bit: 2, x: d, z: -d },
    { bit: 4, x: d, z: d },
    { bit: 8, x: -d, z: d },
  ].filter((b) => p.mask & b.bit)
  const has = (a: number, b: number) => (p.mask & a) && (p.mask & b)

  return (
    <group position={[p.x + p.jitterX, -0.205 + p.lift, p.z + p.jitterZ]} rotation={[0, p.rot, 0]} scale={[p.scaleX, 1, p.scaleZ]}>
      {p.mask === 15 ? (
        <mesh geometry={fullGeom} receiveShadow>
          <meshStandardMaterial color={color} roughness={0.92} metalness={0.03} />
        </mesh>
      ) : (
        <>
          {bits.map((b) => (
            <mesh key={b.bit} geometry={cornerGeom ?? undefined} position={[b.x, 0.02, b.z]} rotation={[0, (b.bit + p.variant) * 0.31, 0]} castShadow receiveShadow>
              {terrainShape === 'organic-mound' && <sphereGeometry args={[tileSize * 0.24, 14, 8]} />}
              <meshStandardMaterial color={color} roughness={0.9} metalness={0.03} />
            </mesh>
          ))}
          {has(1, 2) && (
            <mesh geometry={bridgeGeomX} position={[0, 0.02, -d]} receiveShadow>
              <meshStandardMaterial color={color} roughness={0.9} />
            </mesh>
          )}
          {has(4, 8) && (
            <mesh geometry={bridgeGeomX} position={[0, 0.02, d]} receiveShadow>
              <meshStandardMaterial color={color} roughness={0.9} />
            </mesh>
          )}
          {has(2, 4) && (
            <mesh geometry={bridgeGeomZ} position={[d, 0.02, 0]} receiveShadow>
              <meshStandardMaterial color={color} roughness={0.9} />
            </mesh>
          )}
          {has(1, 8) && (
            <mesh geometry={bridgeGeomZ} position={[-d, 0.02, 0]} receiveShadow>
              <meshStandardMaterial color={color} roughness={0.9} />
            </mesh>
          )}
          {p.cornerCase === 'diagonal' && (
            <mesh position={[0, 0.035, 0]} castShadow receiveShadow>
              <dodecahedronGeometry args={[tileSize * 0.16, 0]} />
              <meshStandardMaterial color={color} roughness={0.86} metalness={0.05} />
            </mesh>
          )}
        </>
      )}
      {graphics === 'high' && p.variant > 0 && (
        <TerrainAccent p={p} theme={theme} biome={biome} tileSize={tileSize} />
      )}
    </group>
  )
}

function MotifPiece({ motif, theme, biome, emberColor, reducedMotion }: { motif: WorldcraftMotif; theme: BiomeTheme; biome: BiomeId; emberColor: string; reducedMotion: boolean }) {
  const hot = motif.tile === 'l' ? '#ff7a3c' : motif.tile === '~' ? '#5fd6ff' : motif.tile === 'm' ? '#bf76ff' : theme.biomeSoft
  if (motif.kind === 'start-ripple') {
    return (
      <group position={[motif.x, 0.035, motif.z]} rotation={[-Math.PI / 2, 0, motif.rot]} scale={motif.scale}>
        <mesh>
          <ringGeometry args={[0.36, 0.42, 32]} />
          <meshBasicMaterial color={theme.biomeSoft} transparent opacity={0.34} side={THREE.DoubleSide} />
        </mesh>
        <mesh>
          <ringGeometry args={[0.52, 0.56, 32]} />
          <meshBasicMaterial color={theme.biome} transparent opacity={0.2} side={THREE.DoubleSide} />
        </mesh>
      </group>
    )
  }
  if (motif.kind === 'home-grove') {
    if (biome === 'forest') {
      return (
        <group position={[motif.x, 0.04, motif.z]} rotation={[0, motif.rot, 0]} scale={motif.scale}>
          {[0, 1, 2, 3].map((i) => {
            const a = (i / 4) * Math.PI * 2
            return (
              <group key={i} position={[Math.cos(a) * 0.48, 0, Math.sin(a) * 0.48]} scale={0.72}>
                <mesh position={[0, 0.2, 0]} castShadow>
                  <cylinderGeometry args={[0.045, 0.065, 0.4, 6]} />
                  <meshStandardMaterial color="#70442b" roughness={0.86} />
                </mesh>
                <mesh position={[0, 0.48, 0]} castShadow>
                  <sphereGeometry args={[0.18, 12, 8]} />
                  <meshStandardMaterial color={theme.biomeSoft} emissive={emberColor} emissiveIntensity={0.12} roughness={0.7} />
                </mesh>
              </group>
            )
          })}
        </group>
      )
    }
    if (biome === 'ice') {
      return (
        <group position={[motif.x, 0.04, motif.z]} rotation={[0, motif.rot, 0]} scale={motif.scale}>
          {[0, 1, 2, 3].map((i) => {
            const a = (i / 4) * Math.PI * 2
            return (
              <mesh key={i} position={[Math.cos(a) * 0.43, 0.28, Math.sin(a) * 0.43]} castShadow>
                <coneGeometry args={[0.12, 0.58, 6]} />
                <meshStandardMaterial color={i % 2 ? '#f4fbff' : theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.12} roughness={0.45} />
              </mesh>
            )
          })}
        </group>
      )
    }
    if (biome === 'lava') {
      return (
        <group position={[motif.x, 0.05, motif.z]} rotation={[0, motif.rot, 0]} scale={motif.scale}>
          {[0, 1, 2, 3].map((i) => {
            const a = (i / 4) * Math.PI * 2
            return (
              <group key={i} position={[Math.cos(a) * 0.42, 0, Math.sin(a) * 0.42]}>
                <mesh position={[0, 0.16, 0]} castShadow>
                  <cylinderGeometry args={[0.07, 0.1, 0.32, 7]} />
                  <meshStandardMaterial color="#261210" roughness={0.8} emissive="#ff6a45" emissiveIntensity={0.14} />
                </mesh>
                <group position={[0, 0.32, 0]}>
                  <FlickerFlame color="#ff9a45" hot="#ffd45c" reducedMotion={reducedMotion} distance={1.5} intensity={0.65} scale={0.72} />
                </group>
              </group>
            )
          })}
        </group>
      )
    }
    if (biome === 'magical') {
      return (
        <group position={[motif.x, 0.08, motif.z]} rotation={[0, motif.rot, 0]} scale={motif.scale}>
          {[0, 1, 2, 3, 4].map((i) => {
            const a = (i / 5) * Math.PI * 2
            return (
              <mesh key={i} position={[Math.cos(a) * 0.46, 0.2 + (i % 2) * 0.08, Math.sin(a) * 0.46]} rotation={[0.3, a, 0.2]}>
                <octahedronGeometry args={[0.09, 0]} />
                <meshStandardMaterial color={i % 2 ? '#7de3ff' : theme.biomeSoft} emissive={theme.biome} emissiveIntensity={0.58} roughness={0.22} />
              </mesh>
            )
          })}
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.44, 0.014, 8, 42, Math.PI * 1.7]} />
            <meshBasicMaterial color={theme.biomeSoft} transparent opacity={0.58} toneMapped={false} />
          </mesh>
        </group>
      )
    }
    if (biome === 'dungeon') {
      return (
        <group position={[motif.x, 0.05, motif.z]} rotation={[0, motif.rot, 0]} scale={motif.scale}>
          {[-1, 1].map((s) => (
            <group key={s} position={[s * 0.36, 0, 0]}>
              <mesh position={[0, 0.28, 0]} castShadow>
                <cylinderGeometry args={[0.07, 0.09, 0.56, 8]} />
                <meshStandardMaterial color={theme.edge} roughness={0.78} />
              </mesh>
              <group position={[0, 0.55, 0]}>
                <FlickerFlame color="#ff9a45" reducedMotion={reducedMotion} distance={1.45} intensity={0.62} scale={0.7} />
              </group>
            </group>
          ))}
        </group>
      )
    }
    return (
      <group position={[motif.x, 0.06, motif.z]} rotation={[0, motif.rot, 0]} scale={motif.scale}>
        {[0, 1, 2, 3, 4].map((i) => {
          const a = (i / 5) * Math.PI * 2
          return (
            <mesh key={i} position={[Math.cos(a) * 0.48, 0.16, Math.sin(a) * 0.48]} castShadow>
              <coneGeometry args={[0.06, 0.42, 7]} />
              <meshStandardMaterial color={theme.biomeSoft} emissive={emberColor} emissiveIntensity={0.18} roughness={0.7} />
            </mesh>
          )
        })}
      </group>
    )
  }
  if (motif.kind === 'hazard-basin') {
    return (
      <group position={[motif.x, 0.08, motif.z]} rotation={[0, motif.rot, 0]} scale={motif.scale}>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[(i - 1) * 0.22, 0.12 + i * 0.025, Math.sin(i + motif.tone) * 0.1]} castShadow>
            <coneGeometry args={[0.08, 0.34, 6]} />
            <meshStandardMaterial color={hot} emissive={hot} emissiveIntensity={0.36} roughness={0.42} />
          </mesh>
        ))}
      </group>
    )
  }
  if (motif.kind === 'treasure-pocket') {
    return (
      <group position={[motif.x, 0.15, motif.z]} rotation={[0, motif.rot, 0]} scale={motif.scale}>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.13, 0.13, 0.04, 18]} />
          <meshStandardMaterial color="#ffcf5c" emissive="#ffcf5c" emissiveIntensity={0.22} metalness={0.3} roughness={0.36} />
        </mesh>
        <mesh position={[0.2, 0.05, 0.02]}>
          <octahedronGeometry args={[0.11, 0]} />
          <meshStandardMaterial color="#7de3ff" emissive="#7de3ff" emissiveIntensity={0.35} roughness={0.24} />
        </mesh>
      </group>
    )
  }
  if (motif.kind === 'gatework') {
    return (
      <group position={[motif.x, 0.06, motif.z]} rotation={[0, motif.rot, 0]} scale={motif.scale}>
        {[-1, 1].map((s) => (
          <mesh key={s} position={[s * 0.28, 0.24, 0]} castShadow receiveShadow>
            <cylinderGeometry args={[0.08, 0.1, 0.5, 8]} />
            <meshStandardMaterial color={theme.edge} emissive={theme.biome} emissiveIntensity={0.1} roughness={0.8} />
          </mesh>
        ))}
      </group>
    )
  }
  return (
    <group position={[motif.x, 0.14, motif.z]} rotation={[0, motif.rot, 0]} scale={motif.scale}>
      {[0, 1, 2].map((i) => (
        <mesh key={i} position={[(i - 1) * 0.18, Math.sin(i + motif.tone) * 0.03, Math.cos(i) * 0.14]} castShadow>
          <icosahedronGeometry args={[0.11 + i * 0.025, 1]} />
          <meshStandardMaterial color="#bf76ff" emissive="#7f38d8" emissiveIntensity={0.55} roughness={0.5} transparent opacity={0.8} />
        </mesh>
      ))}
    </group>
  )
}

function StageFloorAccent({ accent, theme }: { accent: StageFloorAccentInfo; theme: BiomeTheme }) {
  const glow = new THREE.Color(theme.biomeSoft).lerp(new THREE.Color('#ffffff'), 0.08 + accent.tone * 0.12).getStyle()
  const muted = new THREE.Color(theme.surfaceSunken).lerp(new THREE.Color(theme.biome), 0.24 + accent.tone * 0.22).getStyle()
  const y = -0.245
  if (accent.kind === 'forest-leaf-root') {
    return (
      <group position={[accent.x, y, accent.z]} rotation={[0, accent.rot, 0]} scale={accent.scale}>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[(i - 1) * accent.width * 0.34, 0.012 + i * 0.002, Math.sin(i + accent.tone) * accent.depth * 0.36]} rotation={[0, (i - 1) * 0.42, 0]} scale={[accent.width * (1.1 - i * 0.12), 0.028, accent.depth * (0.82 + i * 0.08)]} receiveShadow>
            <sphereGeometry args={[1, 8, 5]} />
            <meshStandardMaterial color={i === 1 ? '#7ea85a' : muted} roughness={0.96} transparent opacity={accent.opacity} />
          </mesh>
        ))}
        <mesh position={[0, 0.018, 0]} rotation={[0, 0.18, 0]}>
          <boxGeometry args={[accent.width * 1.35, 0.018, accent.depth * 0.18]} />
          <meshStandardMaterial color="#4f3825" roughness={0.98} transparent opacity={accent.opacity * 0.75} />
        </mesh>
      </group>
    )
  }
  if (accent.kind === 'cave-crystal-glint') {
    return (
      <group position={[accent.x, y, accent.z]} rotation={[0, accent.rot, 0]} scale={accent.scale}>
        <mesh rotation={[-Math.PI / 2, 0, 0]} scale={[accent.width * 1.4, accent.depth * 1.35, 1]} renderOrder={-34}>
          <circleGeometry args={[1, 24]} />
          <meshBasicMaterial color={theme.biome} map={getSoftDiscTexture() ?? undefined} transparent opacity={accent.opacity * 0.22} depthWrite={false} toneMapped={false} />
        </mesh>
        {[0, 1].map((i) => (
          <mesh key={i} position={[(i - 0.5) * accent.width * 0.34, 0.07 + i * 0.022, (i - 0.5) * accent.depth * 0.28]} rotation={[0.08, (i - 0.5) * 0.34, 0]} castShadow>
            <coneGeometry args={[0.035 + i * 0.012, 0.16 + i * 0.05, 5]} />
            <meshStandardMaterial color={glow} emissive={theme.biomeSoft} emissiveIntensity={0.42} roughness={0.34} transparent opacity={accent.opacity} />
          </mesh>
        ))}
      </group>
    )
  }
  if (accent.kind === 'lava-ember-crack') {
    return (
      <group position={[accent.x, y, accent.z]} rotation={[0, accent.rot, 0]} scale={accent.scale}>
        <mesh position={[0, 0.012, 0]} rotation={[0, 0.12, 0]}>
          <boxGeometry args={[accent.width, 0.018, accent.depth]} />
          <meshBasicMaterial color={accent.variant % 2 ? '#ffd45c' : '#ff6a2f'} transparent opacity={accent.opacity} toneMapped={false} />
        </mesh>
        <mesh position={[accent.width * 0.18, 0.014, accent.depth * 0.9]} rotation={[0, 0.86, 0]}>
          <boxGeometry args={[accent.width * 0.54, 0.014, accent.depth * 0.72]} />
          <meshBasicMaterial color="#ff3d16" transparent opacity={accent.opacity * 0.7} toneMapped={false} />
        </mesh>
      </group>
    )
  }
  if (accent.kind === 'ice-snow-shard') {
    return (
      <group position={[accent.x, y, accent.z]} rotation={[0, accent.rot, 0]} scale={accent.scale}>
        <mesh rotation={[-Math.PI / 2, 0, 0]} scale={[accent.width * 1.35, accent.depth * 1.08, 1]} renderOrder={-34}>
          <circleGeometry args={[1, 18]} />
          <meshBasicMaterial color="#f4fbff" map={getSoftDiscTexture() ?? undefined} transparent opacity={accent.opacity * 0.48} depthWrite={false} />
        </mesh>
        <mesh position={[0.02, 0.065, 0]} rotation={[0.12, accent.tone * 1.2, -0.08]} castShadow>
          <coneGeometry args={[0.042, 0.16, 6]} />
          <meshStandardMaterial color={glow} emissive={theme.biome} emissiveIntensity={0.18} roughness={0.28} transparent opacity={accent.opacity * 0.88} />
        </mesh>
      </group>
    )
  }
  if (accent.kind === 'magical-rune-spark') {
    return (
      <group position={[accent.x, y, accent.z]} rotation={[0, accent.rot, 0]} scale={accent.scale}>
        <mesh position={[0, 0.012, 0]} rotation={[Math.PI / 2, 0, accent.tone * Math.PI]} renderOrder={-34}>
          <torusGeometry args={[accent.width * 0.5, 0.008, 6, 24, Math.PI * 1.35]} />
          <meshBasicMaterial color={glow} transparent opacity={accent.opacity} toneMapped={false} depthWrite={false} />
        </mesh>
        <mesh position={[0, 0.014, 0]} rotation={[0, 0.32, 0]}>
          <boxGeometry args={[accent.width * 0.75, 0.012, accent.depth * 0.18]} />
          <meshBasicMaterial color={theme.biome} transparent opacity={accent.opacity * 0.78} toneMapped={false} />
        </mesh>
      </group>
    )
  }
  return (
    <group position={[accent.x, y, accent.z]} rotation={[0, accent.rot, 0]} scale={accent.scale}>
      {[0, 1, 2].map((i) => (
        <mesh key={i} position={[(i - 1) * accent.width * 0.28, 0.018 + i * 0.002, Math.cos(i + accent.tone) * accent.depth * 0.32]} rotation={[0, (i - 1) * 0.3, 0]} castShadow receiveShadow>
          <boxGeometry args={[accent.width * (0.38 + i * 0.08), 0.045, accent.depth * (0.44 + i * 0.08)]} />
          <meshStandardMaterial color={i === 1 ? '#d0a24c' : muted} metalness={i === 1 ? 0.2 : 0.02} roughness={i === 1 ? 0.52 : 0.9} emissive={i === 1 ? '#ffb84c' : '#000000'} emissiveIntensity={i === 1 ? 0.28 : 0} transparent opacity={accent.opacity} />
        </mesh>
      ))}
    </group>
  )
}

function ClusterFlame({ color, hot, reducedMotion, lit, scale }: { color: string; hot: string; reducedMotion: boolean; lit: boolean; scale: number }) {
  const groupRef = useRef<THREE.Group>(null)
  const lightRef = useRef<THREE.PointLight>(null)
  useFrame((state) => {
    if (reducedMotion) return
    const pulse = 0.84 + Math.sin(state.clock.elapsedTime * 7.4) * 0.1 + Math.sin(state.clock.elapsedTime * 12.1) * 0.04
    if (groupRef.current) groupRef.current.scale.set(scale * (0.94 + pulse * 0.06), scale * (0.92 + pulse * 0.14), scale * (0.94 + pulse * 0.06))
    if (lightRef.current) lightRef.current.intensity = 0.38 * pulse
  })
  return (
    <group ref={groupRef} scale={scale}>
      <mesh>
        <coneGeometry args={[0.075, 0.2, 10]} />
        <meshBasicMaterial color={color} transparent opacity={0.9} toneMapped={false} />
      </mesh>
      <mesh position={[0, 0.025, 0]} scale={[0.56, 0.7, 0.56]}>
        <coneGeometry args={[0.068, 0.18, 10]} />
        <meshBasicMaterial color={hot} transparent opacity={0.82} toneMapped={false} />
      </mesh>
      {lit && <pointLight ref={lightRef} color={color} distance={1.35} intensity={0.36} />}
    </group>
  )
}

function BiomeSetDressingCluster({ cluster, theme, reducedMotion, graphics }: { cluster: BiomeSetDressingClusterInfo; theme: BiomeTheme; reducedMotion: boolean; graphics: 'high' | 'medium' | 'low' }) {
  const base = new THREE.Color(theme.surfaceRaised).lerp(new THREE.Color(theme.biome), 0.18 + cluster.tone * 0.28).getStyle()
  const glow = new THREE.Color(theme.biomeSoft).lerp(new THREE.Color('#ffffff'), 0.05 + cluster.tone * 0.16).getStyle()
  const dark = new THREE.Color(theme.surfaceSunken).lerp(new THREE.Color(theme.ink), 0.26).getStyle()
  const high = graphics === 'high'
  if (cluster.kind === 'forest-grove-cluster') {
    return (
      <group position={[cluster.x, -0.02, cluster.z]} rotation={[0, cluster.rot, 0]} scale={cluster.scale}>
        {[-0.32, 0.08, 0.34].map((offset, i) => (
          <group key={i} position={[offset * cluster.width, 0, Math.sin(i + cluster.tone) * cluster.depth * 0.24]} scale={0.82 + i * 0.08}>
            <mesh position={[0, cluster.height * 0.28, 0]} castShadow>
              <cylinderGeometry args={[0.055, 0.085, cluster.height * 0.54, 7]} />
              <meshStandardMaterial color="#5b3925" roughness={0.94} />
            </mesh>
            {Array.from({ length: 5 }, (_, j) => (
              <mesh key={j} position={[(j - 2) * 0.078, cluster.height * (0.52 + (j % 3) * 0.06), Math.cos(j + cluster.tone) * 0.08]} rotation={[0.08, j * 0.36, (j - 2) * 0.04]} scale={[0.9 + (j % 2) * 0.14, 0.72, 0.84]} castShadow>
                <dodecahedronGeometry args={[0.14 + (j === 2 ? 0.04 : 0), 0]} />
                <meshStandardMaterial color={j === 1 || j === 3 ? '#8ac95f' : base} emissive={theme.biome} emissiveIntensity={0.025} roughness={0.98} flatShading />
              </mesh>
            ))}
            {[0, 1].map((j) => (
              <mesh key={`root-${j}`} position={[(j - 0.5) * 0.12, 0.07, cluster.depth * 0.08]} rotation={[Math.PI / 2, 0, (j - 0.5) * 0.62]} castShadow>
                <capsuleGeometry args={[0.02, 0.26, 4, 7]} />
                <meshStandardMaterial color={j ? '#70442b' : '#51351f'} roughness={0.98} />
              </mesh>
            ))}
          </group>
        ))}
        {[0, 1, 2, 3].map((i) => (
          <mesh key={`fern-${i}`} position={[(i - 1.5) * cluster.width * 0.18, 0.075, cluster.depth * (0.26 + Math.sin(i) * 0.12)]} rotation={[0.2, i * 0.8, 0.4]} scale={[0.08, 0.26, 0.035]}>
            <dodecahedronGeometry args={[1, 0]} />
            <meshStandardMaterial color={i % 2 ? '#6aa64c' : theme.biomeSoft} roughness={0.96} />
          </mesh>
        ))}
        {cluster.variant % 2 === 0 && (
          <mesh position={[-cluster.width * 0.36, 0.12, -cluster.depth * 0.24]} castShadow>
            <sphereGeometry args={[0.09, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color="#ef6a42" roughness={0.62} />
          </mesh>
        )}
      </group>
    )
  }
  if (cluster.kind === 'cave-crystal-cluster') {
    return (
      <group position={[cluster.x, 0, cluster.z]} rotation={[0, cluster.rot, 0]} scale={cluster.scale}>
        <mesh position={[0, 0.035, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[cluster.width * 0.56, cluster.depth * 0.48, 1]}>
          <circleGeometry args={[1, 24]} />
          <meshStandardMaterial color="#55d6ff" emissive="#3bb7ff" emissiveIntensity={0.32} roughness={0.18} transparent opacity={0.5} />
        </mesh>
        {Array.from({ length: 5 }, (_, i) => (
          <mesh key={i} position={[(i - 2) * cluster.width * 0.12, cluster.height * (0.22 + i * 0.035), Math.sin(i + cluster.tone) * cluster.depth * 0.18]} rotation={[0.08, i * 0.36, (i - 2) * 0.11]} scale={[0.68 + (i % 2) * 0.16, cluster.height * (1.8 + i * 0.22), 0.68]} castShadow>
            <octahedronGeometry args={[0.08 + i * 0.01, 0]} />
            <meshStandardMaterial color={i % 2 ? glow : base} emissive={theme.biomeSoft} emissiveIntensity={0.28 + i * 0.05} roughness={0.32} transparent opacity={0.9} flatShading />
          </mesh>
        ))}
        {high && cluster.variant % 3 === 0 && <PulseLight color={theme.biomeSoft} distance={1.55} intensity={0.24} reducedMotion={reducedMotion} phase={cluster.tone * 6} />}
      </group>
    )
  }
  if (cluster.kind === 'lava-vent-cluster') {
    return (
      <group position={[cluster.x, 0, cluster.z]} rotation={[0, cluster.rot, 0]} scale={cluster.scale}>
        {[0, 1, 2].map((i) => (
          <group key={i} position={[(i - 1) * cluster.width * 0.18, 0, Math.sin(i + cluster.tone) * cluster.depth * 0.2]}>
            {[0, 1, 2].map((band) => (
              <mesh key={band} position={[band % 2 ? 0.02 : -0.01, cluster.height * (0.1 + band * 0.105 + i * 0.018), 0]} rotation={[0, i * 0.3 + band * 0.18, 0]} castShadow receiveShadow>
                <cylinderGeometry args={[0.13 + i * 0.018, 0.18 + i * 0.016, cluster.height * 0.16, 7]} />
                <meshStandardMaterial color={i === 1 ? '#2f2725' : dark} roughness={0.93} emissive="#ff3d16" emissiveIntensity={0.09 + band * 0.035} flatShading />
              </mesh>
            ))}
          </group>
        ))}
        {[0, 1, 2].map((i) => (
          <mesh key={`crack-${i}`} position={[(i - 1) * cluster.width * 0.18, 0.075, cluster.depth * (0.12 - i * 0.12)]} rotation={[0, i * 0.42, 0]}>
            <boxGeometry args={[cluster.width * (0.28 + i * 0.04), 0.026, 0.04]} />
            <meshBasicMaterial color={i === 1 ? '#ffd45c' : '#ff6a45'} transparent opacity={0.86} toneMapped={false} />
          </mesh>
        ))}
        <group position={[0, cluster.height * 0.42, 0]}>
          <ClusterFlame color="#ff7a3c" hot="#ffd45c" reducedMotion={reducedMotion} lit={high && cluster.variant % 2 === 0} scale={0.82} />
        </group>
      </group>
    )
  }
  if (cluster.kind === 'ice-pine-cluster') {
    return (
      <group position={[cluster.x, 0, cluster.z]} rotation={[0, cluster.rot, 0]} scale={cluster.scale}>
        {[0, 1].map((i) => <LandmarkPine key={i} x={(i - 0.5) * cluster.width * 0.34} z={Math.sin(i + cluster.tone) * cluster.depth * 0.2} scale={0.86 - i * 0.08} color={i % 2 ? '#8fe8ef' : base} snow />)}
        <mesh position={[cluster.width * 0.2, 0.24, -cluster.depth * 0.22]} rotation={[0.1, cluster.tone * 1.8, -0.08]} castShadow>
          <coneGeometry args={[0.09, cluster.height * 0.58, 6]} />
          <meshStandardMaterial color={glow} emissive={theme.biome} emissiveIntensity={0.18} roughness={0.24} transparent opacity={0.84} />
        </mesh>
        <mesh position={[-cluster.width * 0.2, 0.045, cluster.depth * 0.26]} rotation={[-Math.PI / 2, 0, 0]} scale={[cluster.width * 0.34, cluster.depth * 0.24, 1]}>
          <circleGeometry args={[1, 18]} />
          <meshBasicMaterial color="#f4fbff" map={getSoftDiscTexture() ?? undefined} transparent opacity={0.44} depthWrite={false} />
        </mesh>
      </group>
    )
  }
  if (cluster.kind === 'magical-rune-garden') {
    return (
      <group position={[cluster.x, 0.02, cluster.z]} rotation={[0, cluster.rot, 0]} scale={cluster.scale}>
        <mesh position={[0, 0.055, 0]} rotation={[Math.PI / 2, 0, cluster.tone * Math.PI]}>
          <torusGeometry args={[cluster.width * 0.24, 0.012, 8, 36, Math.PI * 1.55]} />
          <meshBasicMaterial color={glow} transparent opacity={0.72} toneMapped={false} depthWrite={false} />
        </mesh>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[(i - 1) * cluster.width * 0.18, cluster.height * (0.22 + i * 0.1), Math.sin(i + cluster.tone) * cluster.depth * 0.18]} rotation={[0.4 + i * 0.18, 0.6, 0.2]} castShadow>
            <octahedronGeometry args={[0.1 + i * 0.012, 0]} />
            <meshStandardMaterial color={i === 1 ? '#7de3ff' : glow} emissive={theme.biome} emissiveIntensity={0.48} roughness={0.24} transparent opacity={0.9} />
          </mesh>
        ))}
        {Array.from({ length: 6 }, (_, i) => {
          const a = (i / 6) * Math.PI * 2
          return (
            <mesh key={`flower-${i}`} position={[Math.cos(a) * cluster.width * 0.22, 0.11, Math.sin(a) * cluster.depth * 0.25]} rotation={[0.24, -a, a]}>
              <boxGeometry args={[0.12, 0.03, 0.045]} />
              <meshStandardMaterial color={i % 2 ? theme.biomeSoft : '#7de3ff'} emissive={theme.biome} emissiveIntensity={0.3} roughness={0.35} />
            </mesh>
          )
        })}
        {high && cluster.variant % 3 === 1 && <PulseLight color={theme.biomeSoft} distance={1.45} intensity={0.22} reducedMotion={reducedMotion} phase={cluster.tone * 5} />}
      </group>
    )
  }
  return (
    <group position={[cluster.x, 0, cluster.z]} rotation={[0, cluster.rot, 0]} scale={cluster.scale}>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={i} position={[(i - 1.5) * cluster.width * 0.14, 0.08 + i * 0.015, Math.cos(i + cluster.tone) * cluster.depth * 0.18]} rotation={[0, (i - 1) * 0.28, 0]} castShadow receiveShadow>
          <boxGeometry args={[0.26 + i * 0.025, 0.12 + i * 0.02, 0.18 + i * 0.015]} />
          <meshStandardMaterial color={i === 1 ? theme.edge : dark} metalness={0.04} roughness={0.88} />
        </mesh>
      ))}
      <mesh position={[-cluster.width * 0.32, cluster.height * 0.36, 0]} castShadow>
        <cylinderGeometry args={[0.05, 0.07, cluster.height * 0.68, 8]} />
        <meshStandardMaterial color={theme.edge} metalness={0.18} roughness={0.62} />
      </mesh>
      <group position={[-cluster.width * 0.32, cluster.height * 0.74, 0]}>
        <ClusterFlame color="#ff9a45" hot="#ffe08a" reducedMotion={reducedMotion} lit={high && cluster.variant % 2 === 0} scale={0.72} />
      </group>
      {[0, 1, 2].map((i) => (
        <mesh key={`chain-${i}`} position={[cluster.width * (-0.08 + i * 0.12), cluster.height * (0.42 - Math.abs(i - 1) * 0.06), cluster.depth * 0.24]} rotation={[Math.PI / 2, 0, i * Math.PI / 2]}>
          <torusGeometry args={[0.065, 0.013, 6, 12]} />
          <meshStandardMaterial color="#d0a24c" metalness={0.42} roughness={0.42} />
        </mesh>
      ))}
    </group>
  )
}

function BiomeSetDressingClusters({ layout, theme, biome, graphics, reducedMotion }: { layout: BoardLayout; theme: BiomeTheme; biome: BiomeId; graphics: 'high' | 'medium' | 'low'; reducedMotion: boolean }) {
  const clusters = useMemo(() => buildBiomeSetDressingClusters(layout, biome, graphics), [layout, biome, graphics])
  if (clusters.length === 0) return null
  return (
    <group>
      {clusters.map((cluster) => (
        <BiomeSetDressingCluster key={cluster.key} cluster={cluster} theme={theme} reducedMotion={reducedMotion} graphics={graphics} />
      ))}
    </group>
  )
}

function StageFloorAccents({ layout, theme, biome, graphics }: { layout: BoardLayout; theme: BiomeTheme; biome: BiomeId; graphics: 'high' | 'medium' | 'low' }) {
  const accents = useMemo(() => buildStageFloorAccents(layout, biome, graphics), [layout, biome, graphics])
  if (accents.length === 0) return null
  return (
    <group>
      {accents.map((accent) => (
        <StageFloorAccent key={accent.key} accent={accent} theme={theme} />
      ))}
    </group>
  )
}

function WanderSpark({ base, color, reducedMotion }: { base: [number, number]; color: string; reducedMotion: boolean }) {
  const ref = useRef<THREE.Group>(null)
  const seed = useRef(Math.random() * 100)
  useFrame((state) => {
    const g = ref.current
    if (!g) return
    const t = state.clock.elapsedTime + seed.current
    if (reducedMotion) {
      g.position.set(base[0], 0.4, base[1])
      return
    }
    g.position.x = base[0] + Math.sin(t * 0.5) * 0.9
    g.position.z = base[1] + Math.cos(t * 0.37) * 0.9
    g.position.y = 0.4 + Math.sin(t * 2) * 0.06
    const s = 1 + Math.sin(t * 2.2) * 0.05
    g.scale.set(1 / s, s, 1 / s)
  })
  return (
    <group ref={ref} position={[base[0], 0.4, base[1]]}>
      <mesh>
        <octahedronGeometry args={[0.15, 0]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.7} roughness={0.4} transparent opacity={0.85} toneMapped={false} />
      </mesh>
      <pointLight color={color} distance={2} intensity={0.5} />
    </group>
  )
}

function Motes({ layout, color, count, reducedMotion }: { layout: BoardLayout; color: string; count: number; reducedMotion: boolean }) {
  const ref = useRef<THREE.Points>(null)
  const { geometry, seeds, baseY } = useMemo(() => {
    const geo = new THREE.BufferGeometry()
    const pos = new Float32Array(count * 3)
    const sd = new Float32Array(count)
    const by = new Float32Array(count)
    const spread = layout.extent * 1.1
    const rnd = seeded(1337)
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (rnd() - 0.5) * spread
      pos[i * 3 + 1] = rnd() * 3.2 + 0.3
      pos[i * 3 + 2] = (rnd() - 0.5) * spread
      by[i] = pos[i * 3 + 1]
      sd[i] = rnd() * Math.PI * 2
    }
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3))
    return { geometry: geo, seeds: sd, baseY: by }
  }, [count, layout.extent])

  useFrame((state) => {
    const pts = ref.current
    if (!pts || reducedMotion || count <= 0) return
    const attr = pts.geometry.getAttribute('position') as THREE.BufferAttribute
    const t = state.clock.elapsedTime
    for (let i = 0; i < count; i++) {
      attr.setY(i, moteYAt(baseY[i], seeds[i], t))
    }
    attr.needsUpdate = true
    pts.rotation.y = t * 0.02
  })

  if (count <= 0) return null

  return (
    <points ref={ref} geometry={geometry}>
      <pointsMaterial color={color} size={0.07} transparent opacity={0.6} sizeAttenuation depthWrite={false} toneMapped={false} />
    </points>
  )
}

export function moteYAt(baseY: number, seed: number, time: number): number {
  return baseY + Math.sin(time * 0.4 + seed) * 0.06
}

export function WorldEnvironment({ layout, theme, biome, reducedMotion, graphics, emberColor, grid }: WorldEnvironmentProps) {
  const worldLife = useMemo(
    () => buildWorldLife({ rows: layout.rows, cols: layout.cols, extent: layout.extent }, biome, graphics, emberColor),
    [layout.cols, layout.rows, layout.extent, biome, graphics, emberColor],
  )
  const worldcraft = useMemo(
    () => buildWorldcraft(grid, biome, { originX: layout.originX, originZ: layout.originZ, tileSize: layout.tileSize, gap: layout.gap }),
    [biome, grid, layout.gap, layout.originX, layout.originZ, layout.tileSize],
  )
  const terrainPieces = useMemo(() => selectWorldcraftTerrainPieces(worldcraft.terrain, layout, graphics), [graphics, layout, worldcraft.terrain])
  const motifPieces = useMemo(() => {
    if (graphics === 'low') return []
    return worldcraft.motifs.slice(0, graphics === 'medium' ? 2 : 6)
  }, [graphics, worldcraft.motifs])
  const stageGround = useMemo(() => buildStageGround(layout, biome, graphics), [layout, biome, graphics])

  return (
    <group>
      <mesh position={[0, -0.32, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[1, 0.62, 1]} renderOrder={-40}>
        <circleGeometry args={[stageGround.radius, stageGround.segments]} />
        <meshBasicMaterial color={theme.surface} transparent opacity={stageGround.baseOpacity} depthWrite={false} />
      </mesh>
      <mesh position={[0, -0.3, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[1, stageGround.shadowSquash, 1]} renderOrder={-39}>
        <circleGeometry args={[stageGround.shadowRadius, stageGround.segments]} />
        <meshBasicMaterial color={theme.ink} transparent opacity={stageGround.shadowOpacity} depthWrite={false} />
      </mesh>
      <mesh position={[0, -0.28, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[1, stageGround.washSquash, 1]} renderOrder={-38}>
        <circleGeometry args={[stageGround.washRadius, stageGround.segments]} />
        <meshBasicMaterial color={theme.biome} transparent opacity={stageGround.washOpacity} depthWrite={false} toneMapped={false} />
      </mesh>
      <StageFloorAccents layout={layout} theme={theme} biome={biome} graphics={graphics} />
      <BiomeBackdrop layout={layout} theme={theme} biome={biome} graphics={graphics} reducedMotion={reducedMotion} />
      <BiomeLandmarks layout={layout} theme={theme} biome={biome} graphics={graphics} reducedMotion={reducedMotion} />
      <BiomeSetDressingClusters layout={layout} theme={theme} biome={biome} graphics={graphics} reducedMotion={reducedMotion} />
      <DioramaBase layout={layout} theme={theme} biome={biome} reducedMotion={reducedMotion} graphics={graphics} />
      <BaseSilhouetteDressings layout={layout} theme={theme} biome={biome} graphics={graphics} reducedMotion={reducedMotion} />
      <PlatformContourDressings grid={grid} layout={layout} theme={theme} biome={biome} graphics={graphics} reducedMotion={reducedMotion} />
      <PlatformEdgeDressings layout={layout} theme={theme} biome={biome} graphics={graphics} reducedMotion={reducedMotion} />
      <BiomeRim layout={layout} theme={theme} biome={biome} graphics={graphics} reducedMotion={reducedMotion} />

      {terrainPieces.map((p) => (
        <TerrainPiece key={p.key} p={p} theme={theme} biome={biome} tileSize={layout.tileSize} graphics={graphics} />
      ))}

      {motifPieces.map((motif) => <MotifPiece key={motif.key} motif={motif} theme={theme} biome={biome} emberColor={emberColor} reducedMotion={reducedMotion} />)}

      {graphics !== 'low' && worldLife.props.map((p, i) => <DecorProp key={i} p={p} theme={theme} biome={biome} reducedMotion={reducedMotion} />)}

      {worldLife.npcs.map((s) => (
        <WanderSpark key={s.key} base={s.base} color={s.color} reducedMotion={reducedMotion} />
      ))}

      <Motes layout={layout} color={theme.biomeSoft} count={worldLife.moteCount} reducedMotion={reducedMotion} />
    </group>
  )
}
